import React, { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type ExcelJS from 'exceljs';
import { downloadWorkbook, styleTitle, styleHeader, styleInfoRow, styleDataRow, styleFooterRow, THIN_BORDER, XL_COLORS } from '../utils/excelHelper';
import * as api from '../services/api';
import { useNavigate } from 'react-router-dom';
import SearchableSelect from '../components/SearchableSelect';
import CertFormsPanel, { type CertForm } from '../components/CertFormsPanel';
import CertFormHistoryPanel from '../components/CertFormHistoryPanel';
import LaborRecordSheet, { LaborRecord } from '../components/LaborRecordSheet';
import { hasFeature } from '../utils/featureFlags';
import { BRAND_NAME } from '../utils/brand';

// ── 型定義 ──────────────────────────────────────────────────────────────────

interface CompanyInfo {
  company_name: string; company_subdomain: string;
  company_address: string | null; company_tel: string | null; company_rep: string | null;
}
interface CrewMember {
  id: string; name: string; nameKana: string; employeeCode: string;
  role: string; roleLabel: string; birthDate: string; joinDate: string;
  address: string; licenseNo: string; licenseGrade: string;
  nationality: string; birthPlace: string; seamanBookNo: string; positionInVessel: string;
  vesselName: string;
}
interface Vessel {
  id: string; name: string; type: string;
  capacity: number; imoNumber: string; vesselNo: string; flag: string;
  ownerType: string; ownerName: string | null; ownerAddress: string | null;
}
interface AttendanceRow {
  date: string;
  startTime: string;  // JST HH:MM
  endTime: string;    // JST HH:MM（セッション未終了時は空）
  workMin: number;    // total_work_minutes（確定実績値）
  breakMin: number;   // total_break_minutes
  overMin: number;    // total_overtime_minutes
  vessel: string;
  workType: string;
  status: string;     // working / completed
}
interface ActivityLog {
  id: string;
  activity_master_id: string;
  name: string;
  emoji: string;
  duration_minutes: number;
}

// ── 定数 ─────────────────────────────────────────────────────────────────────

const CERT_TYPES = [
  { id: 'boarding',   label: '乗船証明書',   icon: '🚢', desc: '乗船期間・船名・役職を証明' },
  { id: 'employment', label: '就労証明書',   icon: '📋', desc: '雇用期間・役職・給与区分を証明' },
  { id: 'monthly',    label: '月次労働記録', icon: '📅', desc: '社内様式／労務管理記録簿（様式第十六号の五）' },
  { id: 'roster',     label: '乗組員名簿',   icon: '👥', desc: '船舶検査・港湾手続き用名簿' },
  { id: 'koyoiri',    label: '雇入（雇止）届出書',       icon: '📝', desc: '第6号書式（船員法）' },
  { id: 'henko',      label: '雇入契約変更（更新）届出書', icon: '✍️', desc: '第8号書式（船員法）' },
  { id: 'koko',       label: '航行報告書',              icon: '🧭', desc: '第4号書式（船員法施行規則）' },
  { id: 'koko-cert',  label: '航行報告証明申請書',        icon: '📜', desc: '第4号の2書式（写しの証明申請）' },
  { id: 'shipsupply', label: '船用品注文書',            icon: '📦', desc: '社内様式（船用品の注文）', flag: 'extra_cert_forms' },
  { id: 'travelexp',  label: '船員旅費精算書',          icon: '🚄', desc: '社内様式（旅費の精算・受領報告）', flag: 'extra_cert_forms' },
  { id: 'koten',      label: '荒天避難報告書',          icon: '🌊', desc: '社内様式（避難の日時・場所・気象海象・停泊当直）', flag: 'extra_cert_forms' },
  { id: 'tenmatsu',   label: '顛末書',                  icon: '📄', desc: '社内様式（事故・トラブルの発生内容・原因・対応・対策）', flag: 'extra_cert_forms' },
  { id: 'roumu',      label: '労務管理記録簿',          icon: '🕓', desc: '国交省様式（勤怠から自動作成）', flag: 'labor_record_book' },
];
/** 機能フラグが指定されている書類は、有効な会社だけ選べるようにする */
const availableCertTypes = () => CERT_TYPES.filter(t => !('flag' in t) || hasFeature((t as { flag: string }).flag));
// 官庁様式・社内様式（CertFormsPanelで扱う）
const CERT_FORM_TYPES = ['koyoiri', 'henko', 'koko', 'koko-cert', 'shipsupply', 'travelexp', 'koten', 'tenmatsu', 'roumu'];

const ROLE_LABELS: Record<string, string> = {
  system_admin: 'システム管理者', shore_admin: '陸上管理者',
  captain: '船長', crew: '乗組員',
};

// 月次労働記録様式
type MonthlyFormat = 'default' | 'form16';
const MONTHLY_FORMATS: { id: MonthlyFormat; label: string }[] = [
  { id: 'default', label: '社内様式' },
  { id: 'form16',  label: '様式第十六号の五（労務管理記録簿）' },
];
interface LaborSettings {
  ot_agreement: string; comp_agreement: string; rest_split_agreement: string;
  labor_manager: string; shipowner: string; dispatch_owner: string;
  base_period_months: string;
}
const EMPTY_LABOR_SETTINGS: LaborSettings = {
  ot_agreement: '', comp_agreement: '', rest_split_agreement: '',
  labor_manager: '', shipowner: '', dispatch_owner: '', base_period_months: '1',
};

// 乗組員名簿様式
type RosterFormat = 'default' | 'customs' | 'mlit';
// 2026-10-26 独自の「デフォルト」様式は廃止し、国土交通省様式(六)を既定にした
// （シーステージ様の現用クルーリストが様式(六)のため。税関様式は港湾手続き用に残す）
const ROSTER_FORMATS: { id: RosterFormat; label: string }[] = [
  { id: 'mlit',     label: '国土交通省様式' },
  { id: 'customs',  label: '税関様式' },
];

// 税関様式用フィールド
interface CustomsState {
  arrDep: 'arrival' | 'departure';
  port: string;
  date: string;
  vesselNationality: string;
  prevPort: string;
  signDate: string;
  signName: string;
  includedCrew: Record<string, boolean>;
  // 乗組員ごとの身分証明書
  crewIdDocs: Record<string, { type: string; number: string }>;
  // 列の表示切替
  showColumns: { position: boolean; nationality: boolean; birthInfo: boolean; idDoc: boolean; };
}

// 国土交通省様式用フィールド
interface MlitState {
  reportDate: string;
  captainId: string;
  captainFree: string;
  maxHours: string;
  vesselNo: string;
  art72: boolean | null;
  alarm: boolean | null;
  autopilot: boolean | null;
  reportCategoryId: string;
  ownerFree: string;
  employerFree: string;
  includedCrew: Record<string, boolean>;
  autoCert: boolean;
  /** 受有している資格証明書の個別上書き（例:「三級海技士(航海) 一海特」。未入力は自動＝海技免状の級） */
  certText?: Record<string, string>;
}
interface ReportCategoryRecord {
  id: string; company_id: string; name: string; sort_order: number; is_active: number;
}

const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - 1 + i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

// UTC ISO文字列 → JST "HH:MM" 文字列
function toJstHHMM(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo', hour12: false });
  } catch { return ''; }
}
function fmtHours(min: number): string {
  if (!min) return '0.0h';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h${m}m` : `${h}h`;
}

// ── A4 プレビューコンポーネント ───────────────────────────────────────────────

const ROSTER_COL_DEFS = [
  { key: 'no',           header: 'No.',      render: (_c: CrewMember, i: number) => String(i + 1) },
  { key: 'name',         header: '氏名',      render: (c: CrewMember)             => c.name },
  { key: 'nameKana',     header: 'ふりがな',  render: (c: CrewMember)             => c.nameKana || '—' },
  { key: 'role',         header: '役職',      render: (c: CrewMember)             => c.roleLabel },
  { key: 'employeeCode', header: '社員コード', render: (c: CrewMember)             => c.employeeCode },
  { key: 'birthDate',    header: '生年月日',  render: (c: CrewMember)             => c.birthDate || '—' },
  { key: 'license',      header: '海技免状',  render: (c: CrewMember)             => c.licenseGrade || '—' },
  { key: 'address',      header: '住所',      render: (c: CrewMember)             => c.address || '—' },
  { key: 'joinDate',     header: '入社日',    render: (c: CrewMember)             => c.joinDate || '—' },
] as const;

const CertPreview: React.FC<{
  certType: string; crew: CrewMember | null; vessel: Vessel | null;
  year: number; month: number; issueDate: string;
  attendance: AttendanceRow[]; allCrew: CrewMember[];
  company: CompanyInfo;
  rosterFields?: Record<string, boolean>;
  rosterFormat?: RosterFormat;
  customsState?: CustomsState;
  mlitState?: MlitState;
  activityLogs?: ActivityLog[];
  empChecks?: Record<string, boolean>;
  onEmpCheck?: (key: string) => void;
  empWorkRecords?: { label: string; days: number; totalHours: number }[];
  empSlots?: [{ y: number; m: number }, { y: number; m: number }, { y: number; m: number }];
  onEmpSlotChange?: (i: number, y: number, m: number) => void;
  empFields?: Record<string, string>;
  onEmpFieldChange?: (key: string, val: string) => void;
  numChildren?: number;
}> = ({ certType, crew, vessel, year, month, issueDate, attendance, allCrew, company, rosterFields, rosterFormat = 'mlit', customsState, mlitState, activityLogs = [], empChecks = {}, onEmpCheck, empWorkRecords = [], empSlots, onEmpSlotChange, empFields = {}, onEmpFieldChange, numChildren = 3 }) => {

  const companyName = company.company_name || '（会社名未設定）';
  const companyAddr = company.company_address || '（住所：設定 → 設定ページ）';
  const companyTel  = company.company_tel || '（TEL：設定 → 設定ページ）';
  const companyRep  = company.company_rep || '代表者';
  const monthStr    = String(month).padStart(2, '0');
  const lastDay     = new Date(year, month, 0).getDate();

  const totalDays  = attendance.length;
  const totalWorkMin = attendance.reduce((s, r) => s + r.workMin, 0);
  const totalOverMin = attendance.reduce((s, r) => s + r.overMin, 0);
  const totalBreakMin = attendance.reduce((s, r) => s + r.breakMin, 0);

  // 作業種別ごとの集計
  const workTypeMap = new Map<string, { days: number; workMin: number }>();
  for (const r of attendance) {
    const key = r.workType || 'その他';
    const cur = workTypeMap.get(key) ?? { days: 0, workMin: 0 };
    workTypeMap.set(key, { days: cur.days + 1, workMin: cur.workMin + r.workMin });
  }
  const workTypeSummary = Array.from(workTypeMap.entries())
    .sort((a, b) => b[1].workMin - a[1].workMin);

  // 時間を指定して記録した作業（activity_logs）の種別ごとの集計。
  // unified_work_types が有効な会社では区分を出さず「作業内容ごとの時間と回数」として見せる。
  const activityMap = new Map<string, { name: string; emoji: string; count: number; totalMin: number }>();
  for (const log of activityLogs) {
    const key = log.activity_master_id;
    const cur = activityMap.get(key) ?? { name: log.name, emoji: log.emoji, count: 0, totalMin: 0 };
    activityMap.set(key, { ...cur, count: cur.count + 1, totalMin: cur.totalMin + log.duration_minutes });
  }
  const activitySummary = Array.from(activityMap.values()).sort((a, b) => b.totalMin - a.totalMin);

  const wrap: React.CSSProperties = {
    width: '794px', minHeight: '1123px', background: '#fff',
    padding: '56px 64px', fontFamily: '"Hiragino Mincho ProN","Yu Mincho","MS PMincho",serif',
    color: '#111', fontSize: '13px', lineHeight: 1.85, boxSizing: 'border-box', position: 'relative',
  };
  const titleSt: React.CSSProperties  = { textAlign: 'center', fontSize: '24px', fontWeight: 'bold', letterSpacing: '0.35em', marginBottom: '6px', paddingBottom: '14px', borderBottom: '3px double #111' };
  const secTitle: React.CSSProperties = { fontSize: '13px', fontWeight: 'bold', borderLeft: '4px solid #0ea5e9', paddingLeft: '8px', marginTop: '24px', marginBottom: '10px', color: '#0f172a' };
  const tbl: React.CSSProperties      = { width: '100%', borderCollapse: 'collapse', marginBottom: '12px', fontSize: '12px' };
  const th: React.CSSProperties       = { background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '6px 12px', textAlign: 'left', fontWeight: 'bold', color: '#374151', width: '170px', whiteSpace: 'nowrap' };
  const td: React.CSSProperties       = { border: '1px solid #cbd5e1', padding: '6px 12px', color: '#111' };
  const seal: React.CSSProperties     = { position: 'absolute', bottom: '64px', right: '64px', width: '80px', height: '80px', border: '2px solid #dc2626', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626', fontSize: '13px', fontWeight: 'bold', textAlign: 'center', lineHeight: 1.4, opacity: 0.55 };

  const footer = (
    <div style={{ marginTop: '36px', fontSize: '12px', color: '#333', lineHeight: 2.2 }}>
      <div>発行日：{issueDate}</div>
      <div style={{ marginTop: '12px' }}>
        <div style={{ fontWeight: 'bold' }}>{companyName}</div>
        <div>{companyAddr}</div>
        <div>TEL: {companyTel}</div>
        <div style={{ marginTop: '8px' }}>{companyRep}</div>
      </div>
      <div style={seal}>社　印</div>
    </div>
  );

  if (!crew && certType !== 'roster') {
    return (
      <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '16px' }}>
        左側で条件を選択してください
      </div>
    );
  }

  // ── 乗船証明書 ──
  if (certType === 'boarding') return (
    <div style={wrap}>
      <div style={{ textAlign: 'center', fontSize: '12px', color: '#666', marginBottom: '4px' }}>第　　　号</div>
      <div style={titleSt}>乗　船　証　明　書</div>
      <div style={{ textAlign: 'center', fontSize: '13px', marginBottom: '32px', color: '#444' }}>下記の者が当社船舶に乗船したことを証明します。</div>
      <div style={secTitle}>被証明者情報</div>
      <table style={tbl}><tbody>
        <tr><th style={th}>氏名（ふりがな）</th><td style={td}>{crew!.name}{crew!.nameKana ? `（${crew!.nameKana}）` : ''}</td></tr>
        <tr><th style={th}>生年月日</th><td style={td}>{crew!.birthDate || '—'}</td></tr>
        <tr><th style={th}>住所</th><td style={td}>{crew!.address || '—'}</td></tr>
        <tr><th style={th}>海技免状番号</th><td style={td}>{crew!.licenseNo || '—'}</td></tr>
        <tr><th style={th}>海技免状の種類</th><td style={td}>{crew!.licenseGrade || '—'}</td></tr>
      </tbody></table>
      <div style={secTitle}>乗船情報</div>
      <table style={tbl}><tbody>
        <tr><th style={th}>船名</th><td style={td}>{vessel?.name || '（未選択）'}</td></tr>
        <tr><th style={th}>船種</th><td style={td}>{vessel?.type || '—'}</td></tr>
        <tr><th style={th}>総トン数</th><td style={td}>{vessel?.capacity ? `${vessel.capacity} トン` : '—'}</td></tr>
        <tr><th style={th}>IMO番号</th><td style={td}>{vessel?.imoNumber || '—'}</td></tr>
        <tr><th style={th}>乗船期間</th><td style={td}>{year}年{monthStr}月01日　〜　{year}年{monthStr}月{lastDay}日</td></tr>
        <tr><th style={th}>役職</th><td style={td}>{crew!.roleLabel}</td></tr>
      </tbody></table>
      {footer}
    </div>
  );

  // ── 就労証明書（公式様式） ──
  if (certType === 'employment') {
    const issParts = issueDate.split('/');
    const issY = issParts[0] ?? '', issM = issParts[1] ?? '', issD = issParts[2] ?? '';
    const bp = (crew!.birthDate || '').split('-');
    const bY = bp[0] || '', bMo = bp[1] || '', bDy = bp[2] || '';
    const jp = (crew!.joinDate || '').split('-');
    const jY = jp[0] || '', jMo = jp[1] || '', jDy = jp[2] || '';

    // 読み取り専用チェックボックス（左パネルで操作、プレビューは表示のみ）
    const CkB = (key: string): React.ReactElement => (
      <span
        style={{ userSelect: 'none', fontFamily: '"MS Gothic","ＭＳ ゴシック",monospace', display: 'inline-block', width: '1em', textAlign: 'center', color: (empChecks[key] ?? false) ? '#0f4c96' : '#333' }}
      >
        {(empChecks[key] ?? false) ? '■' : '□'}
      </span>
    );

    // 静的フィールド表示（左パネルで入力した値を表示）
    const fvSt: React.CSSProperties = {
      display: 'inline-block', minWidth: '28px', borderBottom: '1px solid #94a3b8',
      padding: '0 2px', textAlign: 'center', color: '#0f172a',
    };
    const FV = (key: string, width = '36px', ph = '　'): React.ReactElement => (
      <span style={{ ...fvSt, minWidth: width }}>
        {empFields[key] || ph}
      </span>
    );

    // 就労実績から計算するヒント値
    const hasRecords = empWorkRecords.some(r => r.days > 0);
    const avgDays = hasRecords
      ? Math.round(empWorkRecords.reduce((s, r) => s + r.days, 0) / Math.max(empWorkRecords.filter(r => r.days > 0).length, 1))
      : null;
    const avgHours = hasRecords
      ? Math.round(empWorkRecords.reduce((s, r) => s + r.totalHours, 0) / Math.max(empWorkRecords.filter(r => r.days > 0).length, 1) * 10) / 10
      : null;
    const hint: React.CSSProperties = { color: '#0ea5e9', fontSize: '8.5px', marginLeft: '4px' };

    const fW: React.CSSProperties = {
      width: '794px', background: '#fff', padding: '18px 24px',
      fontFamily: '"MS PGothic","ＭＳ Ｐゴシック","Hiragino Kaku Gothic ProN","Noto Sans JP",sans-serif',
      fontSize: '10px', color: '#111', lineHeight: 1.4, boxSizing: 'border-box',
    };
    const T: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' };
    const c0: React.CSSProperties = { border: '1px solid #555', padding: '2px 5px', verticalAlign: 'middle', fontSize: '10px' };
    const nSt: React.CSSProperties = { ...c0, background: '#e0e0e0', textAlign: 'center', width: '22px', fontWeight: 'bold' };
    const lSt: React.CSSProperties = { ...c0, background: '#f0f0f0', textAlign: 'center', width: '95px', fontSize: '9px', whiteSpace: 'pre-wrap', lineHeight: 1.35 };
    const hSt: React.CSSProperties = { ...c0, background: '#d0d0d0', textAlign: 'center', fontWeight: 'bold' };
    const Sp = () => <span style={{ margin: '0 4px' }}/>;

    return (
      <div style={fW}>
        {/* タイトル */}
        <div style={{ textAlign: 'center', fontSize: '18px', fontWeight: 'bold', letterSpacing: '0.5em', borderBottom: '2.5px double #111', paddingBottom: '6px', marginBottom: '7px' }}>
          就　労　証　明　書
        </div>

        {/* 宛名 + 証明者情報 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', gap: '10px', alignItems: 'flex-end' }}>
          <div style={{ fontSize: '11px', display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
            <span style={{ borderBottom: '1px solid #555', display: 'inline-block', minWidth: '200px', fontSize: '11px' }}>
              {empFields['addressee'] || '　'}
            </span>
            <span>宛</span>
          </div>
          <div style={{ fontSize: '9px', lineHeight: 1.85, borderLeft: '1px solid #bbb', paddingLeft: '10px', flexShrink: 0 }}>
            <div>証明日　{issY}年　{issM}月　{issD}日</div>
            <div>事業所名　{companyName}</div>
            <div>代表者名　{companyRep}</div>
            <div>所在地　　{companyAddr}</div>
            <div>電話番号　{companyTel}</div>
            <div>担当者名　</div>
            <div>記載者連絡先　</div>
          </div>
        </div>

        {/* 証明文 */}
        <div style={{ fontWeight: 'bold', fontSize: '10.5px', marginBottom: '1px' }}>下記の内容について、事実であることを証明いたします。</div>
        <div style={{ fontSize: '8.5px', color: '#444', marginBottom: '5px' }}>
          ※本証明書の内容について、就労先事業者等に無断で作成し又は改変を行ったときには、刑法上の罪に問われる場合があります。
        </div>

        <table style={T}>
          <thead>
            <tr>
              <td style={hSt}>No.</td>
              <td style={hSt}>項目</td>
              <td style={hSt}>記載欄　<span style={{ fontSize: '8px', fontWeight: 'normal', color: '#555' }}>（□ をクリックで選択）</span></td>
            </tr>
          </thead>
          <tbody>
            {/* 1. 業種 */}
            <tr>
              <td style={nSt} rowSpan={4}>1</td>
              <td style={lSt} rowSpan={4}>業種</td>
              <td style={c0}>{CkB('ind_agri')} 農業・林業<Sp/>{CkB('ind_fish')} 漁業<Sp/>{CkB('ind_mine')} 鉱業・採石業・砂利採取業<Sp/>{CkB('ind_const')} 建設業<Sp/>{CkB('ind_mfg')} 製造業<Sp/>{CkB('ind_util')} 電気・ガス・熱供給・水道業</td>
            </tr>
            <tr><td style={c0}>{CkB('ind_it')} 情報通信業<Sp/>{CkB('ind_trans')} 運輸業・郵便業<Sp/>{CkB('ind_retail')} 卸売業・小売業<Sp/>{CkB('ind_fin')} 金融業・保険業<Sp/>{CkB('ind_real')} 不動産業・物品賃貸業</td></tr>
            <tr><td style={c0}>{CkB('ind_acad')} 学術研究・専門・技術サービス<Sp/>{CkB('ind_hotel')} 宿泊業・飲食サービス業<Sp/>{CkB('ind_life')} 生活関連サービス業・娯楽業<Sp/>{CkB('ind_med')} 医療・福祉</td></tr>
            <tr><td style={c0}>{CkB('ind_edu')} 教育・学習支援業<Sp/>{CkB('ind_multi')} 複合サービス事業<Sp/>{CkB('ind_pub')} 公務<Sp/>{CkB('ind_other')} その他（　　　　　　　　）</td></tr>

            {/* 2. 本人氏名 */}
            <tr>
              <td style={nSt} rowSpan={2}>2</td>
              <td style={lSt} rowSpan={2}>{`フリガナ\n本人氏名`}</td>
              <td style={c0}>フリガナ：{crew!.nameKana || '　'}　　　　　　生年月日：{bY}年 {bMo}月 {bDy}日</td>
            </tr>
            <tr><td style={{ ...c0, fontSize: '13px', fontWeight: 'bold', padding: '3px 6px' }}>{crew!.name}</td></tr>

            {/* 3. 雇用期間 */}
            <tr>
              <td style={nSt}>3</td>
              <td style={lSt}>{`雇用(予定)\n期間等`}</td>
              <td style={c0}>
                {CkB('ep_indef')} 無期<Sp/>{CkB('ep_fixed')} 有期
                <span style={{ marginLeft: '10px' }}>期間（無期の場合は雇用開始日のみ）：{jY}年 {jMo}月 {jDy}日　～　{FV('ep_end_y','36px')}年{FV('ep_end_m','24px')}月{FV('ep_end_d','24px')}日</span>
              </td>
            </tr>

            {/* 4. 就労先事業所 */}
            <tr>
              <td style={nSt} rowSpan={2}>4</td>
              <td style={lSt} rowSpan={2}>{`本人就労先\n事業所`}</td>
              <td style={c0}>名称：{companyName}</td>
            </tr>
            <tr><td style={c0}>住所：{companyAddr}</td></tr>

            {/* 5. 雇用の形態 */}
            <tr>
              <td style={nSt} rowSpan={2}>5</td>
              <td style={lSt} rowSpan={2}>雇用の形態</td>
              <td style={c0}>{CkB('et_reg')} 正社員<Sp/>{CkB('et_part')} パート・アルバイト<Sp/>{CkB('et_disp')} 派遣社員<Sp/>{CkB('et_cont')} 契約社員<Sp/>{CkB('et_fisc')} 会計年度任用職員<Sp/>{CkB('et_tmp')} 非常勤・臨時職員<Sp/>{CkB('et_off')} 役員</td>
            </tr>
            <tr><td style={c0}>{CkB('et_self')} 自営業主<Sp/>{CkB('et_fself')} 自営業専従者<Sp/>{CkB('et_fam')} 家族従業者<Sp/>{CkB('et_home')} 内職<Sp/>{CkB('et_out')} 業務委託<Sp/>{CkB('et_oth')} その他（　　　　　　）</td></tr>

            {/* 6. 就労時間（固定就労 5行 + 変則就労 4行 = 計9行） */}
            <tr>
              <td style={nSt} rowSpan={9}>6</td>
              <td style={lSt} rowSpan={5}>{`就労時間\n(固定就労の\n場合)`}</td>
              <td style={c0}>
                勤務曜日：{(['月','火','水','木','金','土','日','祝日'] as const).map(d => (
                  <span key={d}>{CkB(`wd_${d}`)} {d}　</span>
                ))}
                <span style={{ marginLeft: '6px' }}>月間　　時間　　分（うち休憩　　分）</span>
              </td>
            </tr>
            <tr><td style={c0}>一月当たりの就労日数　月間　　　日　　　　一週当たりの就労日数　週間　　　日</td></tr>
            <tr><td style={c0}>平日　　時　　分 ～ 　　時　　分　（うち休憩　　分）</td></tr>
            <tr><td style={c0}>土曜　　時　　分 ～ 　　時　　分　（うち休憩　　分）</td></tr>
            <tr><td style={c0}>日祝　　時　　分 ～ 　　時　　分　（うち休憩　　分）</td></tr>
            <tr>
              <td style={lSt} rowSpan={4}>{`就労時間\n(変則就労の\n場合)`}</td>
              <td style={c0}>
                合計時間　{CkB('vt_mon')} 月間　{CkB('vt_week')} 週間　{FV('vt_h','32px')}時間　{FV('vt_min','24px')}分（うち休憩　{FV('vt_brk','24px')}分）
                {avgHours !== null && <span style={hint}>参考：月平均 {avgHours}時間</span>}
              </td>
            </tr>
            <tr><td style={c0}>
              就労日数　{CkB('vd_mon')} 月間　{CkB('vd_week')} 週間　{FV('vd_days','28px')}日
              {avgDays !== null && <span style={hint}>参考：月平均 {avgDays}日</span>}
            </td></tr>
            <tr><td style={c0}>主な就労時間帯・シフト時間帯　{FV('vt_s_h','24px')}時{FV('vt_s_m','20px')}分 ～ {FV('vt_e_h','24px')}時{FV('vt_e_m','20px')}分（うち休憩　{FV('vt_s_brk','24px')}分）</td></tr>
            <tr><td style={{ ...c0, height: '5px' }}></td></tr>

            {/* 7. 就労実績（年月は左パネルで選択、プレビューは静的表示） */}
            <tr>
              <td style={nSt}>7</td>
              <td style={{ ...lSt, fontSize: '8px' }}>{`就労実績\n※日数に有給\n休暇を含み、\n時間数に休憩・\n残業時間を含む`}</td>
              <td style={c0}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                  <thead>
                    <tr>
                      {[0, 1, 2].map(i => (
                        <React.Fragment key={i}>
                          <td style={{ border: '1px solid #888', padding: '1px 3px', textAlign: 'center', background: '#e8f4fd', fontWeight: 'bold', color: '#0f4c96' }}>年／月</td>
                          <td style={{ border: '1px solid #888', padding: '1px 3px', textAlign: 'center', background: '#f8f8f8' }}>日／月</td>
                          <td style={{ border: '1px solid #888', padding: '1px 3px', textAlign: 'center', background: '#f8f8f8' }}>時間／月</td>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {[0, 1, 2].map(i => {
                        const rec = empWorkRecords[i];
                        const slot = empSlots?.[i];
                        const label = slot ? `${slot.y}年${slot.m}月` : (rec?.label ?? '—');
                        return (
                          <React.Fragment key={i}>
                            <td style={{ border: '1px solid #888', padding: '2px 4px', textAlign: 'center', background: '#fafafa', fontWeight: 'bold', color: '#0f4c96', fontSize: '8.5px' }}>
                              {label}
                            </td>
                            <td style={{ border: '1px solid #888', padding: '2px 4px', textAlign: 'center', fontWeight: rec?.days ? 'bold' : 'normal', color: rec?.days ? '#0f172a' : '#94a3b8' }}>
                              {rec?.days ? `${rec.days}日` : '—'}
                            </td>
                            <td style={{ border: '1px solid #888', padding: '2px 4px', textAlign: 'center', fontWeight: rec?.totalHours ? 'bold' : 'normal', color: rec?.totalHours ? '#0f172a' : '#94a3b8' }}>
                              {rec?.totalHours ? `${rec.totalHours}h` : '—'}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>

            {/* 8. 産前・産後休業 */}
            <tr>
              <td style={nSt} rowSpan={2}>8</td>
              <td style={{ ...lSt, fontSize: '8.5px' }} rowSpan={2}>{`産前・産後\n休業の取得\n※取得予定を含む`}</td>
              <td style={c0}>{CkB('mat_plan')} 取得予定<Sp/>{CkB('mat_now')} 取得中</td>
            </tr>
            <tr><td style={c0}>期間：{FV('mat_sy','32px')}年{FV('mat_sm','22px')}月{FV('mat_sd','22px')}日 ～ {FV('mat_ey','32px')}年{FV('mat_em','22px')}月{FV('mat_ed','22px')}日</td></tr>

            {/* 9. 育児休業 */}
            <tr>
              <td style={nSt} rowSpan={2}>9</td>
              <td style={{ ...lSt, fontSize: '8.5px' }} rowSpan={2}>{`育児休業の取得\n※取得予定を含む`}</td>
              <td style={c0}>{CkB('par_plan')} 取得予定<Sp/>{CkB('par_now')} 取得中<Sp/>{CkB('par_done')} 取得済み</td>
            </tr>
            <tr><td style={c0}>期間：{FV('par_sy','32px')}年{FV('par_sm','22px')}月{FV('par_sd','22px')}日 ～ {FV('par_ey','32px')}年{FV('par_em','22px')}月{FV('par_ed','22px')}日</td></tr>

            {/* 10. 産休・育休以外の休業 */}
            <tr>
              <td style={nSt} rowSpan={2}>10</td>
              <td style={{ ...lSt, fontSize: '8.5px' }} rowSpan={2}>{`産休・育休\n以外の休業の\n取得`}</td>
              <td style={c0}>
                {CkB('ol_plan')} 取得予定<Sp/>{CkB('ol_now')} 取得中<Sp/>{CkB('ol_done')} 取得済み
                　理由：{CkB('ol_care')} 介護休業<Sp/>{CkB('ol_sick')} 病休<Sp/>{CkB('ol_oth')} その他（{FV('ol_oth_txt','60px')}）
              </td>
            </tr>
            <tr><td style={c0}>期間：{FV('ol_sy','32px')}年{FV('ol_sm','22px')}月{FV('ol_sd','22px')}日 ～ {FV('ol_ey','32px')}年{FV('ol_em','22px')}月{FV('ol_ed','22px')}日</td></tr>

            {/* 11. 復職（予定）年月日 */}
            <tr>
              <td style={nSt}>11</td>
              <td style={{ ...lSt, fontSize: '8.5px' }}>復職（予定）年月日</td>
              <td style={c0}>{CkB('ret_plan')} 復職予定<Sp/>{CkB('ret_done')} 復職済み　{FV('ret_y','32px')}年{FV('ret_m','22px')}月{FV('ret_d','22px')}日</td>
            </tr>

            {/* 12. 育児のための短時間勤務 */}
            <tr>
              <td style={nSt} rowSpan={2}>12</td>
              <td style={{ ...lSt, fontSize: '8px' }} rowSpan={2}>{`育児のための\n短時間勤務制度\n利用有無\n※取得予定を含む`}</td>
              <td style={c0}>{CkB('sh_plan')} 取得予定<Sp/>{CkB('sh_now')} 取得中</td>
            </tr>
            <tr><td style={c0}>
              期間：{FV('sh_sy','32px')}年{FV('sh_sm','22px')}月{FV('sh_sd','22px')}日 ～ {FV('sh_ey','32px')}年{FV('sh_em','22px')}月{FV('sh_ed','22px')}日　時間帯　{FV('sh_s_h','24px')}時{FV('sh_s_m','20px')}分 ～ {FV('sh_e_h','24px')}時{FV('sh_e_m','20px')}分（休憩　{FV('sh_brk','24px')}分）
            </td></tr>

            {/* 13. 保育士等としての勤務実態 */}
            <tr>
              <td style={nSt}>13</td>
              <td style={{ ...lSt, fontSize: '8.5px' }}>{`保育士等として\nの勤務実態の\n有無`}</td>
              <td style={c0}>{CkB('cc_yes')} 有<Sp/>{CkB('cc_plan')} 有（予定）<Sp/>{CkB('cc_no')} 無</td>
            </tr>

            {/* 14. 更新の有無 */}
            <tr>
              <td style={nSt}>14</td>
              <td style={{ ...lSt, fontSize: '8.5px' }}>{`（雇用契約の）\n満了後の\n更新の有無`}</td>
              <td style={c0}>{CkB('ren_yes')} 有<Sp/>{CkB('ren_plan')} 有（予定）<Sp/>{CkB('ren_no')} 無<Sp/>{CkB('ren_unk')} 未定</td>
            </tr>

            {/* 15. 入所内定時育休短縮 */}
            <tr>
              <td style={nSt}>15</td>
              <td style={{ ...lSt, fontSize: '8.5px' }}>入所内定時育休短縮可否</td>
              <td style={c0}>{CkB('er_yes')} 可<Sp/>{CkB('er_plan')} 可（予定）<Sp/>{CkB('er_no')} 否</td>
            </tr>

            {/* 16. 育休延長 */}
            <tr>
              <td style={nSt}>16</td>
              <td style={lSt}>育休延長可否</td>
              <td style={c0}>{CkB('ext_yes')} 可<Sp/>{CkB('ext_plan')} 可（予定）<Sp/>{CkB('ext_no')} 否</td>
            </tr>

            {/* 17. 単身赴任期間 */}
            <tr>
              <td style={nSt}>17</td>
              <td style={{ ...lSt, fontSize: '8.5px' }}>単身赴任期間（予定含む）</td>
              <td style={c0}>{FV('trf_sy','32px')}年{FV('trf_sm','22px')}月{FV('trf_sd','22px')}日 ～ {FV('trf_ey','32px')}年{FV('trf_em','22px')}月{FV('trf_ed','22px')}日</td>
            </tr>

            {/* 18. 備考欄 */}
            <tr>
              <td style={nSt}>18</td>
              <td style={lSt}>備考欄</td>
              <td style={{ ...c0, minHeight: '34px' }}>
                <div style={{ minHeight: '30px', color: '#0f172a' }}>
                  {empFields['remarks'] || ''}
                </div>
              </td>
            </tr>

            {/* 19. 保護者記載欄 */}
            <tr>
              <td style={nSt} rowSpan={numChildren * 2 + 1}>19</td>
              <td style={lSt} rowSpan={numChildren * 2 + 1}>保護者記載欄</td>
              <td style={{ ...c0, background: '#f0f0f0', textAlign: 'center', fontSize: '9px', fontWeight: 'bold' }}>
                ※以下は保護者（申請者）が記載してください
              </td>
            </tr>
            {Array.from({ length: numChildren }, (_, idx) => idx + 1).map(i => (
              <React.Fragment key={i}>
                <tr>
                  <td style={c0}>
                    <span>児童名：<span style={{ ...fvSt, minWidth: '80px' }}>{empFields[`c${i}_name`] || ''}</span></span>
                    <span style={{ marginLeft: '8px' }}>生年月日：<span style={{ ...fvSt, minWidth: '24px' }}>{empFields[`c${i}_by`] || ''}</span>年<span style={{ ...fvSt, minWidth: '18px' }}>{empFields[`c${i}_bm`] || ''}</span>月<span style={{ ...fvSt, minWidth: '18px' }}>{empFields[`c${i}_bd`] || ''}</span>日</span>
                    <span style={{ marginLeft: '8px' }}>施設名：<span style={{ ...fvSt, minWidth: '80px' }}>{empFields[`c${i}_facility`] || ''}</span></span>
                    <span style={{ marginLeft: '6px' }}>{CkB(`c${i}_use`)} 利用中<Sp/>{CkB(`c${i}_app`)} 申込中（第一希望）</span>
                  </td>
                </tr>
                <tr><td style={{ ...c0, height: '5px' }}></td></tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ── 月次労働記録 ──
  if (certType === 'monthly') return (
    <div style={wrap}>
      <div style={titleSt}>月　次　労　働　記　録</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '18px', fontSize: '12px', color: '#333' }}>
        <div>
          <div>氏名：{crew!.name}{crew!.nameKana ? `（${crew!.nameKana}）` : ''}</div>
          <div>役職：{crew!.roleLabel}　／　配属：{attendance.find(a => a.vessel)?.vessel || vessel?.name || '—'}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div>対象年月：{year}年{monthStr}月</div>
          <div>社員コード：{crew!.employeeCode}</div>
        </div>
      </div>
      <div style={secTitle}>作業記録</div>
      <table style={tbl}>
        <thead>
          <tr>{['作業日', '開始', '終了', '実労働時間', '残業時間', '休憩', '作業種別', '船名'].map(h => (
            <th key={h} style={{ ...th, width: 'auto' }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {attendance.length > 0 ? attendance.map((r, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
              <td style={td}>{r.date}</td>
              <td style={td}>{r.startTime || '—'}</td>
              <td style={td}>{r.status === 'working' ? <span style={{ color: '#16a34a', fontSize: '11px' }}>作業中</span> : (r.endTime || '—')}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtHours(r.workMin)}</td>
              <td style={{ ...td, textAlign: 'right', color: r.overMin > 0 ? '#dc2626' : '#94a3b8' }}>{r.overMin > 0 ? fmtHours(r.overMin) : '—'}</td>
              <td style={{ ...td, textAlign: 'right' }}>{r.breakMin > 0 ? `${r.breakMin}分` : '—'}</td>
              <td style={{ ...td, fontSize: '11px' }}>{r.workType || '—'}</td>
              <td style={{ ...td, fontSize: '11px' }}>{r.vessel || '—'}</td>
            </tr>
          )) : (
            <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#94a3b8' }}>この月の記録はありません</td></tr>
          )}
        </tbody>
      </table>
      <div style={secTitle}>集計</div>
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        <table style={{ ...tbl, width: '48%', marginBottom: 0 }}><tbody>
          <tr><th style={th}>作業日数</th><td style={td}>{totalDays} 日</td></tr>
          <tr><th style={th}>総実労働時間</th><td style={td}>{fmtHours(totalWorkMin)}</td></tr>
          <tr><th style={th}>うち残業時間</th><td style={{ ...td, color: totalOverMin > 0 ? '#dc2626' : '#111', fontWeight: totalOverMin > 0 ? 'bold' : 'normal' }}>{totalOverMin > 0 ? fmtHours(totalOverMin) : '—'}</td></tr>
          <tr><th style={th}>総休憩時間</th><td style={td}>{totalBreakMin > 0 ? fmtHours(totalBreakMin) : '—'}</td></tr>
        </tbody></table>
        {workTypeSummary.length > 0 && (
          <table style={{ ...tbl, flex: 1, marginBottom: 0 }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 'auto' }}>作業種別</th>
                <th style={{ ...th, width: '60px', textAlign: 'right' }}>日数</th>
                <th style={{ ...th, width: '80px', textAlign: 'right' }}>実労働時間</th>
              </tr>
            </thead>
            <tbody>
              {workTypeSummary.map(([type, v]) => (
                <tr key={type}>
                  <td style={td}>{type}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{v.days} 日</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtHours(v.workMin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {activitySummary.length > 0 && (
        <>
          <div style={secTitle}>{hasFeature('unified_work_types') ? '作業内容ごとの時間と回数' : '特別作業集計'}</div>
          <table style={tbl}>
            <thead>
              <tr>
                <th style={{ ...th, width: 'auto' }}>作業内容</th>
                <th style={{ ...th, width: '60px', textAlign: 'right' }}>回数</th>
                <th style={{ ...th, width: '100px', textAlign: 'right' }}>時間合計</th>
              </tr>
            </thead>
            <tbody>
              {activitySummary.map((a, i) => (
                <tr key={a.name} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={td}>{a.emoji} {a.name}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{a.count} 回</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtHours(a.totalMin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {footer}
    </div>
  );

  // ── 乗組員名簿（税関様式） ──
  // （独自の「デフォルト」様式は2026-10-26に廃止。既定は国土交通省様式(六)）
  if (certType === 'roster' && rosterFormat === 'customs') {
    const cs = customsState!;
    const isArrival = cs.arrDep === 'arrival';
    const visibleCrew = allCrew.filter(c => cs.includedCrew[c.id] !== false);
    const flagLabel = cs.vesselNationality || (vessel?.flag === 'JP' ? '日本 (Japan)' : (vessel?.flag || '—'));
    const serifFont = '"Hiragino Mincho ProN","Yu Mincho","MS PMincho",serif';
    const PAGE_SIZE = 25;
    const showPos   = cs.showColumns?.position   ?? true;
    const showNat   = cs.showColumns?.nationality ?? true;
    const showBirth = cs.showColumns?.birthInfo   ?? true;
    const showId    = cs.showColumns?.idDoc       ?? true;

    // ページ分割（25人/ページ）
    const pages: typeof visibleCrew[] = [];
    for (let i = 0; i < Math.max(1, visibleCrew.length); i += PAGE_SIZE) {
      pages.push(visibleCrew.slice(i, i + PAGE_SIZE));
    }
    if (pages.length === 0) pages.push([]);

    const tbl: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' };
    const th: React.CSSProperties = {
      border: '1px solid #333', padding: '3px 4px',
      fontSize: '8px', textAlign: 'center', verticalAlign: 'middle',
      fontFamily: serifFont, fontWeight: 'bold', background: '#d8d8d8',
    };
    const td: React.CSSProperties = {
      border: '1px solid #333', padding: '2px 4px', fontSize: '9px',
      verticalAlign: 'middle', fontFamily: serifFont,
    };
    const sec: React.CSSProperties = { fontSize: '8.5px', fontWeight: 'bold' };
    const lbl: React.CSSProperties = { fontSize: '7px', color: '#666', display: 'block', fontStyle: 'italic' };

    const renderPage = (pageCrew: typeof visibleCrew, pageNum: number, totalPages: number) => {
      const idTypeLabel = (type: string) =>
        type === 'seaman' ? '船員手帳' : type === 'passport' ? '旅券' : type === 'other' ? 'その他' : type;

      return (
        <div key={pageNum} style={{
          width: '794px', minHeight: '1123px', background: '#fff',
          padding: '20px 36px 20px', fontFamily: serifFont,
          color: '#111', lineHeight: 1.4, boxSizing: 'border-box',
          position: 'relative', marginBottom: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          {/* 右上: 税関様式番号 */}
          <div style={{ position: 'absolute', top: 8, right: 36, textAlign: 'right', fontSize: 7.5, color: '#333', lineHeight: 1.6 }}>
            税関様式Ｃ第2065号<br/>Customs Form C No.2065
          </div>

          {/* タイトル */}
          <div style={{ textAlign: 'center', fontSize: 17, fontWeight: 'bold', letterSpacing: '0.55em', marginTop: 12, marginBottom: 0 }}>
            乗　組　員　名　簿
          </div>
          <div style={{ textAlign: 'center', fontSize: 10.5, letterSpacing: '0.25em', marginBottom: 6, borderBottom: '1.5px solid #111', paddingBottom: 4 }}>
            CREW LIST
          </div>

          {/* 到着/出発 + Page No. — 参考様式に準拠:チェック用小セル + ラベルセルを分離 */}
          <table style={{ ...tbl, marginBottom: 0 }}>
            <tbody>
              <tr>
                {/* 左スペーサー（枠なし） */}
                <td style={{ border: 'none', width: '34%' }}></td>
                {/* 到着チェックボックスセル */}
                <td style={{ border: '1px solid #333', width: '5%', textAlign: 'center', verticalAlign: 'middle', fontFamily: serifFont, fontSize: 11, fontWeight: 'bold', padding: '2px' }}>
                  {isArrival ? '✓' : ''}
                </td>
                {/* 到着ラベルセル（枠なし） */}
                <td style={{ border: 'none', width: '15%', padding: '3px 5px', verticalAlign: 'middle', fontFamily: serifFont }}>
                  <div style={{ fontSize: 9.5, fontWeight: isArrival ? 'bold' : 'normal' }}>到着</div>
                  <div style={{ fontSize: 7.5, color: '#555', fontStyle: 'italic' }}>Arrival</div>
                </td>
                {/* 出発チェックボックスセル */}
                <td style={{ border: '1px solid #333', width: '5%', textAlign: 'center', verticalAlign: 'middle', fontFamily: serifFont, fontSize: 11, fontWeight: 'bold', padding: '2px' }}>
                  {!isArrival ? '✓' : ''}
                </td>
                {/* 出発ラベルセル（枠なし） */}
                <td style={{ border: 'none', width: '15%', padding: '3px 5px', verticalAlign: 'middle', fontFamily: serifFont }}>
                  <div style={{ fontSize: 9.5, fontWeight: !isArrival ? 'bold' : 'normal' }}>出発</div>
                  <div style={{ fontSize: 7.5, color: '#555', fontStyle: 'italic' }}>Departure</div>
                </td>
                {/* Page No. */}
                <td style={{ ...td, width: '26%', textAlign: 'center', padding: '4px 6px', verticalAlign: 'middle' }}>
                  <div style={{ fontSize: 8, fontWeight: 'bold', fontFamily: serifFont }}>Page No.</div>
                  <div style={{ fontSize: 11, fontWeight: 'bold', fontFamily: serifFont, marginTop: 1 }}>{pageNum} / {totalPages}</div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* フィールド 1-6 */}
          <table style={{ ...tbl, marginTop: 0 }}>
            <tbody>
              <tr>
                <td style={{ ...td, width: '34%' }}>
                  <div style={sec}>1. 船舶の名称</div>
                  <div style={{ fontSize: 10, minHeight: 14, marginTop: 2 }}>{vessel?.name || '—'}</div>
                  <span style={lbl}>Name of ship</span>
                </td>
                <td style={{ ...td, width: '36%' }}>
                  <div style={sec}>2. {isArrival ? '到着港' : '出発港'}※／{isArrival ? '出発港' : '到着港'}※</div>
                  <div style={{ fontSize: 10, minHeight: 14, marginTop: 2 }}>{cs.port || '　'}</div>
                  <span style={lbl}>Port of {isArrival ? 'arrival' : 'departure'}</span>
                </td>
                <td style={{ ...td, width: '30%' }}>
                  <div style={sec}>3. {isArrival ? '到着日' : '出発日'}※／{isArrival ? '出発日' : '到着日'}※</div>
                  <div style={{ fontSize: 10, minHeight: 14, marginTop: 2 }}>{cs.date || '　'}</div>
                  <span style={lbl}>Date of {isArrival ? 'arrival' : 'departure'}</span>
                </td>
              </tr>
              <tr>
                <td style={td}>
                  <div style={sec}>4. 船舶の国籍</div>
                  <div style={{ fontSize: 10, minHeight: 14, marginTop: 2 }}>{flagLabel}</div>
                  <span style={lbl}>Nationality of ship</span>
                </td>
                <td style={td}>
                  <div style={sec}>5. 前寄港地※</div>
                  <div style={{ fontSize: 10, minHeight: 14, marginTop: 2 }}>{cs.prevPort || '　'}</div>
                  <span style={lbl}>Port arrived from</span>
                </td>
                <td style={td}>
                  <div style={sec}>6. 身分証明書（乗員手帳）の種類及び番号</div>
                  <div style={{ fontSize: 8.5, marginTop: 2, color: '#555' }}>各乗員欄を参照</div>
                  <span style={lbl}>Nature and No. of identity document (seaman's passport)</span>
                </td>
              </tr>
            </tbody>
          </table>

          {/* 乗組員テーブル */}
          <table style={{ ...tbl, marginTop: 0 }}>
            <thead>
              <tr>
                <th style={{ ...th, width: '26px' }}>7<br/>No.</th>
                <th style={{ ...th }}>8. 氏名<br/><span style={{ fontWeight: 'normal', fontSize: '7px' }}>Family name, given names</span></th>
                {showPos   && <th style={{ ...th, width: '12%' }}>9. 職名<br/><span style={{ fontWeight: 'normal', fontSize: '7px' }}>Rank or ra-<br/>ting</span></th>}
                {showNat   && <th style={{ ...th, width: '9%' }}>10. 国籍<br/><span style={{ fontWeight: 'normal', fontSize: '7px' }}>Natio-<br/>nality</span></th>}
                {showBirth && <th style={{ ...th }}>11. 生年月日、出生地※<br/><span style={{ fontWeight: 'normal', fontSize: '7px' }}>Date and place of birth</span></th>}
                {showId    && <th style={{ ...th, width: '18%' }}>身分証明書<br/><span style={{ fontWeight: 'normal', fontSize: '7px' }}>Identity document</span></th>}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: PAGE_SIZE }, (_, i) => {
                const c = pageCrew[i];
                const globalIdx = (pageNum - 1) * PAGE_SIZE + i + 1;
                const idDoc = c ? (cs.crewIdDocs?.[c.id] ?? { type: 'seaman', number: c.seamanBookNo || '' }) : null;
                return (
                  <tr key={i} style={{ height: '20px', background: i % 2 === 0 ? '#fff' : '#f7f7f7' }}>
                    <td style={{ ...td, textAlign: 'center', fontSize: 8.5 }}>{c ? globalIdx : ''}</td>
                    <td style={{ ...td, fontSize: 8.5 }}>{c?.name || ''}</td>
                    {showPos   && <td style={{ ...td, textAlign: 'center', fontSize: 8.5 }}>{c ? (c.positionInVessel || c.roleLabel) : ''}</td>}
                    {showNat   && <td style={{ ...td, textAlign: 'center', fontSize: 8.5 }}>{c ? (c.nationality || '日本') : ''}</td>}
                    {showBirth && <td style={{ ...td, fontSize: 8 }}>{c ? `${c.birthDate || ''}${c.birthPlace ? `　${c.birthPlace}` : ''}` : ''}</td>}
                    {showId    && <td style={{ ...td, fontSize: 8 }}>{c && idDoc ? `${idTypeLabel(idDoc.type)}${idDoc.number ? ` ${idDoc.number}` : ''}` : ''}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* フッター（最終ページのみ） */}
          {pageNum === totalPages && (
            <>
              <div style={{ marginTop: 10, fontSize: 9, fontWeight: 'bold' }}>
                12. 日付及び船長又は委任を受けた代理人若しくは船舶の職員の氏名
              </div>
              <div style={{ fontSize: 7.5, color: '#555', marginBottom: 6 }}>
                Date and name of master, authorized agent or officer
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginBottom: 4 }}>
                <div style={{ whiteSpace: 'nowrap', fontSize: 9 }}>日付（Date）：{cs.signDate || '　　　年　　月　　日'}</div>
                <div style={{ flex: 1, borderBottom: '1px solid #555', paddingBottom: 2, minWidth: 160, fontSize: 10 }}>
                  {cs.signName || '　'}
                </div>
              </div>
              <div style={{ marginTop: 10, paddingTop: 5, borderTop: '1px solid #ccc', fontSize: 8 }}>
                <span style={{ fontWeight: 'bold' }}>（注）</span>　※の付されている項目については記入不要。<br/>
                <span style={{ fontWeight: 'bold' }}>Note</span>　It is not necessary to fill in the item marked "※".
              </div>
            </>
          )}
        </div>
      );
    };

    return (
      <div style={{ fontFamily: serifFont }}>
        {pages.map((pageCrew, idx) => renderPage(pageCrew, idx + 1, pages.length))}
      </div>
    );
  }

  // ── 乗組員名簿（国土交通省様式 第6号） ──
  if (certType === 'roster' && rosterFormat === 'mlit') {
    const ms = mlitState!;
    const visibleCrew = allCrew.filter(c => ms.includedCrew[c.id] !== false);
    const captainMember = ms.captainId ? allCrew.find(c => c.id === ms.captainId) : null;
    const captainName = captainMember?.name || ms.captainFree || '';
    const monoFont = '"MS Gothic","ＭＳ ゴシック","Hiragino Kaku Gothic ProN","Noto Sans JP",monospace';

    // 船舶からオーナー情報を取得
    const vesselOwnerType = vessel?.ownerType ?? 'self';
    const reportCat = ms.reportCategoryId; // 使用者マスタID(表示は ownerDisplayName)
    const ownerTypeLabel =
      vesselOwnerType === 'manager'   ? '船舶管理人' :
      vesselOwnerType === 'charterer' ? '船舶借入人' : '船舶所有者';
    const ownerDisplayName =
      ms.ownerFree || (vesselOwnerType === 'self' ? companyName : (vessel?.ownerName || companyName));
    const ownerDisplayAddr =
      vesselOwnerType === 'self' ? companyAddr : (vessel?.ownerAddress || companyAddr);

    // セルスタイル
    const cb = '1px solid #444';
    const lh: React.CSSProperties = { border: cb, background: '#d8d8d8', padding: '3px 5px', fontSize: '8px', verticalAlign: 'top', fontFamily: monoFont, lineHeight: 1.3 };
    const vc: React.CSSProperties = { border: cb, padding: '4px 6px', verticalAlign: 'middle', fontSize: '10px', fontFamily: monoFont };
    const en: React.CSSProperties = { display: 'block', fontSize: '7px', color: '#333', fontFamily: monoFont, marginTop: '1px' };

    // 有/無 表示（プレーンテキスト）
    const YN = (v: boolean | null) => (
      <span style={{ fontSize: 11, fontWeight: v !== null ? 'bold' : 'normal', fontFamily: monoFont }}>
        {v === true ? '有' : v === false ? '無' : ''}
      </span>
    );

    // 届出日フォーマット
    const reportDateStr = ms.reportDate
      ? new Date(ms.reportDate).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })
      : '';

    // クルー行（最低15行）
    const crewPadded: (typeof visibleCrew[0] | null)[] = [...visibleCrew];
    while (crewPadded.length < 15) crewPadded.push(null);

    // 船員を使用する者（4行固定）
    const employerRows = [
      { no: 1, text: `${ownerDisplayAddr ? ownerDisplayAddr + '　' : ''}${ownerDisplayName}` },
      { no: 2, text: '' },
      { no: 3, text: '' },
      { no: 4, text: '' },
    ];

    const notes = [
      { no: 1, ja: '「法第72条の指定の有無」欄には、当該船舶が法第72条に基づく労働時間の特例を受けていることの有無を記載すること。更に「有」と記載した場合には、船員法施行規則上の根拠規定を記載すること。', en: 'In "Designated under Article 72 (yes/no)", indicate whether or not an exception is granted to the working hours based on Article 72 of the Mariners Law for the said vessel. If "yes", indicate the supporting provisions in the Regulations for the Enforcement of the Mariners Law.' },
      { no: 2, ja: '「警報装置の有無」欄には、当該船舶が警報により、直ちに機関区域に行くことが措置されていることの有無を記載すること。', en: 'In "Extension alarm system for call to machinery spaces (yes/no)", indicate whether measures are taken on the vessel allowing for a person to promptly go to the machinery space following the sounding of the warning device.' },
      { no: 3, ja: '「自動操舵装置の有無」欄には、当該船舶が自動操舵装置を設備していることの有無を記載すること。', en: 'In "Automatic steering system (yes/no)", indicate whether or not the said vessel has an automatic steering system.' },
      { no: 4, ja: '乗組員名簿には、当該船舶に乗り組むすべての乗組員を記載すること。', en: 'In the list of crew, indicate all crew members shipped on the said vessel.' },
      { no: 5, ja: '「受有している資格証明書」欄には、当該船員が受有している資格名を記載し、衛生管理者、救命艇手（限定救命艇手にあっては、その旨）、安全担当者、消火作業指揮者若しくは衛生担当者に選任された者、船舶料理士、航海当直部員又は危険物等取扱責任者については、その旨を記載すること。', en: 'In "Certificate", indicate the qualifications the said mariner has. If the person is a health supervisor, lifeboat man, person in charge of safety, fire fighting leader or a person appointed by a health manager, ship cook, navigational watchkeeping staff, or person responsible for handling dangerous and other substances, etc., indicate as such (if the person is a restricted lifeboat man, indicate as such).' },
      { no: 6, ja: '「番号」欄には、「船員を使用する者の住所及び氏名又は名称」欄の番号により、当該船員を使用する者を記載すること。', en: 'In "No.", indicate the person who will employ the said mariner according to the number in "Address and name of persons employing mariners"' },
      { no: 7, ja: '「船員を使用する者の住所及び氏名又は名称」欄には、当該船舶に乗り組むすべての船員について、その使用する船舶所有者（船舶共有の場合には船舶管理人、船舶貸借の場合には船舶借入人、船舶所有者、船舶管理人及び船舶借入人以外の者が船員を使用する場合にはその者）を記載すること。', en: "In \"Address and name of persons employing mariners\", indicate the shipowner who employs all the mariners shipped onboard the said vessel (in the case of co-ownership, the ship's husband, in the case of lease, the lessee, in cases where a person other than the shipowner, ship's husband and lessee employs mariners, such person)." },
      { no: 8, ja: '船舶所有者、船舶管理人及び船舶借入人以外の者が船員を使用する場合、「区分」欄に、当該使用者が船員職業安定法第6条第14項の船員派遣元事業主であるときは「派遣」と記載し、それ以外のときは「その他」と記載すること。', en: 'In cases where a person other than the shipowner, ship\'s husband and lessee employs mariners, indicate "dispatch" in "Distinction" if the said employer is the owner of a mariner\'s employment agency provided for in Article 6, Paragraph 14 of the Mariners Employment Security Act and if the employer is a person other than the said owner of a mariner\'s employment agency, indicate "others".' },
      { no: 9, ja: 'その他の事項については、海員名簿及び雇入（雇止）届出書の記載心得を参照すること。', en: 'With regard to other matters, refer to the notes on the List of Mariners and Application for the articles of agreement.' },
    ];

    return (
      <div style={{ width: '794px', background: '#fff', padding: '20px 24px', fontFamily: monoFont, fontSize: '10px', color: '#111', lineHeight: 1.4, boxSizing: 'border-box' }}>
        {/* フォーム番号 */}
        <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '13px', marginBottom: '6px' }}>(6)</div>

        {/* ── 青枠メインフォーム ── */}
        <div style={{ border: '2px solid #1565c0' }}>

          {/* ── 行1-2: 届出年月日・船名 / 船長名・最長航行時間 ── */}
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '26%' }} />
              <col style={{ width: '24%' }} />
            </colgroup>
            <tbody>
              <tr>
                <td style={{ ...lh, textAlign: 'center' }}>
                  届出年月日<span style={en}>Application date</span>
                </td>
                <td style={{ ...vc, height: 38 }}>{reportDateStr}</td>
                <td style={{ ...lh, textAlign: 'center' }}>
                  船舶番号及び船名<span style={en}>Distinctive number and Name of ship</span>
                </td>
                <td style={vc}>
                  <div style={{ fontSize: 9 }}>
                    <span style={{ color: '#666', fontSize: 8 }}>船舶番号: </span>{ms.vesselNo || vessel?.vesselNo || ''}
                  </div>
                  <div>
                    <span style={{ color: '#666', fontSize: 8 }}>IMO: </span>{vessel?.imoNumber || ''}
                    {'　'}{vessel?.name || ''}
                  </div>
                </td>
              </tr>
              <tr>
                <td style={{ ...lh, textAlign: 'center' }}>
                  船長名<span style={en}>Name of master</span>
                </td>
                <td style={{ ...vc, height: 34 }}>{captainName}</td>
                <td style={{ ...lh, textAlign: 'center' }}>
                  1日の最長航行時間<span style={en}>Max navigation hours per day</span>
                </td>
                <td style={vc}>{ms.maxHours ? `${ms.maxHours} 時間` : ''}</td>
              </tr>
            </tbody>
          </table>

          {/* ── 行3: 法第72条 / 警報装置 / 自動操舵 — ラベル左＋有無右セル ── */}
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '22%' }} /><col style={{ width: '11%' }} />
              <col style={{ width: '22%' }} /><col style={{ width: '11%' }} />
              <col style={{ width: '22%' }} /><col style={{ width: '12%' }} />
            </colgroup>
            <tbody>
              <tr>
                <td style={lh}>
                  法第72条の指定の有無
                  <span style={en}>Designated under Article 72 (yes/no)</span>
                </td>
                <td style={{ ...vc, textAlign: 'center', background: '#fff' }}>{YN(ms.art72)}</td>
                <td style={lh}>
                  警報装置の有無
                  <span style={en}>Extension alarm system (yes/no)</span>
                </td>
                <td style={{ ...vc, textAlign: 'center', background: '#fff' }}>{YN(ms.alarm)}</td>
                <td style={lh}>
                  自動操舵装置の有無
                  <span style={en}>Automatic steering system (yes/no)</span>
                </td>
                <td style={{ ...vc, textAlign: 'center', background: '#fff' }}>{YN(ms.autopilot)}</td>
              </tr>
            </tbody>
          </table>

          {/* ── 行4: 船舶所有者・管理人・借入人 ── */}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ ...lh, width: '40%', verticalAlign: 'top' }}>
                  船舶所有者、船舶管理人又は船舶借入人の住所及び氏名又は名称
                  <span style={en}>Address and name of shipowner, ship's husband or ship charterer</span>
                </td>
                <td style={{ ...vc, verticalAlign: 'top', minHeight: 52 }}>
                  <div style={{ fontSize: 9, color: '#555' }}>{ownerDisplayAddr}</div>
                  <div style={{ fontWeight: 'bold' }}>{ownerDisplayName}</div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* ── 乗組員名簿ヘッダー ── */}
          <div style={{ textAlign: 'center', padding: '5px 0', borderTop: cb, borderBottom: cb, fontSize: '11px', fontWeight: 'bold', letterSpacing: '0.05em', fontFamily: monoFont }}>
            乗組員名簿（クルーリスト）&emsp;List of Crew
          </div>

          {/* ── 乗組員テーブル ── */}
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '44%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '6%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ ...lh, textAlign: 'center', fontWeight: 'bold' }}>
                  船員手帳番号及び氏名<span style={en}>Mariner's pocket ledger No. and name</span>
                </th>
                <th style={{ ...lh, textAlign: 'center', fontWeight: 'bold' }}>
                  職務<span style={en}>Position</span>
                </th>
                <th style={{ ...lh, textAlign: 'center', fontWeight: 'bold' }}>
                  受有している資格証明書<span style={en}>Certificate</span>
                </th>
                <th style={{ ...lh, textAlign: 'center', fontWeight: 'bold' }}>
                  番号<span style={en}>No.</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {crewPadded.map((c, i) => (
                <tr key={i}>
                  <td style={{ ...vc, height: 22, fontSize: 9.5 }}>
                    {c ? `${c.seamanBookNo ? c.seamanBookNo + '　' : ''}${c.name}` : ''}
                  </td>
                  <td style={{ ...vc, textAlign: 'center', fontSize: 10, lineHeight: 1.25 }}>
                    {/* 「船長/消火作業指揮者」の併記（dual_positions）は紙と同じく2段に重ねる */}
                    {c ? (c.positionInVessel || c.roleLabel || '').split(/[/／]/).map(s => s.trim()).filter(Boolean)
                      .map((p, k) => <div key={k}>{p}</div>) : ''}
                  </td>
                  <td style={{ ...vc, textAlign: 'center', fontSize: 9 }}>
                    {/* 個別入力があればそれを優先（例:「三級海技士(航海) 一海特」と無線資格を併記） */}
                    {c ? ((ms.certText?.[c.id] ?? '').trim() || (ms.autoCert ? (c.licenseGrade || '') : '')) : ''}
                  </td>
                  <td style={{ ...vc, textAlign: 'center' }}>
                    {c ? String(visibleCrew.indexOf(c) + 1) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ── 合計人数 ── */}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ ...lh, textAlign: 'center', fontWeight: 'bold', fontSize: 10, padding: '5px 8px' }}>
                  上記乗組員の合計人数&emsp;Total of crew members above
                </td>
                <td style={{ ...vc, width: '16%', textAlign: 'center', fontWeight: 'bold' }}>
                  {visibleCrew.length}&emsp;crews
                </td>
              </tr>
            </tbody>
          </table>

          {/* 空白行 */}
          <div style={{ height: 8, borderTop: cb }} />

          {/* ── 船員を使用する者 + 官庁受理印 ── */}
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '57%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>
            <tbody>
              <tr>
                <td style={{ ...lh, textAlign: 'center', fontWeight: 'bold' }}>
                  船員を使用する者の住所及び氏名又は名称<span style={en}>Address and name of persons employing mariners</span>
                </td>
                <td style={{ ...lh, textAlign: 'center', fontWeight: 'bold' }}>
                  区分<span style={en}>Distinction</span>
                </td>
                <td style={{ border: '1px solid #444', verticalAlign: 'top', padding: 0, paddingLeft: 6 }} rowSpan={5}>
                  <div style={{ border: '1px solid #444', height: '100%', minHeight: 120, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ background: '#d8d8d8', fontFamily: 'monospace', fontSize: 9, fontWeight: 'bold', textAlign: 'center', padding: '3px 4px', borderBottom: '1px solid #444' }}>
                      官庁受理印<br/><span style={{ fontSize: 7, fontWeight: 'normal' }}>Official seal</span>
                    </div>
                    <div style={{ flex: 1 }} />
                  </div>
                </td>
              </tr>
              {employerRows.map(e => (
                <tr key={e.no}>
                  <td style={{ ...vc, height: 30 }}>
                    <span style={{ fontSize: 9, color: '#666', marginRight: 6 }}>{e.no}</span>
                    <span style={{ fontSize: 9 }}>{e.text}</span>
                  </td>
                  <td style={vc}></td>
                </tr>
              ))}
            </tbody>
          </table>

        </div>{/* end 青枠 */}

        {/* ── 記載心得 ── */}
        <div style={{ marginTop: 10, fontSize: '8.5px', lineHeight: 1.6, fontFamily: monoFont }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>記載心得&emsp;Note:</div>
          {notes.map(n => (
            <div key={n.no} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 3 }}>
                <span style={{ flexShrink: 0 }}>{n.no}.</span>
                <span>{n.ja}</span>
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '7.5px', color: '#444', marginTop: 2, marginLeft: 14 }}>
                {n.en}
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', fontSize: 14, color: '#aaa', marginTop: 10 }}>２ページ</div>
      </div>
    );
  }

  return null;
};

// ── Excel行高の自動計算ヘルパー ───────────────────────────────────────────
// 結合セル + wrapText は Excel が自動で行高を広げないため、内容から必要行数を見積もり、
// 行高(pt)を明示設定する。これを怠ると英語サブラベルや長い住所・記載心得が切れる。
type RichSeg = { text: string; fsPt: number };
/** 全角=2 / 半角=1 の表示幅で、指定列幅(文字単位)に何行必要か見積もる */
function estLines(segs: RichSeg[], widthUnits: number): number {
  let total = 0;
  for (const { text, fsPt } of segs) {
    // Excelの列幅単位は既定フォント(約11pt)基準。小さいフォントほど1行に多く入る。
    const capacity = Math.max(4, widthUnits * (11 / fsPt));
    for (const line of String(text ?? '').split('\n')) {
      if (line === '') { total += 1; continue; }
      let w = 0;
      for (const ch of line) w += ch.charCodeAt(0) > 0xFF ? 2 : 1; // 全角判定
      total += Math.max(1, Math.ceil(w / capacity));
    }
  }
  return total;
}
/** 見積り行数から行高(pt)。最大フォントの行送りで概算＋余白 */
function heightPt(segs: RichSeg[], widthUnits: number, pad = 6): number {
  const lines = estLines(segs, widthUnits);
  const maxFs = Math.max(...segs.map(s => s.fsPt), 8);
  return Math.ceil(lines * maxFs * 1.45) + pad;
}

// ── 就労証明書左パネル用コンポーネント（モジュールレベル必須 — 内部定義だと毎回アンマウントされスクロールがリセットされる）
const empInputSt: React.CSSProperties = {
  width: '100%', padding: '7px 8px', border: '1.5px solid #e2e8f0',
  borderRadius: '6px', fontSize: '11px', background: '#fff', boxSizing: 'border-box',
};
const empLabelSt: React.CSSProperties = { fontSize: '10px', color: '#94a3b8', marginBottom: '3px', display: 'block' };
const empRadioLabelSt: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer' };
const EmpSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
    <div style={{ fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '8px' }}>{title}</div>
    {children}
  </div>
);

// ── メインページ ──────────────────────────────────────────────────────────────

const CertificatesPage: React.FC = () => {
  // 前回選んだ書類を復元する。機能フラグが外れて選べなくなっていたら既定に戻す
  const [certType, setCertType]           = useState(() => {
    const saved = localStorage.getItem('cert_type') ?? 'boarding';
    return availableCertTypes().some(t => t.id === saved) ? saved : 'boarding';
  });
  const [showHistory, setShowHistory] = useState(false); // 書類の作成履歴・再出力パネル
  const [crewList, setCrewList]           = useState<CrewMember[]>([]);
  const [vesselList, setVesselList]       = useState<Vessel[]>([]);
  const [attendance, setAttendance]       = useState<AttendanceRow[]>([]);
  const [selectedCrewId, setSelectedCrewId]     = useState<string>('');
  const [selectedVesselId, setSelectedVesselId] = useState<string>('');
  const [year, setYear]   = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [activityLogs, setActivityLogs]   = useState<ActivityLog[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated]   = useState(false);
  const [loading, setLoading]       = useState(true);
  const [company, setCompany]       = useState<CompanyInfo>({ company_name: '', company_subdomain: '', company_address: null, company_tel: null, company_rep: null });
  const previewRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [boardingPreviewHtml, setBoardingPreviewHtml]       = useState<string | null>(null);
  const [boardingPreviewLoading, setBoardingPreviewLoading] = useState(false);
  const [boardingPreviewVersion, setBoardingPreviewVersion] = useState(0);

  // 乗船履歴証明書用：フィールド上書き + 手動乗船履歴
  const [boardingOverrides, setBoardingOverrides] = useState<Record<string, string>>({});
  interface ManualEntry {
    id: string; vessel_name: string; imo_number: string; capacity: string;
    owner_name: string; position: string; boarded_at: string; departed_at: string;
    board_place: string; depart_place: string;
  }
  const [boardingManualEntries, setBoardingManualEntries] = useState<ManualEntry[]>([]);
  interface CertifierRecord { id: string; name: string; relation: string | null; address: string | null; tel: string | null; position: string | null; company_name: string | null; occupation: string | null; }
  interface WorkplaceRecord { id: string; name: string; address: string | null; tel: string | null; position: string | null; occupation: string | null; department: string | null; }
  const [certifiers, setCertifiers] = useState<CertifierRecord[]>([]);
  const [selectedCertifierId, setSelectedCertifierId] = useState<string>('');
  const [workplaces, setWorkplaces] = useState<WorkplaceRecord[]>([]);
  const [reportCategories, setReportCategories] = useState<ReportCategoryRecord[]>([]);
  const [selectedWorkplaceId, setSelectedWorkplaceId] = useState<string>('');
  const [selectedAppWorkplaceId, setSelectedAppWorkplaceId] = useState<string>('');
  const bov = (key: string, fallback = '') => boardingOverrides[key] ?? fallback;
  const setBov = (key: string, val: string) => setBoardingOverrides(p => ({ ...p, [key]: val }));
  const addManualEntry = () => setBoardingManualEntries(p => [...p, {
    id: Math.random().toString(36).slice(2), vessel_name: '', imo_number: '', capacity: '',
    owner_name: '', position: '', boarded_at: '', departed_at: '', board_place: '', depart_place: '',
  }]);
  const removeManualEntry = (id: string) => setBoardingManualEntries(p => p.filter(e => e.id !== id));
  const updateManualEntry = (id: string, field: keyof ManualEntry, val: string) =>
    setBoardingManualEntries(p => p.map(e => e.id === id ? { ...e, [field]: val } : e));

  // 就労証明書用：チェックボックス・テキストフィールド・就労実績（3列個別選択）
  const [empChecks, setEmpChecks] = useState<Record<string, boolean>>({ ind_trans: true });
  const [empFields, setEmpFields] = useState<Record<string, string>>({});
  const [empWorkRecords, setEmpWorkRecords] = useState<{ label: string; days: number; totalHours: number }[]>([]);
  const [numChildren, setNumChildren] = useState(3);

  // 就労実績：3列分の年月セレクター（初期値：直近3ヶ月）
  const _now = new Date();
  const initMonths = Array.from({ length: 3 }, (_, i) => {
    const d = new Date(_now.getFullYear(), _now.getMonth() - (2 - i), 1);
    return { y: d.getFullYear(), m: d.getMonth() + 1 };
  }) as [{ y: number; m: number }, { y: number; m: number }, { y: number; m: number }];
  const [empSlots, setEmpSlots] = useState<[{ y: number; m: number }, { y: number; m: number }, { y: number; m: number }]>(initMonths);

  // 月次労働記録：様式第十六号の五（労務管理記録簿）
  const [monthlyFormat, setMonthlyFormat] = useState<MonthlyFormat>(() => (localStorage.getItem('monthly_format') as MonthlyFormat) ?? 'default');
  const [laborRec, setLaborRec] = useState<LaborRecord | null>(null);
  const [laborLoading, setLaborLoading] = useState(false);
  const [laborSettings, setLaborSettings] = useState<LaborSettings>(EMPTY_LABOR_SETTINGS);
  const [laborSettingsLoaded, setLaborSettingsLoaded] = useState(false);
  const [laborSettingsSaved, setLaborSettingsSaved] = useState<'idle' | 'saving' | 'saved'>('idle');
  const isForm16 = certType === 'monthly' && monthlyFormat === 'form16';

  // 乗組員名簿用：船別乗組員 & フィールド選択
  const [rosterCrewIds, setRosterCrewIds] = useState<string[] | null>(null);
  const [rosterCrewDetails, setRosterCrewDetails] = useState<Record<string, Partial<CrewMember>>>({});
  const [rosterFields, setRosterFields] = useState<Record<string, boolean>>({
    no: true, name: true, nameKana: true, role: true,
    employeeCode: false, birthDate: true, license: true, address: false, joinDate: false,
    vesselName: true,
  });
  // 既定は国土交通省様式(六)。廃止した「デフォルト」が保存されていたら読み替える
  const [rosterFormat, setRosterFormat] = useState<RosterFormat>(() => {
    const s = localStorage.getItem('roster_format') as RosterFormat | null;
    return s === 'customs' ? 'customs' : 'mlit';
  });
  const today = new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '年').replace(/年(\d{2})年/, '年$1月').replace(/年(\d{2})$/, '日').replace(/^(\d{4})年/, '$1年');
  const [customsState, setCustomsState] = useState<CustomsState>({
    arrDep: 'arrival', port: '', date: '', vesselNationality: '', prevPort: '',
    signDate: '', signName: '', includedCrew: {},
    crewIdDocs: {},
    showColumns: { position: true, nationality: true, birthInfo: true, idDoc: true },
  });
  const [mlitState, setMlitState] = useState<MlitState>({
    reportDate: '', captainId: '', captainFree: '', maxHours: '', vesselNo: '', art72: null, alarm: null, autopilot: null, reportCategoryId: '', ownerFree: '', employerFree: '', includedCrew: {}, autoCert: true, certText: {},
  });

  // ── データ読み込み ──
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      // 会社情報
      try {
        const res = await api.get<any>('/users/me/profile');
        const d = res?.data ?? res;
        setCompany({ company_name: d.company_name ?? '', company_subdomain: d.company_subdomain ?? '', company_address: d.company_address ?? null, company_tel: d.company_tel ?? null, company_rep: d.company_rep ?? null });
      } catch (err) { console.error('[CertificatesPage]', err); }
      // 乗組員
      try {
        const res = await api.get<any>('/users');
        const raw: any[] = res?.data ?? [];
        const mapped = raw.map((u): CrewMember => ({
          id:             String(u.id ?? ''),
          name:           String(u.user_name ?? ''),
          nameKana:       String(u.name_kana ?? ''),
          employeeCode:   String(u.employee_code ?? ''),
          role:           String(u.user_role ?? 'crew'),
          roleLabel:      ROLE_LABELS[String(u.user_role ?? 'crew')] ?? String(u.user_role ?? 'crew'),
          birthDate:      String(u.birth_date ?? '').slice(0, 10),
          joinDate:       String(u.created_at ?? '').slice(0, 10),
          address:        String(u.address ?? ''),
          licenseNo:      String(u.license_no ?? ''),
          licenseGrade:   String(u.license_grade ?? ''),
          nationality:    String(u.nationality ?? ''),
          birthPlace:     String(u.birth_place ?? ''),
          seamanBookNo:   String(u.seaman_book_no ?? ''),
          positionInVessel: '',
          vesselName:     String(u.vessel_name ?? ''),
        }));
        setCrewList(mapped);
        if (mapped.length > 0) setSelectedCrewId(mapped[0]!.id);
      } catch (err) { console.error('[CertificatesPage]', err); }
      // 船舶
      try {
        const res = await api.get<any>('/vessels');
        const raw: any[] = res?.data ?? [];
        const mapped = raw.map((v): Vessel => ({
          id:           String(v.id ?? ''),
          name:         String(v.vessel_name ?? v.name ?? ''),
          type:         String(v.vessel_type ?? v.type ?? ''),
          capacity:     Number(v.capacity ?? v.gross_tonnage ?? 0),
          imoNumber:    String(v.imo_number ?? ''),
          vesselNo:     String(v.vessel_no ?? ''),
          flag:         String(v.flag ?? 'JP'),
          ownerType:    String(v.owner_type ?? 'self'),
          ownerName:    v.owner_name ? String(v.owner_name) : null,
          ownerAddress: v.owner_address ? String(v.owner_address) : null,
        }));
        setVesselList(mapped);
        if (mapped.length > 0) setSelectedVesselId(mapped[0]!.id);
      } catch (err) { console.error('[CertificatesPage]', err); }
      setLoading(false);
    };
    init();
  }, []);

  // ── 乗組員名簿：船別乗組員取得（詳細情報含む） ──
  useEffect(() => {
    if (certType !== 'roster' || !selectedVesselId) { setRosterCrewIds(null); setRosterCrewDetails({}); return; }
    api.get<any>(`/vessels/${selectedVesselId}/crew`)
      .then(res => {
        const rows: any[] = res?.data ?? [];
        const ids = rows.map((a: any) => String(a.id ?? ''));
        setRosterCrewIds(ids);
        const details: Record<string, Partial<CrewMember>> = {};
        rows.forEach((a: any) => {
          details[String(a.id ?? '')] = {
            nationality:      String(a.nationality ?? ''),
            birthPlace:       String(a.birth_place ?? ''),
            seamanBookNo:     String(a.seaman_book_no ?? ''),
            positionInVessel: String(a.position ?? ''),
            licenseNo:        String(a.license_no ?? ''),
            licenseGrade:     String(a.license_grade ?? ''),
          };
        });
        setRosterCrewDetails(details);
        // 新しく取得した乗組員をデフォルトで全員含める（初回のみ）
        setCustomsState(prev => {
          const included: Record<string, boolean> = { ...prev.includedCrew };
          ids.forEach(id => { if (!(id in included)) included[id] = true; });
          return { ...prev, includedCrew: included };
        });
        setMlitState(prev => {
          const included: Record<string, boolean> = { ...prev.includedCrew };
          ids.forEach(id => { if (!(id in included)) included[id] = true; });
          return { ...prev, includedCrew: included };
        });
      })
      .catch(() => { setRosterCrewIds(null); setRosterCrewDetails({}); });
  }, [certType, selectedVesselId]);

  // ── 勤怠データ読み込み ──
  useEffect(() => {
    if (!selectedCrewId || certType !== 'monthly') { setAttendance([]); return; }
    api.get<any>('/attendance', { user_id: selectedCrewId, year: String(year), month: String(month) })
      .then(res => {
        // 管理者の操作（船員デモ・配乗の無い管理者の打刻）は勤怠ではないので証明書に載せない
        const raw: any[] = (res?.data ?? []).filter((r: any) => Number(r.admin_operated ?? 0) !== 1);
        setAttendance(raw.map(r => ({
          date:      String(r.work_date ?? '').slice(0, 10),
          startTime: toJstHHMM(String(r.clock_in ?? '')),
          endTime:   r.clock_out ? toJstHHMM(String(r.clock_out)) : '',
          workMin:   Number(r.work_minutes ?? 0),
          breakMin:  Number(r.break_minutes ?? 0),
          overMin:   Number(r.over_minutes ?? 0),
          vessel:    String(r.vessel_name ?? ''),
          workType:  String(r.work_type ?? ''),
          status:    String(r.status ?? 'completed'),
        })));
      })
      .catch(() => setAttendance([]));
  }, [selectedCrewId, year, month, certType]);

  // ── 労務管理記録簿（様式16-5）データ読み込み ──
  useEffect(() => {
    if (!isForm16 || !selectedCrewId) { setLaborRec(null); return; }
    let cancelled = false;
    setLaborLoading(true);
    api.get<any>('/labor-records', { user_id: selectedCrewId, year: String(year), month: String(month) })
      .then(res => { if (!cancelled) setLaborRec(res?.data ?? null); })
      .catch(() => { if (!cancelled) setLaborRec(null); })
      .finally(() => { if (!cancelled) setLaborLoading(false); });
    return () => { cancelled = true; };
  }, [isForm16, selectedCrewId, year, month]);

  // ── 協定・記名者マスタ読み込み（様式16-5 初回表示時） ──
  useEffect(() => {
    if (!isForm16 || laborSettingsLoaded) return;
    api.get<any>('/labor-records/settings')
      .then(res => {
        const d = res?.data;
        if (d) setLaborSettings({ ...EMPTY_LABOR_SETTINGS, ...d });
        setLaborSettingsLoaded(true);
      })
      .catch(() => setLaborSettingsLoaded(true));
  }, [isForm16, laborSettingsLoaded]);

  // ── 協定・記名者マスタの自動保存（変更後800msでUPSERT） ──
  useEffect(() => {
    if (!laborSettingsLoaded) return;
    setLaborSettingsSaved('saving');
    const t = setTimeout(() => {
      api.put('/labor-records/settings', laborSettings)
        .then(() => setLaborSettingsSaved('saved'))
        .catch(() => setLaborSettingsSaved('idle'));
    }, 800);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laborSettings, laborSettingsLoaded]);

  // プレビューには未保存の編集内容を即時反映
  const laborRecView: LaborRecord | null = laborRec ? {
    ...laborRec,
    agreements: {
      overtime: laborSettings.ot_agreement,
      compHoliday: laborSettings.comp_agreement,
      restSplit: laborSettings.rest_split_agreement,
    },
    signers: {
      laborManager: laborSettings.labor_manager,
      shipowner: laborSettings.shipowner || laborRec.signers.shipowner,
      dispatchOwner: laborSettings.dispatch_owner,
    },
    basePeriodMonths: laborSettings.base_period_months || '1',
  } : null;

  const handleLaborExcel = async () => {
    if (!selectedCrewId || !laborRec) return;
    try {
      await downloadFromApi(
        `/labor-records/excel?user_id=${selectedCrewId}&year=${year}&month=${month}`,
        `労務管理記録簿_${laborRec.user.name}_${year}-${String(month).padStart(2, '0')}.xlsx`,
      );
    } catch (e) { console.error(e); alert('Excel出力に失敗しました。'); }
  };

  // ── 就労証明書：選択された3列分の就労実績取得 ──
  useEffect(() => {
    if (!selectedCrewId || certType !== 'employment') { setEmpWorkRecords([]); return; }
    Promise.all(empSlots.map(({ y, m }) =>
      api.get<any>('/attendance', { user_id: selectedCrewId, year: String(y), month: String(m) })
        .then(res => {
          const raw: any[] = (res?.data ?? []).filter((r: any) => Number(r.admin_operated ?? 0) !== 1);
          const days = raw.filter((r: any) => r.status !== 'cancelled').length;
          const totalMin = raw.reduce((s: number, r: any) => s + Number(r.work_minutes ?? 0), 0);
          return { label: `${y}年${m}月`, days, totalHours: Math.round(totalMin / 60 * 10) / 10 };
        })
        .catch(() => ({ label: `${y}年${m}月`, days: 0, totalHours: 0 }))
    )).then(records => setEmpWorkRecords(records));
  }, [selectedCrewId, certType, empSlots]);

  // ── 特別作業ログ読み込み ──
  useEffect(() => {
    if (!selectedCrewId || certType !== 'monthly') { setActivityLogs([]); return; }
    const ms = String(month).padStart(2, '0');
    const lastDay = new Date(year, month, 0).getDate();
    const ld = String(lastDay).padStart(2, '0');
    api.get<any>('/activity-logs', {
      user_id:   selectedCrewId,
      date_from: `${year}-${ms}-01`,
      date_to:   `${year}-${ms}-${ld}`,
    })
      .then(res => {
        const raw: any[] = res?.data ?? [];
        setActivityLogs(raw.map(r => ({
          id:                 String(r.id ?? ''),
          activity_master_id: String(r.activity_master_id ?? ''),
          name:               String(r.name ?? ''),
          emoji:              String(r.emoji ?? '📋'),
          duration_minutes:   Number(r.duration_minutes ?? 0),
        })));
      })
      .catch(() => setActivityLogs([]));
  }, [selectedCrewId, year, month, certType]);

  // 乗船証明書プレビュー: 対象者を選ぶと自動生成。編集内容もデバウンスで自動反映
  //（boardingPreviewVersion は手動「更新」ボタン用のトリガー）
  useEffect(() => {
    if (certType !== 'boarding' || !selectedCrewId) {
      setBoardingPreviewHtml(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setBoardingPreviewLoading(true);
      const token    = api.getToken() ?? '';
      const subdomain = company.company_subdomain || '';
      const body = JSON.stringify({
        format: 'html',
        manualEntries: boardingManualEntries.map(e => ({
          vessel_name: e.vessel_name, imo_number: e.imo_number || null,
          capacity: e.capacity ? Number(e.capacity) : null, owner_name: e.owner_name || null,
          position: e.position || null, boarded_at: e.boarded_at, departed_at: e.departed_at || null,
          board_place: e.board_place || null, depart_place: e.depart_place || null,
        })).filter(e => e.vessel_name && e.boarded_at),
        overrides: boardingOverrides,
      });
      fetch(`${api.getApiBase()}/certificates/boarding-history/${selectedCrewId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin', 'Content-Type': 'application/json' },
        body,
      })
        .then(r => r.text())
        .then(html => { if (!cancelled) setBoardingPreviewHtml(html); })
        .catch(() => { if (!cancelled) setBoardingPreviewHtml(null); })
        .finally(() => { if (!cancelled) setBoardingPreviewLoading(false); });
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certType, selectedCrewId, company.company_subdomain, boardingPreviewVersion, JSON.stringify(boardingManualEntries), JSON.stringify(boardingOverrides)]);

  // 証明者マスタ・勤務先マスタ取得
  useEffect(() => {
    if (certType !== 'boarding') return;
    api.get<CertifierRecord[]>('/certificates/certifiers')
      .then(r => { const data = (r as any)?.data ?? r; if (Array.isArray(data)) setCertifiers(data); })
      .catch(() => {});
    api.get<WorkplaceRecord[]>('/certificates/workplaces')
      .then(d => setWorkplaces(Array.isArray(d) ? d : []))
      .catch(() => {});
    api.get<ReportCategoryRecord[]>('/certificates/report-categories')
      .then(d => setReportCategories(Array.isArray(d) ? d : []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certType]);

  const issueDate      = new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const selectedCrew   = crewList.find(c => c.id === selectedCrewId) ?? null;
  const selectedVessel = vesselList.find(v => v.id === selectedVesselId) ?? null;
  const certLabel      = CERT_TYPES.find(t => t.id === certType)?.label ?? '';

  // 乗組員名簿: 選択船舶に配置登録された乗組員のみ（取得失敗時は全員）、詳細情報をマージ
  const rosterCrew = (certType === 'roster' && rosterCrewIds !== null
    ? crewList.filter(c => rosterCrewIds.includes(c.id))
    : crewList
  ).map(c => ({ ...c, ...(rosterCrewDetails[c.id] ?? {}) }));

  // ── バックエンドAPIを直接呼ぶダウンロードヘルパー ──
  // apiPath は "/certificates/..." 形式（APIベースURL以降のパス）
  const downloadFromApi = async (apiPath: string, filename: string) => {
    const token = api.getToken() ?? '';
    const subdomain = company.company_subdomain || '';
    const res = await fetch(`${api.getApiBase()}${apiPath}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Company-Subdomain': subdomain,
        'X-App-Type': 'admin',
      },
    });
    if (!res.ok) throw new Error(await res.text());
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  // 乗船履歴証明書：POST でファイル生成・ダウンロード
  const downloadBoardingPost = async (format: 'pdf' | 'excel' | 'word', filename: string) => {
    if (!selectedCrewId) return;
    const token = api.getToken() ?? '';
    const subdomain = company.company_subdomain || '';
    const body = JSON.stringify({
      format,
      manualEntries: boardingManualEntries.map(e => ({
        vessel_name: e.vessel_name, imo_number: e.imo_number || null,
        capacity: e.capacity ? Number(e.capacity) : null, owner_name: e.owner_name || null,
        position: e.position || null, boarded_at: e.boarded_at, departed_at: e.departed_at || null,
        board_place: e.board_place || null, depart_place: e.depart_place || null,
      })).filter((e: any) => e.vessel_name && e.boarded_at),
      overrides: boardingOverrides,
    });
    const res = await fetch(`${api.getApiBase()}/certificates/boarding-history/${selectedCrewId}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin', 'Content-Type': 'application/json' },
      body,
    });
    if (!res.ok) throw new Error(await res.text());
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  // ── 乗組員名簿 Excel（デフォルト様式） ──
  const handleDefaultRosterExcel = async () => {
    const vessel = vesselList.find(v => v.id === selectedVesselId);
    const activeCols = ROSTER_COL_DEFS.filter(c => rosterFields[c.key] !== false);
    const colCount = activeCols.length;

    const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    wb.creator = BRAND_NAME;
    const ws = wb.addWorksheet('乗組員名簿');

    const colWidthMap: Record<string, number> = {
      address: 30, name: 18, nameKana: 16, birthDate: 12, license: 18,
    };
    ws.columns = activeCols.map(c => ({ width: colWidthMap[c.key] ?? 10 }));

    // タイトル行
    const titleText = `乗組員名簿　船名：${vessel?.name || '—'}　総トン数：${vessel?.capacity || '—'} T　IMO：${vessel?.imoNumber || '—'}`;
    const titleRow = ws.addRow([titleText]);
    ws.mergeCells(1, 1, 1, colCount);
    styleTitle(titleRow, colCount);

    // サブタイトル行
    const subRow = ws.addRow([`作成日：${issueDate}　乗組員数：${rosterCrew.length} 名`]);
    ws.mergeCells(2, 1, 2, colCount);
    subRow.height = 18;
    subRow.eachCell({ includeEmpty: true }, (cell, col) => {
      if (col > colCount) return;
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: XL_COLORS.labelBg } };
      cell.font = { size: 10, name: 'メイリオ' };
      cell.alignment = { horizontal: 'left', vertical: 'middle' };
      cell.border = THIN_BORDER;
    });

    // ヘッダー行
    const headerRow = ws.addRow(activeCols.map(c => c.header));
    styleHeader(headerRow, colCount);

    // データ行
    rosterCrew.forEach((c, i) => {
      const dr = ws.addRow(activeCols.map(col => (col.render as (c: CrewMember, i: number) => string)(c, i)));
      styleDataRow(dr, i, colCount);
    });

    // フッター
    ws.addRow([]);
    const footerRow = ws.addRow([company.company_name, '', company.company_rep || '']);
    styleFooterRow(footerRow, colCount);

    await downloadWorkbook(wb, `乗組員名簿_${vessel?.name || '全船舶'}.xlsx`);
  };

  // ── 乗組員名簿 Excel（税関様式） ──
  // プレビュー(税関様式Ｃ第2065号)と同じレイアウトで出力する
  const handleCustomsExcel = async () => {
    const cs = customsState;
    const vessel = vesselList.find(v => v.id === selectedVesselId);
    const isArrival = cs.arrDep === 'arrival';
    const visibleCrew = rosterCrew.filter(c => cs.includedCrew[c.id] !== false);
    const showPos   = cs.showColumns?.position    ?? true;
    const showNat   = cs.showColumns?.nationality ?? true;
    const showBirth = cs.showColumns?.birthInfo   ?? true;
    const showId    = cs.showColumns?.idDoc       ?? true;
    const flagLabel = cs.vesselNationality || (vessel?.flag === 'JP' ? '日本 (Japan)' : (vessel?.flag || '日本 (Japan)'));
    const idTypeLabel = (t: string) => t === 'seaman' ? '船員手帳' : t === 'passport' ? '旅券' : 'その他';

    const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    wb.creator = BRAND_NAME;
    const ws = wb.addWorksheet('税関様式', {
      pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0,
        margins: { left: 0.5, right: 0.5, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 } },
    });
    // 12列グリッド。到着/出発のチェック欄を小さな正方形にするため、
    // 乗組員表の各列(氏名など)は複数のグリッド列を結合して幅を作る。
    const COL = 12;
    const colW = [4, 8, 8, 8, 5, 9, 8, 5, 9, 9, 10, 8]; // 合計 ≈ 91（A4縦に収まる）
    ws.columns = colW.map(w => ({ width: w }));
    const spanW = (from: number, to: number) => colW.slice(from - 1, to).reduce((a, b) => a + b, 0);

    const SERIF = 'ＭＳ Ｐ明朝';
    const GRAY = 'FFD8D8D8';
    const ZEBRA = 'FFF7F7F7';
    const bdr: Partial<ExcelJS.Borders> = {
      top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
    };
    const fill = (argb: string): ExcelJS.Fill => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
    const jaLbl: Partial<ExcelJS.Font> = { bold: true, size: 9, name: SERIF };
    const enLbl: Partial<ExcelJS.Font> = { size: 7.5, italic: true, color: { argb: 'FF555555' }, name: SERIF };
    const valFt: Partial<ExcelJS.Font> = { size: 10.5, name: SERIF };

    // ── 右上: 様式番号 ──
    const fr = ws.addRow(['税関様式Ｃ第2065号　Customs Form C No.2065']);
    ws.mergeCells(fr.number, 1, fr.number, COL);
    fr.getCell(1).font = { size: 8, name: SERIF };
    fr.getCell(1).alignment = { horizontal: 'right' };

    // ── タイトル ──
    const titleRow = ws.addRow(['乗　組　員　名　簿']);
    ws.mergeCells(titleRow.number, 1, titleRow.number, COL);
    titleRow.height = 26;
    titleRow.getCell(1).font = { bold: true, size: 16, name: SERIF };
    titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    const subRow = ws.addRow(['CREW LIST']);
    ws.mergeCells(subRow.number, 1, subRow.number, COL);
    subRow.height = 16;
    subRow.getCell(1).font = { size: 10.5, name: SERIF };
    subRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    subRow.eachCell({ includeEmpty: true }, c => { c.border = { bottom: { style: 'medium' } }; });

    // ── 到着/出発チェック + Page No. ──
    // spacer[1-4] / 到着□[5] / 到着ラベル[6-7] / 出発□[8] / 出発ラベル[9-10] / Page No.[11-12]
    const adRow = ws.addRow([]);
    adRow.height = 30;
    const checkBox = (col: number, on: boolean) => {
      const c = adRow.getCell(col);
      c.value = on ? '✓' : '';
      c.border = bdr;
      c.font = { bold: true, size: 12, name: SERIF };
      c.alignment = { horizontal: 'center', vertical: 'middle' };
    };
    const adLabel = (from: number, to: number, ja: string, en: string, on: boolean) => {
      const c = adRow.getCell(from);
      c.value = { richText: [
        { text: ja + '\n', font: { bold: on, size: 9.5, name: SERIF } },
        { text: en, font: { size: 7.5, italic: true, color: { argb: 'FF555555' }, name: SERIF } },
      ] } as ExcelJS.CellRichTextValue;
      c.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      ws.mergeCells(adRow.number, from, adRow.number, to);
    };
    checkBox(5, isArrival);
    adLabel(6, 7, '到着', 'Arrival', isArrival);
    checkBox(8, !isArrival);
    adLabel(9, 10, '出発', 'Departure', !isArrival);
    const pageCell = adRow.getCell(11);
    pageCell.value = { richText: [
      { text: 'Page No.\n', font: { bold: true, size: 8, name: SERIF } },
      { text: '1 / 1', font: { bold: true, size: 10, name: SERIF } },
    ] } as ExcelJS.CellRichTextValue;
    pageCell.border = bdr;
    pageCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    ws.mergeCells(adRow.number, 11, adRow.number, 12);

    // ── フィールド 1-6（3項目×2行、各項目4グリッド列＝1/3幅） ──
    const FIELD_SPANS: [number, number][] = [[1, 4], [5, 8], [9, 12]];
    const addFieldRow = (fields: [string, string, string][]) => {
      const row = ws.addRow([]);
      let maxH = 34;
      fields.forEach(([ja, val, en], i) => {
        const [from, to] = FIELD_SPANS[i]!;
        const w = spanW(from, to);
        const segs: RichSeg[] = [
          { text: ja + '\n', fsPt: 9 },
          { text: (val || '　') + '\n', fsPt: 10.5 },
          { text: en, fsPt: 7.5 },
        ];
        const cell = row.getCell(from);
        cell.value = { richText: [
          { text: ja + '\n', font: jaLbl },
          { text: (val || '　') + '\n', font: valFt },
          { text: en, font: enLbl },
        ] } as ExcelJS.CellRichTextValue;
        cell.border = bdr;
        cell.alignment = { vertical: 'top', wrapText: true };
        ws.mergeCells(row.number, from, row.number, to);
        maxH = Math.max(maxH, heightPt(segs, w, 8));
      });
      row.height = maxH;
    };
    addFieldRow([
      ['1. 船舶の名称', vessel?.name || '—', 'Name of ship'],
      [`2. ${isArrival ? '到着港' : '出発港'}※／${isArrival ? '出発港' : '到着港'}※`, cs.port || '', `Port of ${isArrival ? 'arrival' : 'departure'}`],
      [`3. ${isArrival ? '到着日' : '出発日'}※／${isArrival ? '出発日' : '到着日'}※`, cs.date || '', `Date of ${isArrival ? 'arrival' : 'departure'}`],
    ]);
    addFieldRow([
      ['4. 船舶の国籍', flagLabel, 'Nationality of ship'],
      ['5. 前寄港地※', cs.prevPort || '', 'Port arrived from'],
      ['6. 身分証明書（乗員手帳）の種類及び番号', '各乗員欄を参照', "Nature and No. of identity document (seaman's passport)"],
    ]);

    // ── 乗組員テーブル（表示列トグルに応じてグリッド列を配分。氏名が余剰幅を吸収） ──
    type ColDef = { header: string; center: boolean; span: number; from?: number; to?: number; get: (c: typeof visibleCrew[0], idx: number) => string | number };
    const rawCols: (ColDef | null)[] = [
      { header: '7\nNo.', center: true, span: 1, get: (_c, idx) => idx + 1 },
      { header: '8. 氏名\nFamily name, given names', center: false, span: 3, get: c => c.name },
      showPos   ? { header: '9. 職名\nRank or rating', center: true, span: 2, get: (c: typeof visibleCrew[0]) => c.positionInVessel || c.roleLabel } : null,
      showNat   ? { header: '10. 国籍\nNationality', center: true, span: 1, get: (c: typeof visibleCrew[0]) => c.nationality || '日本' } : null,
      showBirth ? { header: '11. 生年月日、出生地※\nDate and place of birth', center: false, span: 3, get: (c: typeof visibleCrew[0]) => `${c.birthDate || ''}${c.birthPlace ? '　' + c.birthPlace : ''}` } : null,
      showId    ? { header: '身分証明書\nIdentity document', center: false, span: 2, get: (c: typeof visibleCrew[0]) => {
        const idDoc = cs.crewIdDocs?.[c.id] ?? { type: 'seaman', number: c.seamanBookNo || '' };
        return `${idTypeLabel(idDoc.type)}${idDoc.number ? ' ' + idDoc.number : ''}`;
      } } : null,
    ];
    const colDefs = rawCols.filter((c): c is ColDef => c !== null);
    // 非表示列で空いたグリッド列は氏名列に足して常に12列を使い切る
    const usedSpan = colDefs.reduce((s, c) => s + c.span, 0);
    const nameCol = colDefs[1]!;
    nameCol.span += (COL - usedSpan);
    // from/to を割り当て
    let cursor = 1;
    for (const c of colDefs) { c.from = cursor; c.to = cursor + c.span - 1; cursor += c.span; }

    const hdr = ws.addRow([]);
    hdr.height = 28;
    for (const c of colDefs) {
      const cell = hdr.getCell(c.from!);
      cell.value = c.header;
      cell.fill = fill(GRAY);
      cell.font = { bold: true, size: 8.5, name: SERIF };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = bdr;
      if (c.to! > c.from!) ws.mergeCells(hdr.number, c.from!, hdr.number, c.to!);
    }

    // データ行（様式らしく最低12行に揃える）
    const rowsCount = Math.max(visibleCrew.length, 12);
    for (let i = 0; i < rowsCount; i++) {
      const c = visibleCrew[i];
      const dr = ws.addRow([]);
      dr.height = 18;
      const bg = i % 2 === 0 ? 'FFFFFFFF' : ZEBRA;
      for (const cd of colDefs) {
        const cell = dr.getCell(cd.from!);
        cell.value = c ? cd.get(c, i) : '';
        cell.fill = fill(bg);
        cell.font = { size: 9.5, name: SERIF };
        cell.alignment = { vertical: 'middle', wrapText: true, horizontal: cd.center ? 'center' : 'left' };
        cell.border = bdr;
        if (cd.to! > cd.from!) ws.mergeCells(dr.number, cd.from!, dr.number, cd.to!);
      }
    }

    // ── 12. 署名欄 ──
    ws.addRow([]).height = 6;
    const sigLblRow = ws.addRow(['12. 日付及び船長又は委任を受けた代理人若しくは船舶の職員の氏名']);
    ws.mergeCells(sigLblRow.number, 1, sigLblRow.number, COL);
    sigLblRow.getCell(1).font = { bold: true, size: 9, name: SERIF };
    const sigEnRow = ws.addRow(['Date and name of master, authorized agent or officer']);
    ws.mergeCells(sigEnRow.number, 1, sigEnRow.number, COL);
    sigEnRow.getCell(1).font = enLbl;
    const sigRow = ws.addRow([]);
    sigRow.height = 24;
    sigRow.getCell(1).value = `日付（Date）：${cs.signDate || '　　　年　　月　　日'}`;
    ws.mergeCells(sigRow.number, 1, sigRow.number, 5);
    sigRow.getCell(1).font = { size: 9.5, name: SERIF };
    sigRow.getCell(1).alignment = { vertical: 'bottom' };
    ws.mergeCells(sigRow.number, 6, sigRow.number, COL);
    const sigName = sigRow.getCell(6);
    sigName.value = cs.signName || '';
    sigName.font = { size: 10.5, name: SERIF };
    sigName.alignment = { vertical: 'bottom' };
    sigName.border = { bottom: { style: 'thin' } };

    // ── （注）記載心得 ──
    ws.addRow([]).height = 6;
    const noteJa = ws.addRow(['（注）　※の付されている項目については記入不要。']);
    ws.mergeCells(noteJa.number, 1, noteJa.number, COL);
    noteJa.getCell(1).font = { size: 8.5, name: SERIF };
    noteJa.eachCell({ includeEmpty: true }, c => { c.border = { top: { style: 'thin' } }; });
    const noteEn = ws.addRow(['Note　It is not necessary to fill in the item marked "※".']);
    ws.mergeCells(noteEn.number, 1, noteEn.number, COL);
    noteEn.getCell(1).font = enLbl;

    await downloadWorkbook(wb, `乗組員名簿_税関様式_${vessel?.name || ''}.xlsx`);
  };

  // ── 乗組員名簿 Word（税関様式） ──
  const handleCustomsWord = async () => {
    const cs = customsState;
    const vessel = vesselList.find(v => v.id === selectedVesselId);
    const visibleCrew = rosterCrew.filter(c => cs.includedCrew[c.id] !== false);
    const token = api.getToken() ?? '';
    const subdomain = company.company_subdomain || '';
    try {
      const res = await fetch(`${api.getApiBase()}/certificates/customs-roster`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Company-Subdomain': subdomain,
          'X-App-Type': 'admin',
        },
        body: JSON.stringify({
          vesselName: vessel?.name || '',
          vesselTonnage: vessel?.capacity ? String(vessel.capacity) : '',
          vesselNationality: cs.vesselNationality || (vessel?.flag === 'JP' ? '日本 (Japan)' : vessel?.flag || '日本 (Japan)'),
          arrDep: cs.arrDep,
          port: cs.port,
          date: cs.date,
          prevPort: cs.prevPort,
          signDate: cs.signDate,
          signName: cs.signName,
          showColumns: cs.showColumns ?? { position: true, nationality: true, birthInfo: true, idDoc: true },
          crew: visibleCrew.map(c => {
            // DB値（seamanBookNo等）を初期値とし、左パネルで手動変更があればそれを使う
            const idDoc = cs.crewIdDocs?.[c.id] ?? { type: 'seaman', number: c.seamanBookNo || '' };
            return {
              name: c.name || '',
              position: c.positionInVessel || c.roleLabel || '',
              nationality: c.nationality || '日本',
              birthDate: c.birthDate || '',
              birthPlace: c.birthPlace || '',
              idType: idDoc.type,
              idNumber: idDoc.number,
            };
          }),
        }),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('Customs Word error:', res.status, errText);
        alert('Word生成に失敗しました');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `乗組員名簿_税関様式_${vessel?.name || ''}.docx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Customs Word fetch error:', e);
      alert('Word生成に失敗しました');
    }
  };

  // ── 乗組員名簿 Excel（国土交通省様式 第6号）──
  // プレビュー(第6号様式)と同じレイアウト・記載心得つきで出力する
  const handleMlitExcel = async () => {
    const ms = mlitState;
    const vessel = vesselList.find(v => v.id === selectedVesselId);
    const visibleCrew = rosterCrew.filter(c => ms.includedCrew[c.id] !== false);
    const captainMember = ms.captainId ? rosterCrew.find(c => c.id === ms.captainId) : null;
    const captainName = captainMember?.name || ms.captainFree || '';
    const vesselOwnerType = vessel?.ownerType ?? 'self';
    const ownerDisplayName = ms.ownerFree || (vesselOwnerType === 'self' ? company.company_name : (vessel?.ownerName || company.company_name));
    const ownerDisplayAddr = vesselOwnerType === 'self' ? (company.company_address || '') : (vessel?.ownerAddress || company.company_address || '');
    const YN = (v: boolean | null) => (v === true ? '有' : v === false ? '無' : '');
    const reportDateStr = ms.reportDate
      ? new Date(ms.reportDate).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })
      : '';

    const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
    wb.creator = BRAND_NAME;
    const ws = wb.addWorksheet('国土交通省様式', {
      pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0,
        margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 } },
    });
    // 12列グリッド（プレビューの複雑な段組を再現するため細かい列をマージして使う）
    const COL = 12;
    ws.columns = Array.from({ length: COL }, () => ({ width: 8.2 }));

    const GOTHIC = 'ＭＳ ゴシック';
    const GRAY = 'FFD8D8D8';
    const bdr: Partial<ExcelJS.Borders> = {
      top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
    };
    const fill = (argb: string): ExcelJS.Fill => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
    const jaLbl: Partial<ExcelJS.Font> = { bold: true, size: 8.5, name: GOTHIC };
    const enLbl: Partial<ExcelJS.Font> = { size: 7, color: { argb: 'FF444444' }, name: GOTHIC };
    const valFt: Partial<ExcelJS.Font> = { size: 10, name: GOTHIC };

    // 12列すべて幅8.2。結合幅(文字単位)から行高を計算し、行を必要なだけ高くする。
    const COLW = 8.2;
    const grow = (r: ExcelJS.Row, segs: RichSeg[], from: number, to: number, pad = 6) => {
      const w = (to - from + 1) * COLW;
      r.height = Math.max(Number(r.height ?? 0), heightPt(segs, w, pad));
    };
    /** グレー地のラベルセル（日本語+英語2段） */
    const labelCell = (r: ExcelJS.Row, from: number, to: number, ja: string, en: string) => {
      const c = r.getCell(from);
      c.value = { richText: [{ text: ja + '\n', font: jaLbl }, { text: en, font: enLbl }] } as ExcelJS.CellRichTextValue;
      c.fill = fill(GRAY); c.border = bdr;
      c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      if (to > from) ws.mergeCells(r.number, from, r.number, to);
      grow(r, [{ text: ja + '\n', fsPt: 8.5 }, { text: en, fsPt: 7 }], from, to);
    };
    /** 値セル */
    const valueCell = (r: ExcelJS.Row, from: number, to: number, v: string, center = false) => {
      const c = r.getCell(from);
      c.value = v; c.border = bdr; c.font = valFt;
      c.alignment = { horizontal: center ? 'center' : 'left', vertical: 'middle', wrapText: true };
      if (to > from) ws.mergeCells(r.number, from, r.number, to);
      grow(r, [{ text: v, fsPt: 10 }], from, to);
      return c;
    };

    // ── フォーム番号 (6) ──
    const fnRow = ws.addRow(['(6)']);
    ws.mergeCells(fnRow.number, 1, fnRow.number, COL);
    fnRow.getCell(1).font = { bold: true, size: 12, name: GOTHIC };
    fnRow.getCell(1).alignment = { horizontal: 'center' };

    // ── 行1: 届出年月日 / 船舶番号及び船名 ──
    const r1 = ws.addRow([]); r1.height = 34;
    labelCell(r1, 1, 3, '届出年月日', 'Application date');
    valueCell(r1, 4, 6, reportDateStr);
    labelCell(r1, 7, 9, '船舶番号及び船名', 'Distinctive number and Name of ship');
    const imoClean = (vessel?.imoNumber || '').replace(/^\s*IMO[:：]?\s*/i, '');
    const shipNoLine = [
      (ms.vesselNo || vessel?.vesselNo) ? `船舶番号: ${ms.vesselNo || vessel?.vesselNo}` : '',
      imoClean ? `IMO: ${imoClean}` : '',
    ].filter(Boolean).join('　');
    valueCell(r1, 10, 12, `${shipNoLine}${shipNoLine ? '\n' : ''}${vessel?.name || ''}`);

    // ── 行2: 船長名 / 1日の最長航行時間 ──
    const r2 = ws.addRow([]); r2.height = 30;
    labelCell(r2, 1, 3, '船長名', 'Name of master');
    valueCell(r2, 4, 6, captainName);
    labelCell(r2, 7, 9, '1日の最長航行時間', 'Max navigation hours per day');
    valueCell(r2, 10, 12, ms.maxHours ? `${ms.maxHours} 時間` : '');

    // ── 行3: 法72条 / 警報装置 / 自動操舵 ──
    const r3 = ws.addRow([]); r3.height = 34;
    labelCell(r3, 1, 3, '法第72条の指定の有無', 'Designated under Article 72 (yes/no)');
    const yn1 = valueCell(r3, 4, 4, YN(ms.art72), true); yn1.font = { bold: true, size: 11, name: GOTHIC };
    labelCell(r3, 5, 7, '警報装置の有無', 'Extension alarm system (yes/no)');
    const yn2 = valueCell(r3, 8, 8, YN(ms.alarm), true); yn2.font = { bold: true, size: 11, name: GOTHIC };
    labelCell(r3, 9, 11, '自動操舵装置の有無', 'Automatic steering system (yes/no)');
    const yn3 = valueCell(r3, 12, 12, YN(ms.autopilot), true); yn3.font = { bold: true, size: 11, name: GOTHIC };

    // ── 行4: 船舶所有者等 ──
    const r4 = ws.addRow([]);
    labelCell(r4, 1, 5, '船舶所有者、船舶管理人又は船舶借入人の住所及び氏名又は名称', "Address and name of shipowner, ship's husband or ship charterer");
    const oc = r4.getCell(6);
    oc.value = { richText: [
      { text: (ownerDisplayAddr ? ownerDisplayAddr + '\n' : ''), font: { size: 8.5, color: { argb: 'FF555555' }, name: GOTHIC } },
      { text: ownerDisplayName, font: { bold: true, size: 10, name: GOTHIC } },
    ] } as ExcelJS.CellRichTextValue;
    oc.border = bdr; oc.alignment = { vertical: 'middle', wrapText: true };
    ws.mergeCells(r4.number, 6, r4.number, COL);
    grow(r4, [{ text: (ownerDisplayAddr ? ownerDisplayAddr + '\n' : ''), fsPt: 8.5 }, { text: ownerDisplayName, fsPt: 10 }], 6, COL);

    // ── 乗組員名簿ヘッダー帯 ──
    const band = ws.addRow(['乗組員名簿（クルーリスト）　List of Crew']);
    ws.mergeCells(band.number, 1, band.number, COL);
    band.height = 20;
    const bc = band.getCell(1);
    bc.font = { bold: true, size: 10.5, name: GOTHIC };
    bc.alignment = { horizontal: 'center', vertical: 'middle' };
    bc.border = bdr;

    // ── 乗組員テーブル（氏名 A-E / 職務 F-H / 資格 I-K / 番号 L）──
    const ch = ws.addRow([]); ch.height = 26;
    labelCell(ch, 1, 5, '船員手帳番号及び氏名', "Mariner's pocket ledger No. and name");
    labelCell(ch, 6, 8, '職務', 'Position');
    labelCell(ch, 9, 11, '受有している資格証明書', 'Certificate');
    labelCell(ch, 12, 12, '番号', 'No.');

    const crewPadded: (typeof visibleCrew[0] | null)[] = [...visibleCrew];
    while (crewPadded.length < 15) crewPadded.push(null);
    crewPadded.forEach(c => {
      const dr = ws.addRow([]); dr.height = 18;
      valueCell(dr, 1, 5, c ? `${c.seamanBookNo ? c.seamanBookNo + '　' : ''}${c.name}` : '');
      // 「船長/消火作業指揮者」の併記は改行で2段に、資格は個別入力があれば優先（無線資格の併記用）
      const posText = c ? (c.positionInVessel || c.roleLabel || '').split(/[/／]/).map(s => s.trim()).filter(Boolean).join('\n') : '';
      const pc = valueCell(dr, 6, 8, posText, true);
      if (posText.includes('\n')) { pc.alignment = { ...(pc.alignment ?? {}), wrapText: true, vertical: 'middle', horizontal: 'center' }; dr.height = 26; }
      valueCell(dr, 9, 11, c ? ((ms.certText?.[c.id] ?? '').trim() || (ms.autoCert ? (c.licenseGrade || '') : '')) : '', true);
      valueCell(dr, 12, 12, c ? String(visibleCrew.indexOf(c) + 1) : '', true);
    });

    // ── 合計人数 ──
    const tot = ws.addRow([]); tot.height = 20;
    labelCell(tot, 1, 10, '上記乗組員の合計人数　Total of crew members above', '');
    const tc2 = valueCell(tot, 11, 12, `${visibleCrew.length}　crews`, true);
    tc2.font = { bold: true, size: 10.5, name: GOTHIC };

    // ── 船員を使用する者 + 官庁受理印 ──
    const eh = ws.addRow([]); eh.height = 26;
    labelCell(eh, 1, 7, '船員を使用する者の住所及び氏名又は名称', 'Address and name of persons employing mariners');
    labelCell(eh, 8, 9, '区分', 'Distinction');
    labelCell(eh, 10, 12, '官庁受理印', 'Official seal');
    const employerRows = [
      { no: 1, text: `${ownerDisplayAddr ? ownerDisplayAddr + '　' : ''}${ownerDisplayName}` },
      { no: 2, text: '' }, { no: 3, text: '' }, { no: 4, text: '' },
    ];
    const firstEmp = eh.number + 1;
    employerRows.forEach(e => {
      const er = ws.addRow([]); er.height = 24;
      valueCell(er, 1, 7, `${e.no}　${e.text}`);
      valueCell(er, 8, 9, '');
      // 官庁受理印の空欄（縦マージは後でまとめて）
      const sc2 = er.getCell(10); sc2.border = bdr;
    });
    ws.mergeCells(firstEmp, 10, firstEmp + employerRows.length - 1, 12);
    const sealCell = ws.getCell(firstEmp, 10);
    sealCell.border = bdr;
    sealCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // ── 記載心得 ──
    ws.addRow([]).height = 8;
    const noteTitle = ws.addRow(['記載心得　Note:']);
    ws.mergeCells(noteTitle.number, 1, noteTitle.number, COL);
    noteTitle.getCell(1).font = { bold: true, size: 9, name: GOTHIC };
    const notes: { no: number; ja: string; en: string }[] = [
      { no: 1, ja: '「法第72条の指定の有無」欄には、当該船舶が法第72条に基づく労働時間の特例を受けていることの有無を記載すること。更に「有」と記載した場合には、船員法施行規則上の根拠規定を記載すること。', en: 'In "Designated under Article 72 (yes/no)", indicate whether or not an exception is granted to the working hours based on Article 72 of the Mariners Law for the said vessel. If "yes", indicate the supporting provisions in the Regulations for the Enforcement of the Mariners Law.' },
      { no: 2, ja: '「警報装置の有無」欄には、当該船舶が警報により、直ちに機関区域に行くことが措置されていることの有無を記載すること。', en: 'In "Extension alarm system for call to machinery spaces (yes/no)", indicate whether measures are taken on the vessel allowing for a person to promptly go to the machinery space following the sounding of the warning device.' },
      { no: 3, ja: '「自動操舵装置の有無」欄には、当該船舶が自動操舵装置を設備していることの有無を記載すること。', en: 'In "Automatic steering system (yes/no)", indicate whether or not the said vessel has an automatic steering system.' },
      { no: 4, ja: '乗組員名簿には、当該船舶に乗り組むすべての乗組員を記載すること。', en: 'In the list of crew, indicate all crew members shipped on the said vessel.' },
      { no: 5, ja: '「受有している資格証明書」欄には、当該船員が受有している資格名を記載し、衛生管理者、救命艇手（限定救命艇手にあっては、その旨）、安全担当者、消火作業指揮者若しくは衛生担当者に選任された者、船舶料理士、航海当直部員又は危険物等取扱責任者については、その旨を記載すること。', en: 'In "Certificate", indicate the qualifications the said mariner has. If the person is a health supervisor, lifeboat man, person in charge of safety, fire fighting leader or a person appointed by a health manager, ship cook, navigational watchkeeping staff, or person responsible for handling dangerous and other substances, etc., indicate as such (if the person is a restricted lifeboat man, indicate as such).' },
      { no: 6, ja: '「番号」欄には、「船員を使用する者の住所及び氏名又は名称」欄の番号により、当該船員を使用する者を記載すること。', en: 'In "No.", indicate the person who will employ the said mariner according to the number in "Address and name of persons employing mariners"' },
      { no: 7, ja: '「船員を使用する者の住所及び氏名又は名称」欄には、当該船舶に乗り組むすべての船員について、その使用する船舶所有者（船舶共有の場合には船舶管理人、船舶貸借の場合には船舶借入人、船舶所有者、船舶管理人及び船舶借入人以外の者が船員を使用する場合にはその者）を記載すること。', en: "In \"Address and name of persons employing mariners\", indicate the shipowner who employs all the mariners shipped onboard the said vessel (in the case of co-ownership, the ship's husband, in the case of lease, the lessee, in cases where a person other than the shipowner, ship's husband and lessee employs mariners, such person)." },
      { no: 8, ja: '船舶所有者、船舶管理人及び船舶借入人以外の者が船員を使用する場合、「区分」欄に、当該使用者が船員職業安定法第6条第14項の船員派遣元事業主であるときは「派遣」と記載し、それ以外のときは「その他」と記載すること。', en: 'In cases where a person other than the shipowner, ship\'s husband and lessee employs mariners, indicate "dispatch" in "Distinction" if the said employer is the owner of a mariner\'s employment agency provided for in Article 6, Paragraph 14 of the Mariners Employment Security Act and if the employer is a person other than the said owner of a mariner\'s employment agency, indicate "others".' },
      { no: 9, ja: 'その他の事項については、海員名簿及び雇入（雇止）届出書の記載心得を参照すること。', en: 'With regard to other matters, refer to the notes on the List of Mariners and Application for the articles of agreement.' },
    ];
    const noteW = COL * COLW; // 全幅
    for (const n of notes) {
      const jaText = `${n.no}.　${n.ja}`;
      const jr = ws.addRow([jaText]);
      ws.mergeCells(jr.number, 1, jr.number, COL);
      jr.getCell(1).font = { size: 8, name: GOTHIC };
      jr.getCell(1).alignment = { vertical: 'top', wrapText: true };
      jr.height = heightPt([{ text: jaText, fsPt: 8 }], noteW, 3);
      const enText = `　　${n.en}`;
      const er2 = ws.addRow([enText]);
      ws.mergeCells(er2.number, 1, er2.number, COL);
      er2.getCell(1).font = { size: 7, color: { argb: 'FF555555' }, name: GOTHIC };
      er2.getCell(1).alignment = { vertical: 'top', wrapText: true };
      er2.height = heightPt([{ text: enText, fsPt: 7 }], noteW, 3);
    }

    await downloadWorkbook(wb, `乗組員名簿_国土交通省様式_${vessel?.name || ''}.xlsx`);
  };

  // ── 乗船履歴証明書 PDF ──
  const handleBoardingPdfDownload = async () => {
    if (!selectedCrewId) return;
    setGenerating(true);
    try {
      await downloadBoardingPost('pdf', `乗船履歴証明書_${selectedCrew?.name ?? ''}.pdf`);
      setGenerated(true);
      setTimeout(() => setGenerated(false), 4000);
    } catch (e) {
      console.error(e);
      alert('PDF生成に失敗しました。');
    } finally {
      setGenerating(false);
    }
  };

  // ── 乗船履歴証明書 Word ──
  const handleBoardingWordDownload = async () => {
    if (!selectedCrewId) return;
    try {
      await downloadBoardingPost('word', `乗船履歴証明書_${selectedCrew?.name ?? ''}.docx`);
    } catch (e) {
      console.error(e);
      alert('Word生成に失敗しました。');
    }
  };

  // ── Excel ダウンロード（就労証明書 — フロントエンド生成） ──
  const handleExcelDownload = async () => {
    if (!selectedCrew) return;
    try {
      const ck = (key: string) => (empChecks[key] ?? false) ? '■' : '□';
      const fv = (key: string, fallback = '') => empFields[key] || fallback;

      const { default: ExcelJS } = await import('exceljs');
    const wb = new ExcelJS.Workbook();
      wb.creator = BRAND_NAME;
      const ws = wb.addWorksheet('就労証明書');
      // 5列構成: No(4) / 項目(14) / 記載欄C(38) / D(32) / E(28)
      ws.columns = [{ width: 4 }, { width: 14 }, { width: 38 }, { width: 32 }, { width: 28 }];

      // 通常表示（縦A4で1枚印刷）
      ws.views = [{ state: 'normal' }];
      ws.pageSetup = {
        fitToPage: true, fitToWidth: 1, fitToHeight: 1,
        orientation: 'portrait', paperSize: 9,
        horizontalCentered: true, verticalCentered: true,
      };
      ws.pageSetup.margins = { left: 0.25, right: 0.25, top: 0.3, bottom: 0.3, header: 0.1, footer: 0.1 };

      const bdr: Partial<ExcelJS.Borders> = {
        top: { style: 'thin', color: { argb: 'FF555555' } },
        bottom: { style: 'thin', color: { argb: 'FF555555' } },
        left: { style: 'thin', color: { argb: 'FF555555' } },
        right: { style: 'thin', color: { argb: 'FF555555' } },
      };
      const fill = (argb: string): ExcelJS.Fill => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
      const NAVY = 'FF1E3A5F'; const DARK = 'FFD0D0D0'; const LABEL = 'FFF0F0F0'; const NUM = 'FFE0E0E0';
      const WHITE = 'FFFFFFFF'; const LIGHT = 'FFF8F8F8';

      const applyC = (cell: ExcelJS.Cell, bg: string, bold = false, align: ExcelJS.Alignment['horizontal'] = 'left', sz = 10) => {
        cell.fill = fill(bg); cell.border = bdr;
        cell.font = { size: sz, bold, name: 'MS PGothic' };
        cell.alignment = { horizontal: align, vertical: 'middle', wrapText: true };
      };

      // ─── ヘッダーエリア ───
      const issParts = issueDate.split('/');
      const compLines = [
        `証明日：${issParts[0] || ''}年${issParts[1] || ''}月${issParts[2] || ''}日`,
        `事業所名：${company.company_name}`,
        `代表者名：${company.company_rep || ''}`,
        `所在地　：${company.company_address || ''}`,
        `電話番号：${company.company_tel || ''}`,
      ];
      // Row 1: タイトル（A1:E1 全体マージ、白背景）
      {
        const r1 = ws.addRow(['', '', '', '', '']);
        r1.height = 45;
        ws.mergeCells(r1.number, 1, r1.number, 5);
        const tc = r1.getCell(1);
        tc.value = '就　労　証　明　書';
        tc.font = { bold: true, size: 18, name: 'MS PGothic', underline: true };
        tc.alignment = { horizontal: 'center', vertical: 'middle' };
        tc.border = {};
      }
      // Rows 2-6: 右=会社情報(D-E マージ) / 左=A-C マージ（行によって宛・証明文を配置）
      const compRowH = [20, 20, 20, 36, 20];
      compLines.forEach((line, ri) => {
        const row = ws.addRow(['', '', '', line, '']);
        row.height = compRowH[ri]!;
        ws.mergeCells(row.number, 4, row.number, 5);
        applyC(row.getCell(4), LIGHT, false, 'left', 9);
        ws.mergeCells(row.number, 1, row.number, 3);
        const lc = row.getCell(1);
        lc.fill = fill(WHITE);
        lc.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        if (ri === 2) {  // 代表者名行の左 → 宛名
          lc.value = `${fv('addressee', '　　　　　　　　　　')}　宛`;
          lc.font = { size: 11, name: 'MS PGothic' };
        } else if (ri === 3) {  // 所在地行の左 → 証明文
          lc.value = '下記の内容について、事実であることを証明いたします。';
          lc.font = { bold: true, size: 10, name: 'MS PGothic' };
        } else {
          lc.font = { size: 9, name: 'MS PGothic' };
        }
      });

      // テーブルヘッダー（記載欄は col 3-5 マージ）
      const hdr = ws.addRow(['No.', '項目', '記載欄　（■=選択済み　□=未選択）', '', '']);
      ws.mergeCells(hdr.number, 3, hdr.number, 5);
      hdr.height = 18;
      hdr.eachCell({ includeEmpty: true }, (cell, col) => {
        if (col > 5) return;
        cell.fill = fill(DARK); cell.font = { bold: true, size: 10, name: 'MS PGothic' };
        cell.alignment = { horizontal: 'center', vertical: 'middle' }; cell.border = bdr;
      });

      const BP = ['月', '火', '水', '木', '金', '土', '日', '祝日'] as const;

      type RowDef = { no: string | number; label: string; content: string; colD?: string; colE?: string; span?: number; h?: number; bg?: string; bold?: boolean; align?: ExcelJS.Alignment['horizontal'] };
      // データ行: 基本高さ 22pt（h未指定時のデフォルト）
      const items: RowDef[] = [
        { no: 1, label: '業種', span: 4, content:
          `${ck('ind_agri')} 農業・林業　${ck('ind_fish')} 漁業　${ck('ind_mine')} 鉱業・採石業・砂利採取業　${ck('ind_const')} 建設業　${ck('ind_mfg')} 製造業　${ck('ind_util')} 電気・ガス・熱供給・水道業` },
        { no: '', label: '', content:
          `${ck('ind_it')} 情報通信業　${ck('ind_trans')} 運輸業・郵便業　${ck('ind_retail')} 卸売業・小売業　${ck('ind_fin')} 金融業・保険業　${ck('ind_real')} 不動産業・物品賃貸業` },
        { no: '', label: '', content:
          `${ck('ind_acad')} 学術研究・専門・技術サービス　${ck('ind_hotel')} 宿泊業・飲食サービス業　${ck('ind_life')} 生活関連サービス業・娯楽業　${ck('ind_med')} 医療・福祉` },
        { no: '', label: '', content:
          `${ck('ind_edu')} 教育・学習支援業　${ck('ind_multi')} 複合サービス事業　${ck('ind_pub')} 公務　${ck('ind_other')} その他（${fv('ind_other_txt')}）` },

        { no: 2, label: 'フリガナ\n本人氏名', span: 2, content:
          `フリガナ：${selectedCrew.nameKana || ''}　　生年月日：${selectedCrew.birthDate || ''}` },
        { no: '', label: '', content: selectedCrew.name },

        { no: 3, label: '雇用(予定)\n期間等', content:
          `${ck('ep_indef')} 無期　${ck('ep_fixed')} 有期　　期間：${selectedCrew.joinDate || ''}　～　${fv('ep_end_y')}年${fv('ep_end_m')}月${fv('ep_end_d')}日` },

        { no: 4, label: '本人就労先\n事業所', span: 2, content: `名称：${company.company_name}` },
        { no: '', label: '', content: `住所：${company.company_address || ''}` },

        { no: 5, label: '雇用の形態', span: 2, content:
          `${ck('et_reg')} 正社員　${ck('et_part')} パート・アルバイト　${ck('et_disp')} 派遣社員　${ck('et_cont')} 契約社員　${ck('et_fisc')} 会計年度任用職員　${ck('et_tmp')} 非常勤・臨時職員　${ck('et_off')} 役員` },
        { no: '', label: '', content:
          `${ck('et_self')} 自営業主　${ck('et_fself')} 自営業専従者　${ck('et_fam')} 家族従業者　${ck('et_home')} 内職　${ck('et_out')} 業務委託　${ck('et_oth')} その他（${fv('et_oth_txt')}）` },

        { no: 6, label: '就労時間\n(固定就労)', span: 5, content:
          `勤務曜日：${BP.map(d => `${ck('wd_' + d)} ${d}`).join('　')}` },
        { no: '', label: '', content: '一月当たりの就労日数　月間　　日　　一週当たりの就労日数　週間　　日' },
        { no: '', label: '', content: `平日　　時　　分 ～ 　　時　　分（うち休憩　　分）` },
        { no: '', label: '', content: `土曜　　時　　分 ～ 　　時　　分（うち休憩　　分）` },
        { no: '', label: '', content: `日祝　　時　　分 ～ 　　時　　分（うち休憩　　分）` },

        { no: '', label: '就労時間\n(変則就労)', span: 3, content:
          `合計時間　${ck('vt_mon')} 月間　${ck('vt_week')} 週間　${fv('vt_h')}時間　${fv('vt_min')}分（うち休憩 ${fv('vt_brk')}分）` },
        { no: '', label: '', content: `就労日数　${ck('vd_mon')} 月間　${ck('vd_week')} 週間　${fv('vd_days')}日` },
        { no: '', label: '', content: `主な就労時間帯：${fv('vt_s_h')}時${fv('vt_s_m')}分 ～ ${fv('vt_e_h')}時${fv('vt_e_m')}分（うち休憩 ${fv('vt_s_brk')}分）` },

        // 就労実績: 4行×3列テーブル（年月 / 勤務日数 / 勤務時間）
        { no: 7, label: '就労実績', span: 4, bg: LIGHT, bold: true, align: 'center',
          content: '年　　月', colD: '勤務日数', colE: '勤務時間' },
        ...([0, 1, 2] as const).map((i): RowDef => {
          const slot = empSlots?.[i];
          const rec = empWorkRecords[i];
          return {
            no: '', label: '', bg: LIGHT, align: 'center',
            content: slot ? `${slot.y}年${slot.m}月` : (rec?.label ?? '—'),
            colD: rec?.days ? `${rec.days}日` : '—',
            colE: rec?.totalHours ? `${rec.totalHours}h` : '—',
          };
        }),

        { no: 8, label: '産前・産後\n休業', span: 2, content: `${ck('mat_plan')} 取得予定　${ck('mat_now')} 取得中` },
        { no: '', label: '', content: `期間：${fv('mat_sy')}年${fv('mat_sm')}月${fv('mat_sd')}日 ～ ${fv('mat_ey')}年${fv('mat_em')}月${fv('mat_ed')}日` },

        { no: 9, label: '育児休業', span: 2, content: `${ck('par_plan')} 取得予定　${ck('par_now')} 取得中　${ck('par_done')} 取得済み` },
        { no: '', label: '', content: `期間：${fv('par_sy')}年${fv('par_sm')}月${fv('par_sd')}日 ～ ${fv('par_ey')}年${fv('par_em')}月${fv('par_ed')}日` },

        { no: 10, label: '産休・育休\n以外の休業', span: 2, content:
          `${ck('ol_plan')} 取得予定　${ck('ol_now')} 取得中　${ck('ol_done')} 取得済み　理由：${ck('ol_care')} 介護休業　${ck('ol_sick')} 病休　${ck('ol_oth')} その他（${fv('ol_oth_txt')}）` },
        { no: '', label: '', content: `期間：${fv('ol_sy')}年${fv('ol_sm')}月${fv('ol_sd')}日 ～ ${fv('ol_ey')}年${fv('ol_em')}月${fv('ol_ed')}日` },

        { no: 11, label: '復職(予定)\n年月日', content: `${ck('ret_plan')} 復職予定　${ck('ret_done')} 復職済み　${fv('ret_y')}年${fv('ret_m')}月${fv('ret_d')}日` },

        { no: 12, label: '育児のための\n短時間勤務', span: 2, content: `${ck('sh_plan')} 取得予定　${ck('sh_now')} 取得中` },
        { no: '', label: '', content: `期間：${fv('sh_sy')}年${fv('sh_sm')}月${fv('sh_sd')}日 ～ ${fv('sh_ey')}年${fv('sh_em')}月${fv('sh_ed')}日　時間帯 ${fv('sh_s_h')}時${fv('sh_s_m')}分 ～ ${fv('sh_e_h')}時${fv('sh_e_m')}分（休憩 ${fv('sh_brk')}分）` },

        { no: 13, label: '保育士等\nとしての勤務', content: `${ck('cc_yes')} 有　${ck('cc_plan')} 有（予定）　${ck('cc_no')} 無` },
        { no: 14, label: '雇用契約\n更新の有無', content: `${ck('ren_yes')} 有　${ck('ren_plan')} 有（予定）　${ck('ren_no')} 無　${ck('ren_unk')} 未定` },
        { no: 15, label: '入所内定時\n育休短縮可否', content: `${ck('er_yes')} 可　${ck('er_plan')} 可（予定）　${ck('er_no')} 否` },
        { no: 16, label: '育休延長可否', content: `${ck('ext_yes')} 可　${ck('ext_plan')} 可（予定）　${ck('ext_no')} 否` },
        { no: 17, label: '単身赴任期間', content: `${fv('trf_sy')}年${fv('trf_sm')}月${fv('trf_sd')}日 ～ ${fv('trf_ey')}年${fv('trf_em')}月${fv('trf_ed')}日` },
        { no: 18, label: '備考欄', h: 38, content: fv('remarks') },
      ];

      // 子供欄（note行 + 各児童2行）、フォームキーは1始まり
      items.push({
        no: 19, label: '保護者\n記載欄',
        span: numChildren * 2 + 1,
        bg: LIGHT, bold: true, align: 'center',
        content: '※以下は保護者（申請者）が記載してください',
      });
      for (let ci = 0; ci < numChildren; ci++) {
        items.push({ no: '', label: '', content:
          `児童名：${fv(`c${ci + 1}_name`)}　生年月日：${fv(`c${ci + 1}_by`)}年${fv(`c${ci + 1}_bm`)}月${fv(`c${ci + 1}_bd`)}日　施設名：${fv(`c${ci + 1}_facility`)}` });
        items.push({ no: '', label: '', content: `続柄：${fv(`c${ci + 1}_rel`)}` });
      }

      // 行を追加（No/Labelのspan処理）
      let spanInfo: { noCell: ExcelJS.Cell; labelCell: ExcelJS.Cell; remaining: number; startRow: number } | null = null;
      const dataStartRow = ws.lastRow!.number + 1;

      items.forEach((item, idx) => {
        const row = ws.addRow([item.no !== '' ? String(item.no) : '', item.label, item.content, item.colD ?? '', item.colE ?? '']);
        row.height = item.h ?? 22;

        // No セル
        const noCell = row.getCell(1);
        applyC(noCell, NUM, true, 'center', 10);

        // Label セル
        const lblCell = row.getCell(2);
        applyC(lblCell, LABEL, false, 'center', 9);

        // Content セル（3列分離 or C+D+E マージ）
        const cCell = row.getCell(3);
        applyC(cCell, item.bg ?? WHITE, item.bold ?? false, item.align ?? 'left', 10);
        if (item.colD !== undefined) {
          // 就労実績：3列分離（C=年月 / D=勤務日数 / E=勤務時間）
          const dCell = row.getCell(4);
          applyC(dCell, item.bg ?? WHITE, item.bold ?? false, 'center', 10);
          dCell.value = item.colD;
          const eCell = row.getCell(5);
          applyC(eCell, item.bg ?? WHITE, item.bold ?? false, 'center', 10);
          eCell.value = item.colE ?? '';
        } else {
          ws.mergeCells(row.number, 3, row.number, 5);
        }

        // span処理
        if (item.span && item.span > 1 && (item.no !== '' || item.label !== '')) {
          spanInfo = { noCell, labelCell: lblCell, remaining: item.span - 1, startRow: row.number };
        } else if (item.no === '' && item.label === '' && spanInfo && spanInfo.remaining > 0) {
          noCell.value = ''; lblCell.value = '';
          spanInfo.remaining--;
          if (spanInfo.remaining === 0) {
            ws.mergeCells(spanInfo.startRow, 1, row.number, 1);
            ws.mergeCells(spanInfo.startRow, 2, row.number, 2);
            spanInfo = null;
          }
        }
        if (idx === items.length - 1 && spanInfo && spanInfo.remaining > 0) {
          ws.mergeCells(spanInfo.startRow, 1, row.number, 1);
          ws.mergeCells(spanInfo.startRow, 2, row.number, 2);
        }
      });

      // データ行の偶数行に背景色
      const lastRow = ws.lastRow!.number;
      for (let r = dataStartRow; r <= lastRow; r++) {
        const dataRow = ws.getRow(r);
        const cCell = dataRow.getCell(3);
        if (r % 2 === 0 && cCell.fill && (cCell.fill as ExcelJS.FillPattern).fgColor?.argb === WHITE) {
          cCell.fill = fill(LIGHT);
        }
      }

      await downloadWorkbook(wb, `就労証明書_${selectedCrew.name}.xlsx`);
    } catch (e) {
      console.error(e);
      alert('Excel出力に失敗しました。');
    }
  };

  // ── PDF 生成 ──
  const handleGeneratePdf = async () => {
    if (!previewRef.current) return;
    setGenerating(true);
    // contentEditable の点線下線を PDF 生成中は非表示にする
    const editableSpans = previewRef.current.querySelectorAll<HTMLElement>('[contenteditable]');
    editableSpans.forEach(el => { el.style.borderBottom = 'none'; });
    try {
      const el = previewRef.current;
      const scrollW = el.scrollWidth;
      const scrollH = el.scrollHeight;
      const canvas = await (html2canvas as any)(el, {
        scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false,
        width: scrollW, height: scrollH,
        windowWidth: scrollW, windowHeight: scrollH,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      // 様式16-5（労務管理記録簿）は横長のためA4横で出力
      const pdf = new jsPDF({ orientation: isForm16 ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();  // 210
      const pdfH = pdf.internal.pageSize.getHeight(); // 297
      const scaledH = pdfW * (canvas.height / canvas.width);

      if (certType === 'employment') {
        // 就労証明書は常に1ページに収める（子供が多くても）
        if (scaledH <= pdfH) {
          pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, scaledH);
        } else {
          // 縦方向にスケールダウンして1ページに収める
          const fitScale = pdfH / scaledH;
          const fitW = pdfW * fitScale;
          const offsetX = (pdfW - fitW) / 2;
          pdf.addImage(imgData, 'JPEG', offsetX, 0, fitW, pdfH);
        }
      } else if (scaledH <= pdfH + 1) {
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, scaledH);
      } else {
        const pageHeightPx = Math.floor(canvas.width * (pdfH / pdfW));
        let yPx = 0;
        while (yPx < canvas.height) {
          const slicePx = Math.min(canvas.height - yPx, pageHeightPx);
          const tmp = document.createElement('canvas');
          tmp.width = canvas.width; tmp.height = slicePx;
          tmp.getContext('2d')!.drawImage(canvas, 0, -yPx);
          if (yPx > 0) pdf.addPage();
          const sliceH = pdfW * (slicePx / canvas.width);
          pdf.addImage(tmp.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pdfW, sliceH);
          yPx += slicePx;
        }
      }
      const crewName = certType === 'roster' ? '全乗組員' : (selectedCrew?.name ?? '');
      pdf.save(`${certLabel}_${crewName}_${year}${String(month).padStart(2, '0')}.pdf`);
      setGenerated(true);
      setTimeout(() => setGenerated(false), 4000);
    } catch (e) {
      console.error(e);
      alert('PDF生成に失敗しました。');
    } finally {
      // 点線下線を元に戻す
      editableSpans.forEach(el => { el.style.borderBottom = ''; });
      setGenerating(false);
    }
  };

  // 就労証明書左パネル用ヘルパー（empInputSt/empLabelSt/empRadioLabelSt/EmpSection はモジュールレベル定数を使用）
  const renderDateRange = (prefix: string, _label: string) => (
    <div>
      <div style={empLabelSt}>開始日</div>
      <div style={{ display: 'flex', gap: '3px', alignItems: 'center', marginBottom: '5px' }}>
        <input type="number" placeholder="2024" value={empFields[`${prefix}_sy`]??''} onChange={e=>setEmpFields(p=>({...p,[`${prefix}_sy`]:e.target.value}))} style={{...empInputSt,width:'52px',padding:'5px 3px'}} />年
        <input type="number" placeholder="4" value={empFields[`${prefix}_sm`]??''} onChange={e=>setEmpFields(p=>({...p,[`${prefix}_sm`]:e.target.value}))} style={{...empInputSt,width:'34px',padding:'5px 3px'}} />月
        <input type="number" placeholder="1" value={empFields[`${prefix}_sd`]??''} onChange={e=>setEmpFields(p=>({...p,[`${prefix}_sd`]:e.target.value}))} style={{...empInputSt,width:'34px',padding:'5px 3px'}} />日
      </div>
      <div style={empLabelSt}>終了日</div>
      <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
        <input type="number" placeholder="2025" value={empFields[`${prefix}_ey`]??''} onChange={e=>setEmpFields(p=>({...p,[`${prefix}_ey`]:e.target.value}))} style={{...empInputSt,width:'52px',padding:'5px 3px'}} />年
        <input type="number" placeholder="3" value={empFields[`${prefix}_em`]??''} onChange={e=>setEmpFields(p=>({...p,[`${prefix}_em`]:e.target.value}))} style={{...empInputSt,width:'34px',padding:'5px 3px'}} />月
        <input type="number" placeholder="31" value={empFields[`${prefix}_ed`]??''} onChange={e=>setEmpFields(p=>({...p,[`${prefix}_ed`]:e.target.value}))} style={{...empInputSt,width:'34px',padding:'5px 3px'}} />日
      </div>
    </div>
  );

  // ── 官庁様式（雇入/変更/航行報告/航行報告証明申請）は専用パネルを表示 ──
  if (CERT_FORM_TYPES.includes(certType)) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f6ff', fontFamily: "'Inter','Helvetica Neue',sans-serif" }}>
        <div style={{ position: 'relative', background: 'linear-gradient(90deg,#0f172a 0%,#1e3a5f 100%)', padding: '0 32px', height: '60px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
          <span style={{ fontSize: '22px' }}>📄</span>
          <div><div style={{ color: '#e2e8f0', fontSize: '16px', fontWeight: 800 }}>証明書・届出書 自動生成</div><div style={{ color: '#94a3b8', fontSize: '11px' }}>官庁様式をワンクリックでPDF/Word/Excel発行</div></div>
          {/* 書類の種類ドロップダウン（中央上部に統一）*/}
          <select value={certType} onChange={e => { setCertType(e.target.value); localStorage.setItem('cert_type', e.target.value); }}
            style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', minWidth: 280, textAlign: 'center' }}>
            {availableCertTypes().map(t => <option key={t.id} value={t.id} style={{ color: '#0f172a' }}>{t.icon} {t.label}</option>)}
          </select>
          <button onClick={() => setShowHistory(true)} title="作成した書類を同じ内容で再出力する"
            style={{ marginLeft: 'auto', padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            🕘 作成履歴・再出力
          </button>
        </div>
        <div style={{ padding: 16, height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
          <CertFormsPanel key={certType} form={certType as CertForm} />
        </div>
        {showHistory && <CertFormHistoryPanel form={certType} onClose={() => setShowHistory(false)} />}
      </div>
    );
  }

  // ── UI ──
  return (
    <div style={{ minHeight: '100vh', background: '#f0f6ff', fontFamily: "'Inter','Helvetica Neue',sans-serif" }}>
      {/* ヘッダー */}
      <div style={{ position: 'relative', background: 'linear-gradient(90deg,#0f172a 0%,#1e3a5f 100%)', padding: '0 32px', height: '60px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
        <span style={{ fontSize: '22px' }}>📄</span>
        <div>
          <div style={{ color: '#e2e8f0', fontSize: '16px', fontWeight: 800 }}>証明書・労働記録 自動生成</div>
          <div style={{ color: '#475569', fontSize: '11px' }}>ワンクリックでPDF発行</div>
        </div>
        {/* 書類の種類ドロップダウン（中央上部に統一）*/}
        <select value={certType} onChange={e => { setCertType(e.target.value); localStorage.setItem('cert_type', e.target.value); setGenerated(false); }}
          style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', minWidth: 280, textAlign: 'center' }}>
          {availableCertTypes().map(t => <option key={t.id} value={t.id} style={{ color: '#0f172a' }}>{t.icon} {t.label}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {(company.company_address === null || company.company_tel === null) && (
            <div style={{ background: '#f59e0b22', border: '1px solid #f59e0b', borderRadius: 8, padding: '4px 12px', color: '#f59e0b', fontSize: 12, fontWeight: 600 }}>
              ⚠ 設定ページで会社情報（住所・TEL・代表者名）を入力してください
            </div>
          )}
          <button onClick={() => setShowHistory(true)} title="船員アプリ・管理サイトで作成した書類を同じ内容で再出力します"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', color: '#e2e8f0', fontSize: 12.5, fontWeight: 600 }}>
            🕘 作成履歴・再出力
          </button>
          <button onClick={() => navigate('/master/activities?tab=certifier')}
            title="証明者・勤務先・届出区分のマスタを開きます"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', color: '#e2e8f0', fontSize: 12.5, fontWeight: 600 }}>
            ⚙ マスタ管理
          </button>
        </div>
      </div>
      {showHistory && <CertFormHistoryPanel onClose={() => setShowHistory(false)} />}

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
        {/* 左パネル */}
        <div style={{ background: '#fff', borderRight: '1px solid #e2e8f0', overflowY: 'auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* 選択中の書類（種類の切替はヘッダー中央のドロップダウン）*/}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{CERT_TYPES.find(t => t.id === certType)?.icon}</span>{CERT_TYPES.find(t => t.id === certType)?.label}
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: 3 }}>{CERT_TYPES.find(t => t.id === certType)?.desc}</div>
          </div>

          {/* 対象者 */}
          {certType !== 'roster' && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>対象者</div>
              {loading ? (
                <div style={{ fontSize: '12px', color: '#94a3b8' }}>読み込み中...</div>
              ) : crewList.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#ef4444' }}>乗組員が登録されていません</div>
              ) : (
                <SearchableSelect
                  value={selectedCrewId}
                  onChange={setSelectedCrewId}
                  options={[...crewList]
                    .sort((a, b) => {
                      const ga = a.vesselName || 'ｚｚ'; // 未配属は最後
                      const gb = b.vesselName || 'ｚｚ';
                      if (ga !== gb) return ga.localeCompare(gb, 'ja');
                      return a.name.localeCompare(b.name, 'ja');
                    })
                    .map(c => ({
                      value: c.id, label: c.name, sub: `（${c.roleLabel}）`,
                      group: c.vesselName ? `⛴ ${c.vesselName}` : '船舶未配属',
                    }))}
                  placeholder="氏名で検索..."
                />
              )}
            </div>
          )}

          {/* 対象船舶（乗船証明書・月次労働記録は乗船記録から自動判定するため選択不要） */}
          {certType !== 'boarding' && certType !== 'monthly' && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>対象船舶</div>
              {loading ? (
                <div style={{ fontSize: '12px', color: '#94a3b8' }}>読み込み中...</div>
              ) : vesselList.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#ef4444' }}>船舶が登録されていません</div>
              ) : (
                <select value={selectedVesselId} onChange={e => setSelectedVesselId(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#fff', cursor: 'pointer' }}>
                  {vesselList.map(v => (
                    <option key={v.id} value={v.id}>{v.name}（{v.type || '種別不明'}）</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* 月次労働記録: 様式選択 + 様式16-5コントロール */}
          {certType === 'monthly' && (
            <>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>様式</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {MONTHLY_FORMATS.map(f => (
                    <button key={f.id} onClick={() => { setMonthlyFormat(f.id); localStorage.setItem('monthly_format', f.id); }}
                      style={{ padding: '9px 10px', borderRadius: 8, textAlign: 'left', border: monthlyFormat === f.id ? '2px solid #0ea5e9' : '1.5px solid #e2e8f0', background: monthlyFormat === f.id ? 'rgba(14,165,233,0.08)' : '#f8fafc', color: monthlyFormat === f.id ? '#0ea5e9' : '#374151', fontWeight: monthlyFormat === f.id ? 700 : 500, fontSize: 12, cursor: 'pointer' }}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {monthlyFormat === 'form16' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>協定・記名者（会社共通マスタ）</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: laborSettingsSaved === 'saved' ? '#16a34a' : '#94a3b8' }}>
                      {laborSettingsSaved === 'saving' ? '保存中…' : laborSettingsSaved === 'saved' ? '✓ 保存済み' : ''}
                    </span>
                  </div>
                  {([
                    ['ot_agreement',         '時間外労働協定の有無'],
                    ['comp_agreement',       '補償休日労働協定の有無'],
                    ['rest_split_agreement', '休息時間分割協定の有無'],
                  ] as [keyof LaborSettings, string][]).map(([key, label]) => (
                    <div key={key}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>{label}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {(['', '有', '無'] as const).map(v => (
                          <button key={v || 'none'} onClick={() => setLaborSettings(p => ({ ...p, [key]: v }))}
                            style={{ flex: 1, padding: '6px 0', borderRadius: 7, fontSize: 12, cursor: 'pointer',
                              border: laborSettings[key] === v ? '2px solid #0ea5e9' : '1.5px solid #e2e8f0',
                              background: laborSettings[key] === v ? 'rgba(14,165,233,0.08)' : '#f8fafc',
                              color: laborSettings[key] === v ? '#0ea5e9' : '#64748b',
                              fontWeight: laborSettings[key] === v ? 700 : 500 }}>
                            {v || '未記入'}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>基準労働期間</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" min={1} max={12} value={laborSettings.base_period_months}
                        onChange={e => setLaborSettings(p => ({ ...p, base_period_months: e.target.value }))}
                        style={{ width: 70, padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, textAlign: 'right' }} />
                      <span style={{ fontSize: 12, color: '#374151' }}>ヶ月</span>
                    </div>
                  </div>
                  {([
                    ['labor_manager',  '労務管理責任者', ''],
                    ['shipowner',      '船舶所有者', laborRec?.signers.shipowner && !laborSettings.shipowner ? `未入力時: ${laborRec.signers.shipowner}（船舶マスタ）` : '例: 〇〇海運株式会社'],
                    ['dispatch_owner', '派遣元船舶所有者（派遣の場合のみ）', ''],
                  ] as [keyof LaborSettings, string, string][]).map(([key, label, ph]) => (
                    <div key={key}>
                      <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>{label}</label>
                      <input type="text" value={laborSettings[key]}
                        onChange={e => setLaborSettings(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={ph}
                        style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                    </div>
                  ))}
                  <div style={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.6 }}>
                    ここで入力した内容は会社共通のマスタとして保存され、全乗組員の記録簿・Excel出力に反映されます。船舶所有者が未入力の場合は船舶マスタの所有者名を使用します。
                  </div>
                </div>
              )}
            </>
          )}

          {/* 乗組員名簿: 様式選択 + 様式別コントロール */}
          {certType === 'roster' && (
            <>
              {/* 様式選択 */}
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>
                  様式
                  {rosterCrewIds !== null && (
                    <span style={{ marginLeft: 8, fontWeight: 400, color: '#0ea5e9' }}>（船舶乗組員: {rosterCrewIds.length}名）</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {ROSTER_FORMATS.map(f => (
                    <button key={f.id} onClick={() => { setRosterFormat(f.id); localStorage.setItem('roster_format', f.id); }}
                      style={{ flex: 1, padding: '8px 4px', borderRadius: 8, border: rosterFormat === f.id ? '2px solid #0ea5e9' : '1.5px solid #e2e8f0', background: rosterFormat === f.id ? 'rgba(14,165,233,0.08)' : '#f8fafc', color: rosterFormat === f.id ? '#0ea5e9' : '#374151', fontWeight: rosterFormat === f.id ? 700 : 500, fontSize: 11, cursor: 'pointer' }}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 税関様式コントロール */}
              {rosterFormat === 'customs' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* 到着/出発 */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: 5 }}>区分</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {(['arrival', 'departure'] as const).map(v => (
                        <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, border: customsState.arrDep === v ? '2px solid #0ea5e9' : '1.5px solid #e2e8f0', background: customsState.arrDep === v ? 'rgba(14,165,233,0.07)' : '#f8fafc', fontWeight: customsState.arrDep === v ? 700 : 400 }}>
                          <input type="radio" name="arrDep" checked={customsState.arrDep === v}
                            onChange={() => setCustomsState(p => ({ ...p, arrDep: v }))}
                            style={{ accentColor: '#0ea5e9' }} />
                          {v === 'arrival' ? '到着' : '出発'}
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* 到着港/出発港 */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>
                      {customsState.arrDep === 'arrival' ? '2. 到着港' : '2. 出発港'}
                    </label>
                    <input type="text" value={customsState.port} onChange={e => setCustomsState(p => ({ ...p, port: e.target.value }))}
                      placeholder="例: 東京港" style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                  </div>
                  {/* 到着日/出発日 */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>
                      {customsState.arrDep === 'arrival' ? '3. 到着日' : '3. 出発日'}
                    </label>
                    <input type="date" value={customsState.date} onChange={e => setCustomsState(p => ({ ...p, date: e.target.value }))}
                      style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                  </div>
                  {/* 4. 船舶の国籍 */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>4. 船舶の国籍</label>
                    <input type="text" value={customsState.vesselNationality}
                      onChange={e => setCustomsState(p => ({ ...p, vesselNationality: e.target.value }))}
                      placeholder={selectedVessel?.flag === 'JP' ? '日本 (Japan)' : (selectedVessel?.flag || '日本 (Japan)')}
                      style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                  </div>
                  {/* 前寄港地 */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>5. 前寄港地</label>
                    <input type="text" value={customsState.prevPort} onChange={e => setCustomsState(p => ({ ...p, prevPort: e.target.value }))}
                      placeholder="例: 横浜港" style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                  </div>
                  {/* 表示列の選択 */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: 5 }}>表示列の選択</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {([
                        ['position', '9. 職名'],
                        ['nationality', '10. 国籍'],
                        ['birthInfo', '11. 生年月日・出生地'],
                        ['idDoc', '身分証明書'],
                      ] as const).map(([key, label]) => (
                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', padding: '4px 8px', borderRadius: 6,
                          background: customsState.showColumns?.[key] !== false ? '#eff6ff' : '#f8fafc',
                          border: `1px solid ${customsState.showColumns?.[key] !== false ? '#bfdbfe' : '#e2e8f0'}` }}>
                          <input type="checkbox"
                            checked={customsState.showColumns?.[key] !== false}
                            onChange={e => setCustomsState(p => ({ ...p, showColumns: { ...p.showColumns, [key]: e.target.checked } }))}
                            style={{ accentColor: '#3b82f6', cursor: 'pointer' }} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* 乗組員の選択 + 身分証明書 per person */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: 5 }}>
                      乗組員の含む/除外・身分証明書
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                      {rosterCrew.map(c => {
                        const included = customsState.includedCrew[c.id] !== false;
                        const idDoc = customsState.crewIdDocs?.[c.id] ?? { type: 'seaman', number: c.seamanBookNo || '' };
                        return (
                          <div key={c.id} style={{ border: `1px solid ${included ? '#86efac' : '#fca5a5'}`, borderRadius: 7, padding: '6px 8px', background: included ? '#f0fdf4' : '#fef2f2' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer', marginBottom: included ? 6 : 0 }}>
                              <input type="checkbox" checked={included}
                                onChange={e => setCustomsState(p => ({ ...p, includedCrew: { ...p.includedCrew, [c.id]: e.target.checked } }))}
                                style={{ accentColor: '#22c55e', cursor: 'pointer' }} />
                              <span style={{ fontWeight: 600 }}>{c.name}</span>
                              <span style={{ color: '#64748b', fontSize: 10 }}>（{c.positionInVessel || c.roleLabel}）</span>
                            </label>
                            {included && (
                              <div style={{ display: 'flex', gap: 4 }}>
                                <select value={idDoc.type}
                                  onChange={e => setCustomsState(p => ({ ...p, crewIdDocs: { ...p.crewIdDocs, [c.id]: { ...idDoc, type: e.target.value } } }))}
                                  style={{ flex: '0 0 80px', padding: '3px 4px', border: '1px solid #d1fae5', borderRadius: 5, fontSize: 10, background: '#fff' }}>
                                  <option value="seaman">船員手帳</option>
                                  <option value="passport">旅券</option>
                                  <option value="other">その他</option>
                                </select>
                                <input type="text" value={idDoc.number}
                                  onChange={e => setCustomsState(p => ({ ...p, crewIdDocs: { ...p.crewIdDocs, [c.id]: { ...idDoc, number: e.target.value } } }))}
                                  placeholder="番号"
                                  style={{ flex: 1, padding: '3px 6px', border: '1px solid #d1fae5', borderRadius: 5, fontSize: 11, background: '#fff' }} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* 署名欄 */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>12. 日付（署名欄）</label>
                    <input type="text" value={customsState.signDate} onChange={e => setCustomsState(p => ({ ...p, signDate: e.target.value }))}
                      placeholder="例: 令和7年6月29日" style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box', marginBottom: 6 }} />
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>署名者名（船長等）</label>
                    <input type="text" value={customsState.signName} onChange={e => setCustomsState(p => ({ ...p, signName: e.target.value }))}
                      placeholder="例: 山田 太郎" style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                  </div>
                </div>
              )}

              {/* 国土交通省様式コントロール */}
              {rosterFormat === 'mlit' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* 届出年月日 */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>届出年月日</label>
                    <input type="date" value={mlitState.reportDate} onChange={e => setMlitState(p => ({ ...p, reportDate: e.target.value }))}
                      style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                  </div>
                  {/* 船長名 */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>船長名</div>
                    <div style={{ marginBottom: 4 }}>
                      <SearchableSelect
                        value={mlitState.captainId}
                        onChange={id => setMlitState(p => ({ ...p, captainId: id, captainFree: '' }))}
                        options={[
                          { value: '', label: '（自由入力を使用）' },
                          ...rosterCrew.map(c => ({ value: c.id, label: c.name, sub: `（${c.positionInVessel || c.roleLabel}）` })),
                        ]}
                        placeholder="氏名で検索..."
                      />
                    </div>
                    {!mlitState.captainId && (
                      <input type="text" value={mlitState.captainFree} onChange={e => setMlitState(p => ({ ...p, captainFree: e.target.value }))}
                        placeholder="船長名を入力" style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                    )}
                  </div>
                  {/* 1日の最長航行時間 */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>1日の最長航行時間（h）</label>
                    <input type="number" min="1" max="24" value={mlitState.maxHours} onChange={e => setMlitState(p => ({ ...p, maxHours: e.target.value }))}
                      placeholder="例: 8" style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                  </div>
                  {/* 有無チェック */}
                  {([
                    { key: 'art72' as const, label: '法第72条の指定の有無' },
                    { key: 'alarm' as const, label: '警報装置の有無' },
                    { key: 'autopilot' as const, label: '自動操舵装置の有無' },
                  ]).map(({ key, label }) => (
                    <div key={key}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>{label}</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {([true, false, null] as const).map((v, idx) => (
                          <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', padding: '5px 10px', borderRadius: 7, border: mlitState[key] === v ? '2px solid #0ea5e9' : '1.5px solid #e2e8f0', background: mlitState[key] === v ? 'rgba(14,165,233,0.07)' : '#f8fafc', fontWeight: mlitState[key] === v ? 700 : 400 }}>
                            <input type="radio" name={key} checked={mlitState[key] === v}
                              onChange={() => setMlitState(p => ({ ...p, [key]: v }))}
                              style={{ accentColor: '#0ea5e9' }} />
                            {v === true ? '有' : v === false ? '無' : '未設定'}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  {/* 船舶番号 */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>船舶番号</label>
                    <input type="text" value={mlitState.vesselNo}
                      onChange={e => setMlitState(p => ({ ...p, vesselNo: e.target.value }))}
                      placeholder={selectedVessel?.vesselNo || '船舶番号を入力'}
                      style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                    {selectedVessel?.imoNumber && <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>IMO: {selectedVessel.imoNumber}（自動）</div>}
                  </div>
                  {/* 届出区分（マスタ選択） */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>届出区分</div>
                    <select value={mlitState.reportCategoryId}
                      onChange={e => {
                        if (e.target.value === '__new__') { navigate('/master/activities?tab=report-category'); return; }
                        setMlitState(p => ({ ...p, reportCategoryId: e.target.value }));
                      }}
                      style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, background: '#fff', boxSizing: 'border-box', marginBottom: 6 }}>
                      <option value="">-- マスタから選択 --</option>
                      {reportCategories.map(rc => (
                        <option key={rc.id} value={rc.id}>{rc.name}</option>
                      ))}
                      <option value="__new__">＋ 新規登録（マスタ管理へ）</option>
                    </select>
                    <input type="text" value={mlitState.ownerFree}
                      onChange={e => setMlitState(p => ({ ...p, ownerFree: e.target.value }))}
                      placeholder="使用者名（空欄=会社名）"
                      style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                  </div>
                  {/* 乗組員の選択 */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: 6 }}>乗組員の含む/除外</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
                      {rosterCrew.map(c => (
                        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer', padding: '4px 8px', borderRadius: 6, background: mlitState.includedCrew[c.id] !== false ? '#f0fdf4' : '#fef2f2', border: `1px solid ${mlitState.includedCrew[c.id] !== false ? '#86efac' : '#fca5a5'}` }}>
                          <input type="checkbox" checked={mlitState.includedCrew[c.id] !== false}
                            onChange={e => setMlitState(p => ({ ...p, includedCrew: { ...p.includedCrew, [c.id]: e.target.checked } }))}
                            style={{ accentColor: '#22c55e', cursor: 'pointer' }} />
                          {c.name}（{c.positionInVessel || c.roleLabel}）
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* 資格証明書の自動記入 */}
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', padding: '7px 10px', borderRadius: 7, background: mlitState.autoCert ? 'rgba(14,165,233,0.07)' : '#f8fafc', border: mlitState.autoCert ? '2px solid #0ea5e9' : '1.5px solid #e2e8f0' }}>
                      <input type="checkbox" checked={mlitState.autoCert}
                        onChange={e => setMlitState(p => ({ ...p, autoCert: e.target.checked }))}
                        style={{ accentColor: '#0ea5e9', cursor: 'pointer' }} />
                      <span style={{ fontWeight: 600 }}>資格証明書をDBから自動記入</span>
                    </label>
                  </div>
                  {/* 受有している資格証明書の個別入力（海技士に加えて無線資格「一海特」等を併記する用） */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                      受有している資格証明書（個別入力。空欄=自動＝海技免状の級）
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
                      {rosterCrew.filter(c => mlitState.includedCrew[c.id] !== false).map(c => (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, color: '#475569', width: 86, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
                          <input type="text" value={mlitState.certText?.[c.id] ?? ''}
                            onChange={e => setMlitState(p => ({ ...p, certText: { ...(p.certText ?? {}), [c.id]: e.target.value } }))}
                            placeholder={c.licenseGrade ? `${c.licenseGrade}（例: ${c.licenseGrade} 一海特）` : '例: 三級海技士(航海) 一海特'}
                            style={{ flex: 1, padding: '5px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 11, boxSizing: 'border-box' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* 船員を使用する者 */}
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 4 }}>
                      船員を使用する者（空欄=会社名）
                    </label>
                    <input type="text" value={mlitState.employerFree} onChange={e => setMlitState(p => ({ ...p, employerFree: e.target.value }))}
                      placeholder={company.company_name || '会社名'}
                      style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* 対象年月 */}
          {certType === 'monthly' && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>対象年月</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select value={year} onChange={e => setYear(Number(e.target.value))}
                  style={{ flex: 1, padding: '10px 8px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#fff', cursor: 'pointer' }}>
                  {YEARS.map(y => <option key={y} value={y}>{y}年</option>)}
                </select>
                <select value={month} onChange={e => setMonth(Number(e.target.value))}
                  style={{ flex: 1, padding: '10px 8px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#fff', cursor: 'pointer' }}>
                  {MONTHS.map(m => <option key={m} value={m}>{m}月</option>)}
                </select>
              </div>
              {attendance.length === 0 && !loading && (
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: 6 }}>この月の勤怠記録はありません</div>
              )}
            </div>
          )}

          {/* 乗船履歴証明書 フィールド */}
          {certType === 'boarding' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* 申請者情報 */}
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#1e40af', background: '#eff6ff', padding: '5px 8px', borderRadius: 6, border: '1px solid #bfdbfe' }}>申請者情報</div>
              {[
                { key: 'name', label: '氏名', ph: selectedCrew?.name ?? '' },
                { key: 'birthDate', label: '生年月日', ph: selectedCrew?.birthDate ?? '' },
                { key: 'licenseGrade', label: '資格・級', ph: selectedCrew?.licenseGrade ?? '' },
                { key: 'licenseNo', label: '番号', ph: selectedCrew?.licenseNo ?? '' },
                { key: 'licenseValidFrom', label: '有効期間起算日', ph: 'YYYY-MM-DD' },
                { key: 'address', label: '現住所', ph: selectedCrew?.address ?? '' },
              ].map(({ key, label, ph }) => (
                <div key={key}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 }}>{label}</label>
                  <input type="text" value={bov(key)} onChange={e => setBov(key, e.target.value)}
                    placeholder={ph || label}
                    style={{ width: '100%', padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                </div>
              ))}

              {/* 証明者情報 */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#166534', background: '#f0fdf4', padding: '5px 8px', borderRadius: 6, border: '1px solid #bbf7d0' }}>証明者情報</div>
                <button onClick={() => navigate('/master/activities?tab=certifier')}
                  style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                  マスタ登録 →
                </button>
              </div>
              {/* 証明者マスタドロップダウン */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 }}>証明者を選択</label>
                <select value={selectedCertifierId}
                  onChange={e => {
                    const id = e.target.value;
                    if (id === '__new__') { navigate('/master/activities?tab=certifier'); return; }
                    setSelectedCertifierId(id);
                    const c = certifiers.find(x => x.id === id);
                    if (c) setBoardingOverrides(p => ({ ...p, certifierName: c.name, certifierRelation: c.relation ?? '', certifierAddress: c.address ?? '', certifierTel: c.tel ?? '', certifierOccupation: c.occupation ?? '' }));
                  }}
                  style={{ width: '100%', padding: '6px 10px', border: '1.5px solid #bbf7d0', borderRadius: 7, fontSize: 12, background: '#f0fdf4', cursor: 'pointer', boxSizing: 'border-box' }}>
                  <option value="">-- マスタから選択 --</option>
                  {certifiers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.position ? ` (${c.position})` : ''}</option>
                  ))}
                  <option value="__new__">＋ 新規登録（マスタ管理へ）</option>
                </select>
              </div>
              {[
                { key: 'certifierName', label: '証明者 氏名', ph: company.company_rep ?? '' },
                { key: 'certifierRelation', label: '申請者との間柄', ph: '会社代表者' },
                { key: 'certifierAddress', label: '証明者 現住所', ph: company.company_address ?? '' },
                { key: 'certifierTel', label: '証明者 電話番号', ph: company.company_tel ?? '' },
                { key: 'certifierOccupation', label: '証明者 職業', ph: '会社役員' },
              ].map(({ key, label, ph }) => (
                <div key={key}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 }}>{label}</label>
                  <input type="text" value={bov(key)} onChange={e => setBov(key, e.target.value)}
                    placeholder={ph || label}
                    style={{ width: '100%', padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                </div>
              ))}

              {/* 申請者の勤務先又は学校（乗船期間中）＝申請者本人の情報。証明者の勤務先とは別物。 */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#9a3412', background: '#fff7ed', padding: '5px 8px', borderRadius: 6, border: '1px solid #fed7aa' }}>申請者の勤務先又は学校（乗船期間中）</div>
                <button onClick={() => navigate('/master/activities?tab=workplace')}
                  style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: '#ea580c', color: '#fff', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                  マスタ登録 →
                </button>
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 }}>勤務先/学校を選択</label>
                <select value={selectedAppWorkplaceId}
                  onChange={e => {
                    const id = e.target.value;
                    if (id === '__new__') { navigate('/master/activities?tab=workplace'); return; }
                    setSelectedAppWorkplaceId(id);
                    const w = workplaces.find(x => x.id === id);
                    if (w) setBoardingOverrides(p => ({ ...p, applicantWorkName: w.name, applicantWorkAddress: w.address ?? '', applicantWorkTel: w.tel ?? '', occupation: w.occupation ?? '', department: w.department ?? '' }));
                  }}
                  style={{ width: '100%', padding: '6px 10px', border: '1.5px solid #fed7aa', borderRadius: 7, fontSize: 12, background: '#fff7ed', cursor: 'pointer', boxSizing: 'border-box' }}>
                  <option value="">-- マスタから選択 --</option>
                  {workplaces.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                  <option value="__new__">＋ 新規登録（マスタ管理へ）</option>
                </select>
              </div>
              {[
                { key: 'occupation', label: '職業', ph: '船員' },
                { key: 'applicantWorkName', label: '名称', ph: '○○水産株式会社' },
                { key: 'applicantWorkAddress', label: '所在地', ph: '' },
                { key: 'applicantWorkTel', label: '電話番号', ph: '' },
                { key: 'department', label: '所属部署', ph: '甲板部' },
              ].map(({ key, label, ph }) => (
                <div key={key}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 }}>{label}</label>
                  <input type="text" value={bov(key)} onChange={e => setBov(key, e.target.value)}
                    placeholder={ph || label}
                    style={{ width: '100%', padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                </div>
              ))}

              {/* 証明者の勤務先 */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#1e40af', background: '#eff6ff', padding: '5px 8px', borderRadius: 6, border: '1px solid #bfdbfe' }}>証明者の勤務先</div>
                <button onClick={() => navigate('/master/activities?tab=workplace')}
                  style={{ padding: '4px 9px', borderRadius: 6, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                  マスタ登録 →
                </button>
              </div>
              {/* 勤務先マスタドロップダウン */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 }}>勤務先を選択</label>
                <select value={selectedWorkplaceId}
                  onChange={e => {
                    const id = e.target.value;
                    if (id === '__new__') { navigate('/master/activities?tab=workplace'); return; }
                    setSelectedWorkplaceId(id);
                    const w = workplaces.find(x => x.id === id);
                    if (w) setBoardingOverrides(p => ({ ...p, companyName: w.name, companyAddress: w.address ?? '', companyTel: w.tel ?? '', workplacePosition: w.position ?? '' }));
                  }}
                  style={{ width: '100%', padding: '6px 10px', border: '1.5px solid #bfdbfe', borderRadius: 7, fontSize: 12, background: '#eff6ff', cursor: 'pointer', boxSizing: 'border-box' }}>
                  <option value="">-- マスタから選択 --</option>
                  {workplaces.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                  <option value="__new__">＋ 新規登録（マスタ管理へ）</option>
                </select>
              </div>
              {[
                { key: 'companyName', label: '勤務先 名称', ph: company.company_name },
                { key: 'companyAddress', label: '勤務先 所在地', ph: company.company_address ?? '' },
                { key: 'companyTel', label: '勤務先 電話番号', ph: company.company_tel ?? '' },
                { key: 'workplacePosition', label: '勤務先 職名', ph: '代表取締役' },
              ].map(({ key, label, ph }) => (
                <div key={key}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 }}>{label}</label>
                  <input type="text" value={bov(key)} onChange={e => setBov(key, e.target.value)}
                    placeholder={ph || label}
                    style={{ width: '100%', padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 12, boxSizing: 'border-box' }} />
                </div>
              ))}

              {/* 前職・手動乗船履歴 */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', background: '#fffbeb', padding: '5px 8px', borderRadius: 6, border: '1px solid #fde68a' }}>前職・手動追加（先頭に表示）</div>
                <button onClick={addManualEntry}
                  style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: '#f59e0b', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  ＋ 追加
                </button>
              </div>
              {boardingManualEntries.map((entry, idx) => (
                <div key={entry.id} style={{ border: '1.5px solid #fde68a', borderRadius: 8, padding: 10, background: '#fffbeb', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>手動 #{idx + 1}</span>
                    <button onClick={() => removeManualEntry(entry.id)}
                      style={{ padding: '2px 8px', borderRadius: 5, border: 'none', background: '#fca5a5', color: '#991b1b', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>
                      削除
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {([
                      { f: 'vessel_name' as const, l: '船舶名*', type: 'text' },
                      { f: 'imo_number' as const, l: '船舶番号(IMO)', type: 'text' },
                      { f: 'capacity' as const, l: '総トン数', type: 'number' },
                      { f: 'owner_name' as const, l: '船舶所有者名', type: 'text' },
                      { f: 'position' as const, l: '職名', type: 'text' },
                      { f: 'boarded_at' as const, l: '乗船日*', type: 'date' },
                      { f: 'departed_at' as const, l: '下船日', type: 'date' },
                      { f: 'board_place' as const, l: '乗船場所', type: 'text' },
                      { f: 'depart_place' as const, l: '下船場所', type: 'text' },
                    ] as Array<{ f: keyof typeof entry; l: string; type: string }>).map(({ f, l, type }) => (
                      <div key={f}>
                        <label style={{ fontSize: '10px', fontWeight: 600, color: '#78350f', display: 'block', marginBottom: 2 }}>{l}</label>
                        <input type={type} value={entry[f] as string} onChange={e => updateManualEntry(entry.id, f, e.target.value)}
                          placeholder={type !== 'date' ? l.replace('*', '') : undefined}
                          style={{ width: '100%', padding: '5px 8px', border: '1px solid #fde68a', borderRadius: 5, fontSize: 11, boxSizing: 'border-box', background: '#fff' }} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {boardingManualEntries.length === 0 && (
                <div style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center', padding: '8px', background: '#f8fafc', borderRadius: 6, border: '1px dashed #e2e8f0' }}>
                  「＋ 追加」で前職等の乗船履歴を入力できます
                </div>
              )}
            </div>
          )}

          {certType === 'employment' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>

              {/* ①宛先 */}
              <EmpSection title="① 宛先">
                <input type="text" placeholder="○○市長　など" value={empFields['addressee'] ?? ''}
                  onChange={e => setEmpFields(p => ({ ...p, addressee: e.target.value }))}
                  style={empInputSt} />
              </EmpSection>

              {/* ②業種 */}
              <EmpSection title="② 業種">
                <select value={Object.entries(empChecks).find(([k,v]) => k.startsWith('ind_') && v)?.[0] ?? 'ind_trans'}
                  onChange={e => {
                    const allInd = ['ind_agri','ind_fish','ind_mine','ind_const','ind_mfg','ind_util','ind_it','ind_trans','ind_retail','ind_fin','ind_real','ind_acad','ind_hotel','ind_life','ind_med','ind_edu','ind_multi','ind_pub','ind_other'];
                    setEmpChecks(p => { const n={...p}; allInd.forEach(k=>n[k]=false); n[e.target.value]=true; return n; });
                  }}
                  style={empInputSt}>
                  <option value="ind_agri">農業・林業</option>
                  <option value="ind_fish">漁業</option>
                  <option value="ind_mine">鉱業・採石業</option>
                  <option value="ind_const">建設業</option>
                  <option value="ind_mfg">製造業</option>
                  <option value="ind_util">電気・ガス・熱供給・水道業</option>
                  <option value="ind_it">情報通信業</option>
                  <option value="ind_trans">運輸業・郵便業</option>
                  <option value="ind_retail">卸売業・小売業</option>
                  <option value="ind_fin">金融業・保険業</option>
                  <option value="ind_real">不動産業・物品賃貸業</option>
                  <option value="ind_acad">学術研究・専門・技術サービス</option>
                  <option value="ind_hotel">宿泊業・飲食サービス業</option>
                  <option value="ind_life">生活関連サービス業・娯楽業</option>
                  <option value="ind_med">医療・福祉</option>
                  <option value="ind_edu">教育・学習支援業</option>
                  <option value="ind_multi">複合サービス事業</option>
                  <option value="ind_pub">公務</option>
                  <option value="ind_other">その他</option>
                </select>
              </EmpSection>

              {/* ③雇用形態 */}
              <EmpSection title="③ 雇用形態">
                <select value={Object.entries(empChecks).find(([k,v]) => k.startsWith('et_') && v)?.[0] ?? ''}
                  onChange={e => {
                    const allEt = ['et_reg','et_part','et_disp','et_cont','et_fisc','et_tmp','et_off','et_self','et_fself','et_fam','et_home','et_out','et_oth'];
                    setEmpChecks(p => { const n={...p}; allEt.forEach(k=>n[k]=false); if(e.target.value) n[e.target.value]=true; return n; });
                  }}
                  style={empInputSt}>
                  <option value="">選択してください</option>
                  <option value="et_reg">正社員</option>
                  <option value="et_part">パート・アルバイト</option>
                  <option value="et_disp">派遣社員</option>
                  <option value="et_cont">契約社員</option>
                  <option value="et_fisc">会計年度任用職員</option>
                  <option value="et_tmp">非常勤・臨時職員</option>
                  <option value="et_off">役員</option>
                  <option value="et_self">自営業主</option>
                  <option value="et_fself">自営業専従者</option>
                  <option value="et_fam">家族従業者</option>
                  <option value="et_home">内職</option>
                  <option value="et_out">業務委託</option>
                  <option value="et_oth">その他</option>
                </select>
              </EmpSection>

              {/* ④雇用期間 */}
              <EmpSection title="④ 雇用（予定）期間">
                <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                  {[['ep_indef','無期'],['ep_fixed','有期']].map(([k,l]) => (
                    <label key={k!} style={empRadioLabelSt}>
                      <input type="radio" checked={empChecks[k!] ?? false}
                        onChange={() => setEmpChecks(p => ({ ...p, ep_indef: false, ep_fixed: false, [k!]: true }))}
                        style={{ accentColor: '#0ea5e9' }} /> {l}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px' }}>終了日（有期の場合）</div>
                <input type="date" value={
                  (empFields['ep_end_y'] && empFields['ep_end_m'] && empFields['ep_end_d'])
                    ? `${empFields['ep_end_y']}-${(empFields['ep_end_m']??'').padStart(2,'0')}-${(empFields['ep_end_d']??'').padStart(2,'0')}`
                    : ''
                } onChange={e => {
                  const [y,m,d] = (e.target.value||'').split('-');
                  setEmpFields(p => ({ ...p, ep_end_y: y??'', ep_end_m: m?String(Number(m)):'' , ep_end_d: d?String(Number(d)):'' }));
                }} style={empInputSt} />
              </EmpSection>

              {/* ⑤就労時間 */}
              <EmpSection title="⑤ 就労時間（変則就労）">
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  {[['vt_mon','月間'],['vt_week','週間']].map(([k,l]) => (
                    <label key={k!} style={empRadioLabelSt}>
                      <input type="radio" checked={empChecks[k!] ?? false}
                        onChange={() => setEmpChecks(p => ({ ...p, vt_mon: false, vt_week: false, [k!]: true }))}
                        style={{ accentColor: '#0ea5e9' }} /> {l}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', marginBottom: '6px' }}>
                  <div><div style={empLabelSt}>時間</div><input type="number" value={empFields['vt_h']??''} onChange={e=>setEmpFields(p=>({...p,vt_h:e.target.value}))} style={{...empInputSt, padding:'5px 4px'}} placeholder="190" /></div>
                  <div><div style={empLabelSt}>分</div><input type="number" value={empFields['vt_min']??''} onChange={e=>setEmpFields(p=>({...p,vt_min:e.target.value}))} style={{...empInputSt, padding:'5px 4px'}} placeholder="0" /></div>
                  <div><div style={empLabelSt}>休憩分</div><input type="number" value={empFields['vt_brk']??''} onChange={e=>setEmpFields(p=>({...p,vt_brk:e.target.value}))} style={{...empInputSt, padding:'5px 4px'}} placeholder="60" /></div>
                </div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  {[['vd_mon','月間'],['vd_week','週間']].map(([k,l]) => (
                    <label key={k!} style={empRadioLabelSt}>
                      <input type="radio" checked={empChecks[k!] ?? false}
                        onChange={() => setEmpChecks(p => ({ ...p, vd_mon: false, vd_week: false, [k!]: true }))}
                        style={{ accentColor: '#0ea5e9' }} /> {l}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px', marginBottom: '6px' }}>
                  <div><div style={empLabelSt}>就労日数</div><input type="number" value={empFields['vd_days']??''} onChange={e=>setEmpFields(p=>({...p,vd_days:e.target.value}))} style={empInputSt} placeholder="20" /></div>
                </div>
                <div style={empLabelSt}>シフト時間帯</div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <input type="number" placeholder="8" value={empFields['vt_s_h']??''} onChange={e=>setEmpFields(p=>({...p,vt_s_h:e.target.value}))} style={{...empInputSt, width:'38px', padding:'5px 4px'}} />時
                  <input type="number" placeholder="00" value={empFields['vt_s_m']??''} onChange={e=>setEmpFields(p=>({...p,vt_s_m:e.target.value}))} style={{...empInputSt, width:'34px', padding:'5px 4px'}} />分～
                  <input type="number" placeholder="17" value={empFields['vt_e_h']??''} onChange={e=>setEmpFields(p=>({...p,vt_e_h:e.target.value}))} style={{...empInputSt, width:'38px', padding:'5px 4px'}} />時
                  <input type="number" placeholder="00" value={empFields['vt_e_m']??''} onChange={e=>setEmpFields(p=>({...p,vt_e_m:e.target.value}))} style={{...empInputSt, width:'34px', padding:'5px 4px'}} />分
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginTop: '4px' }}>
                  <span style={empLabelSt}>休憩</span>
                  <input type="number" placeholder="60" value={empFields['vt_s_brk']??''} onChange={e=>setEmpFields(p=>({...p,vt_s_brk:e.target.value}))} style={{...empInputSt, width:'50px', padding:'5px 4px'}} />分
                </div>
              </EmpSection>

              {/* ⑥就労実績 */}
              <EmpSection title="⑥ 就労実績の対象年月">
                {[0,1,2].map(i => (
                  <div key={i} style={{ display:'flex', gap:'5px', alignItems:'center', marginBottom:'5px' }}>
                    <span style={{ fontSize:'10px', color:'#94a3b8', width:'28px', flexShrink:0 }}>#{i+1}</span>
                    <select value={empSlots[i]!.y} onChange={e=>setEmpSlots(prev=>{const n=[...prev] as typeof prev; n[i]={...n[i]!,y:Number(e.target.value)}; return n;})}
                      style={{ flex:1, padding:'6px 4px', border:'1.5px solid #bae6fd', borderRadius:'6px', fontSize:'11px', background:'#f0f9ff', cursor:'pointer' }}>
                      {YEARS.map(y=><option key={y} value={y}>{y}年</option>)}
                    </select>
                    <select value={empSlots[i]!.m} onChange={e=>setEmpSlots(prev=>{const n=[...prev] as typeof prev; n[i]={...n[i]!,m:Number(e.target.value)}; return n;})}
                      style={{ flex:1, padding:'6px 4px', border:'1.5px solid #bae6fd', borderRadius:'6px', fontSize:'11px', background:'#f0f9ff', cursor:'pointer' }}>
                      {MONTHS.map(m=><option key={m} value={m}>{m}月</option>)}
                    </select>
                  </div>
                ))}
              </EmpSection>

              {/* ⑦産前産後休業 */}
              <EmpSection title="⑦ 産前・産後休業">
                <div style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
                  {[['mat_plan','取得予定'],['mat_now','取得中']].map(([k,l])=>(
                    <label key={k!} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>setEmpChecks(p=>({...p,mat_plan:false,mat_now:false,[k!]:true}))} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                  ))}
                </div>
                {renderDateRange('mat','産前産後休業')}
              </EmpSection>

              {/* ⑧育児休業 */}
              <EmpSection title="⑧ 育児休業">
                <div style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
                  {[['par_plan','取得予定'],['par_now','取得中'],['par_done','取得済み']].map(([k,l])=>(
                    <label key={k!} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>setEmpChecks(p=>({...p,par_plan:false,par_now:false,par_done:false,[k!]:true}))} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                  ))}
                </div>
                {renderDateRange('par','育児休業')}
              </EmpSection>

              {/* ⑨その他休業 */}
              <EmpSection title="⑨ 産休・育休以外の休業">
                <div style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
                  {[['ol_plan','取得予定'],['ol_now','取得中'],['ol_done','取得済み']].map(([k,l])=>(
                    <label key={k!} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>setEmpChecks(p=>({...p,ol_plan:false,ol_now:false,ol_done:false,[k!]:true}))} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                  ))}
                </div>
                <div style={{ display:'flex', gap:'6px', marginBottom:'6px' }}>
                  {[['ol_care','介護休業'],['ol_sick','病休'],['ol_oth','その他']].map(([k,l])=>(
                    <label key={k!} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>setEmpChecks(p=>({...p,ol_care:false,ol_sick:false,ol_oth:false,[k!]:true}))} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                  ))}
                </div>
                {empChecks['ol_oth'] && <input type="text" placeholder="その他の理由" value={empFields['ol_oth_txt']??''} onChange={e=>setEmpFields(p=>({...p,ol_oth_txt:e.target.value}))} style={{...empInputSt, marginBottom:'6px'}} />}
                {renderDateRange('ol','その他休業')}
              </EmpSection>

              {/* ⑩復職 */}
              <EmpSection title="⑩ 復職（予定）年月日">
                <div style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
                  {[['ret_plan','復職予定'],['ret_done','復職済み']].map(([k,l])=>(
                    <label key={k!} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>setEmpChecks(p=>({...p,ret_plan:false,ret_done:false,[k!]:true}))} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                  ))}
                </div>
                <div style={{ display:'flex', gap:'4px', alignItems:'center' }}>
                  <input type="number" placeholder="2024" value={empFields['ret_y']??''} onChange={e=>setEmpFields(p=>({...p,ret_y:e.target.value}))} style={{...empInputSt, width:'52px'}} />年
                  <input type="number" placeholder="4" value={empFields['ret_m']??''} onChange={e=>setEmpFields(p=>({...p,ret_m:e.target.value}))} style={{...empInputSt, width:'38px'}} />月
                  <input type="number" placeholder="1" value={empFields['ret_d']??''} onChange={e=>setEmpFields(p=>({...p,ret_d:e.target.value}))} style={{...empInputSt, width:'38px'}} />日
                </div>
              </EmpSection>

              {/* ⑪育児短時間勤務 */}
              <EmpSection title="⑪ 育児のための短時間勤務">
                <div style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
                  {[['sh_plan','取得予定'],['sh_now','取得中']].map(([k,l])=>(
                    <label key={k!} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>setEmpChecks(p=>({...p,sh_plan:false,sh_now:false,[k!]:true}))} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                  ))}
                </div>
                {renderDateRange('sh','短時間勤務')}
                <div style={empLabelSt}>就労時間帯</div>
                <div style={{ display:'flex', gap:'4px', alignItems:'center' }}>
                  <input type="number" placeholder="9" value={empFields['sh_s_h']??''} onChange={e=>setEmpFields(p=>({...p,sh_s_h:e.target.value}))} style={{...empInputSt, width:'38px', padding:'5px 4px'}} />時
                  <input type="number" placeholder="00" value={empFields['sh_s_m']??''} onChange={e=>setEmpFields(p=>({...p,sh_s_m:e.target.value}))} style={{...empInputSt, width:'34px', padding:'5px 4px'}} />分～
                  <input type="number" placeholder="15" value={empFields['sh_e_h']??''} onChange={e=>setEmpFields(p=>({...p,sh_e_h:e.target.value}))} style={{...empInputSt, width:'38px', padding:'5px 4px'}} />時
                  <input type="number" placeholder="00" value={empFields['sh_e_m']??''} onChange={e=>setEmpFields(p=>({...p,sh_e_m:e.target.value}))} style={{...empInputSt, width:'34px', padding:'5px 4px'}} />分
                </div>
                <div style={{ display:'flex', gap:'4px', alignItems:'center', marginTop:'4px' }}>
                  <span style={empLabelSt}>休憩</span>
                  <input type="number" placeholder="60" value={empFields['sh_brk']??''} onChange={e=>setEmpFields(p=>({...p,sh_brk:e.target.value}))} style={{...empInputSt, width:'50px', padding:'5px 4px'}} />分
                </div>
              </EmpSection>

              {/* ⑫保育士等の勤務 */}
              <EmpSection title="⑫ 保育士等としての勤務実態">
                <div style={{ display:'flex', gap:'8px' }}>
                  {[['cc_yes','有'],['cc_plan','有（予定）'],['cc_no','無']].map(([k,l])=>(
                    <label key={k!} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>setEmpChecks(p=>({...p,cc_yes:false,cc_plan:false,cc_no:false,[k!]:true}))} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                  ))}
                </div>
              </EmpSection>

              {/* ⑬更新の有無 */}
              <EmpSection title="⑬ 雇用契約更新の有無">
                <div style={{ display:'flex', gap:'8px' }}>
                  {[['ren_yes','有'],['ren_plan','有（予定）'],['ren_no','無'],['ren_unk','未定']].map(([k,l])=>(
                    <label key={k!} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>setEmpChecks(p=>({...p,ren_yes:false,ren_plan:false,ren_no:false,ren_unk:false,[k!]:true}))} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                  ))}
                </div>
              </EmpSection>

              {/* ⑭入所内定時育休短縮 */}
              <EmpSection title="⑭ 入所内定時育休短縮可否">
                <div style={{ display:'flex', gap:'8px' }}>
                  {[['er_yes','可'],['er_plan','可（予定）'],['er_no','否']].map(([k,l])=>(
                    <label key={k!} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>setEmpChecks(p=>({...p,er_yes:false,er_plan:false,er_no:false,[k!]:true}))} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                  ))}
                </div>
              </EmpSection>

              {/* ⑮育休延長 */}
              <EmpSection title="⑮ 育児休業延長可否">
                <div style={{ display:'flex', gap:'8px' }}>
                  {[['ext_yes','可'],['ext_plan','可（予定）'],['ext_no','否']].map(([k,l])=>(
                    <label key={k!} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>setEmpChecks(p=>({...p,ext_yes:false,ext_plan:false,ext_no:false,[k!]:true}))} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                  ))}
                </div>
              </EmpSection>

              {/* ⑯単身赴任期間 */}
              <EmpSection title="⑯ 単身赴任期間">
                {renderDateRange('trf','単身赴任')}
              </EmpSection>

              {/* ⑰備考 */}
              <EmpSection title="⑰ 備考欄">
                <textarea rows={3} placeholder="自由記載" value={empFields['remarks']??''}
                  onChange={e=>setEmpFields(p=>({...p,remarks:e.target.value}))}
                  style={{ width:'100%', padding:'6px 8px', border:'1.5px solid #e2e8f0', borderRadius:'6px', fontSize:'11px', resize:'vertical', boxSizing:'border-box' }} />
              </EmpSection>

              {/* ⑱保護者記載欄（動的追加可能） */}
              <EmpSection title="⑱ 保護者記載欄（申請者が記入）">
                {Array.from({ length: numChildren }, (_, idx) => idx + 1).map(i => (
                  <div key={i} style={{ background:'#f8fafc', borderRadius:'6px', padding:'8px', marginBottom:'8px', border:'1px solid #e2e8f0' }}>
                    <div style={{ fontSize:'10px', fontWeight:700, color:'#64748b', marginBottom:'6px' }}>児童 {i}</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                      <div><span style={empLabelSt}>氏名</span><input type="text" value={empFields[`c${i}_name`]??''} onChange={e=>setEmpFields(p=>({...p,[`c${i}_name`]:e.target.value}))} style={empInputSt} /></div>
                      <div>
                        <span style={empLabelSt}>生年月日</span>
                        <div style={{ display:'flex', gap:'3px', alignItems:'center' }}>
                          <input type="number" placeholder="2020" value={empFields[`c${i}_by`]??''} onChange={e=>setEmpFields(p=>({...p,[`c${i}_by`]:e.target.value}))} style={{...empInputSt,width:'52px',padding:'5px 3px'}} />年
                          <input type="number" placeholder="4" value={empFields[`c${i}_bm`]??''} onChange={e=>setEmpFields(p=>({...p,[`c${i}_bm`]:e.target.value}))} style={{...empInputSt,width:'34px',padding:'5px 3px'}} />月
                          <input type="number" placeholder="1" value={empFields[`c${i}_bd`]??''} onChange={e=>setEmpFields(p=>({...p,[`c${i}_bd`]:e.target.value}))} style={{...empInputSt,width:'34px',padding:'5px 3px'}} />日
                        </div>
                      </div>
                      <div><span style={empLabelSt}>施設名</span><input type="text" value={empFields[`c${i}_facility`]??''} onChange={e=>setEmpFields(p=>({...p,[`c${i}_facility`]:e.target.value}))} style={empInputSt} /></div>
                      <div style={{ display:'flex', gap:'8px' }}>
                        {[[`c${i}_use`,'利用中'],[`c${i}_app`,'申込中（第一希望）']].map(([k,l])=>(
                          <label key={k} style={empRadioLabelSt}><input type="radio" checked={empChecks[k!]??false} onChange={()=>{const allCk=[`c${i}_use`,`c${i}_app`]; setEmpChecks(p=>{const n={...p}; allCk.forEach(ck=>n[ck]=false); n[k!]=true; return n;});}} style={{accentColor:'#0ea5e9'}} /> {l}</label>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                <div style={{ display:'flex', gap:'6px' }}>
                  <button onClick={() => setNumChildren(n => n + 1)}
                    style={{ flex:1, padding:'6px', border:'1.5px dashed #0ea5e9', borderRadius:'6px', background:'#f0f9ff', color:'#0ea5e9', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>
                    ＋ 児童を追加
                  </button>
                  {numChildren > 1 && (
                    <button onClick={() => setNumChildren(n => Math.max(n - 1, 1))}
                      style={{ padding:'6px 10px', border:'1.5px solid #fca5a5', borderRadius:'6px', background:'#fff1f2', color:'#ef4444', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>
                      －
                    </button>
                  )}
                </div>
              </EmpSection>

            </div>
          )}

          <div style={{ height: '1px', background: '#f1f5f9' }} />

          {certType === 'boarding' ? (
            <>
              <button onClick={() => { if (selectedCrewId) setBoardingPreviewVersion(v => v + 1); }}
                disabled={!selectedCrewId}
                title="対象者を選ぶと自動でプレビューされます。手動で再生成したいときに使います。"
                style={{ width: '100%', padding: '9px', borderRadius: '10px', border: '1.5px solid #e2e8f0', cursor: selectedCrewId ? 'pointer' : 'not-allowed', color: selectedCrewId ? '#475569' : '#cbd5e1', fontSize: '13px', fontWeight: 600, background: '#fff', marginBottom: 2 }}>
                🔄 プレビューを再生成
              </button>
              <button onClick={handleBoardingPdfDownload} disabled={generating}
                style={{ width: '100%', padding: '15px', borderRadius: '12px', border: 'none', cursor: generating ? 'not-allowed' : 'pointer', color: '#fff', fontSize: '15px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: generating ? '#94a3b8' : generated ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#0ea5e9,#0284c7)' }}>
                {generating ? 'PDF生成中...' : generated ? '✅ ダウンロード完了！' : '📄 PDFをダウンロード（公式様式）'}
              </button>
              <button onClick={handleBoardingWordDownload}
                style={{ width: '100%', padding: '13px', borderRadius: '12px', border: 'none', cursor: 'pointer', color: '#fff', fontSize: '14px', fontWeight: 700, background: 'linear-gradient(135deg,#7c3aed,#6d28d9)' }}>
                📝 Wordをダウンロード（.docx）
              </button>
            </>
          ) : certType === 'roster' ? (
            <>
              <button onClick={handleGeneratePdf} disabled={generating}
                style={{ width: '100%', padding: '15px', borderRadius: '12px', border: 'none', cursor: generating ? 'not-allowed' : 'pointer', color: '#fff', fontSize: '15px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: generating ? '#94a3b8' : generated ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#0ea5e9,#0284c7)' }}>
                {generating ? 'PDF生成中...' : generated ? '✅ ダウンロード完了！' : '📄 PDFプレビューを保存'}
              </button>
              {rosterFormat === 'customs' && (
                <>
                  <button onClick={handleCustomsExcel}
                    style={{ width: '100%', padding: '13px', borderRadius: '12px', border: 'none', cursor: 'pointer', color: '#fff', fontSize: '14px', fontWeight: 700, background: 'linear-gradient(135deg,#16a34a,#15803d)' }}>
                    📊 Excelをダウンロード（税関様式）
                  </button>
                  <button onClick={handleCustomsWord}
                    style={{ width: '100%', padding: '13px', borderRadius: '12px', border: 'none', cursor: 'pointer', color: '#fff', fontSize: '14px', fontWeight: 700, background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
                    📝 Wordをダウンロード（税関様式）
                  </button>
                </>
              )}
              {rosterFormat === 'mlit' && (
                <button onClick={handleMlitExcel}
                  style={{ width: '100%', padding: '13px', borderRadius: '12px', border: 'none', cursor: 'pointer', color: '#fff', fontSize: '14px', fontWeight: 700, background: 'linear-gradient(135deg,#16a34a,#15803d)' }}>
                  📊 Excelをダウンロード（国土交通省様式）
                </button>
              )}
            </>
          ) : (
            <>
              <button onClick={handleGeneratePdf} disabled={generating}
                style={{ width: '100%', padding: '15px', borderRadius: '12px', border: 'none', cursor: generating ? 'not-allowed' : 'pointer', color: '#fff', fontSize: '15px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: generating ? '#94a3b8' : generated ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#0ea5e9,#0284c7)' }}>
                {generating ? 'PDF生成中...' : generated ? '✅ ダウンロード完了！' : 'PDFをダウンロード'}
              </button>
              {certType === 'employment' && (
                <button onClick={handleExcelDownload}
                  style={{ width: '100%', padding: '13px', borderRadius: '12px', border: 'none', cursor: 'pointer', color: '#fff', fontSize: '14px', fontWeight: 700, background: 'linear-gradient(135deg,#16a34a,#15803d)' }}>
                  Excelをダウンロード（公式様式）
                </button>
              )}
              {isForm16 && (
                <button onClick={handleLaborExcel} disabled={!laborRec}
                  style={{ width: '100%', padding: '13px', borderRadius: '12px', border: 'none', cursor: laborRec ? 'pointer' : 'not-allowed', color: '#fff', fontSize: '14px', fontWeight: 700, background: laborRec ? 'linear-gradient(135deg,#16a34a,#15803d)' : '#94a3b8' }}>
                  📊 Excelをダウンロード（様式第十六号の五）
                </button>
              )}
            </>
          )}
          <button onClick={() => window.print()}
            style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
            印刷する
          </button>
        </div>

        {/* 右パネル（A4 プレビュー） */}
        <div style={{ overflow: 'auto', background: '#e2e8f0', padding: '24px', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: '100%', maxWidth: isForm16 ? '1300px' : '1100px' }}>
            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
              <span style={{ background: '#1e293b', color: '#94a3b8', fontSize: '11px', fontWeight: 700, padding: '4px 16px', borderRadius: '999px' }}>
                A4 プレビュー
              </span>
            </div>
            {certType === 'boarding' ? (
              <div style={{ boxShadow: '0 4px 32px rgba(0,0,0,0.18)', borderRadius: '4px', background: '#fff', width: '100%', aspectRatio: '1.414 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {boardingPreviewLoading ? (
                  <div style={{ color: '#64748b', fontSize: '14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
                    <div>証明書を生成中...</div>
                  </div>
                ) : boardingPreviewHtml ? (
                  <iframe srcDoc={boardingPreviewHtml} style={{ width: '100%', height: '100%', border: 'none' }} title="乗船履歴証明書プレビュー" />
                ) : (
                  <div style={{ color: '#64748b', fontSize: '14px', textAlign: 'center' }}>
                    {selectedCrewId ? (
                      <>
                        <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
                        <div>プレビューを準備中…</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: '32px', marginBottom: '12px' }}>👤</div>
                        <div>対象者を選択してください</div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : isForm16 ? (
              <div style={{ overflowX: 'auto' }}>
                {!selectedCrewId ? (
                  <div style={{ background: '#fff', borderRadius: 4, padding: 80, textAlign: 'center', color: '#94a3b8', boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}>対象者を選択してください</div>
                ) : laborLoading ? (
                  <div style={{ background: '#fff', borderRadius: 4, padding: 80, textAlign: 'center', color: '#94a3b8', boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}>⏳ 読み込み中...</div>
                ) : laborRecView ? (
                  <div ref={previewRef} style={{ boxShadow: '0 4px 32px rgba(0,0,0,0.18)', borderRadius: '2px', display: 'inline-block' }}>
                    <LaborRecordSheet rec={laborRecView} />
                  </div>
                ) : (
                  <div style={{ background: '#fff', borderRadius: 4, padding: 80, textAlign: 'center', color: '#94a3b8', boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}>データを取得できませんでした</div>
                )}
              </div>
            ) : (
              <div ref={previewRef} style={{ boxShadow: '0 4px 32px rgba(0,0,0,0.18)', borderRadius: '2px', overflow: 'visible', display: 'inline-block' }}>
                <CertPreview
                  certType={certType} crew={selectedCrew} vessel={selectedVessel}
                  year={year} month={month} issueDate={issueDate}
                  attendance={attendance} allCrew={rosterCrew} company={company}
                  rosterFields={rosterFields} rosterFormat={rosterFormat}
                  customsState={customsState} mlitState={mlitState}
                  activityLogs={activityLogs}
                  empChecks={empChecks}
                  onEmpCheck={key => setEmpChecks(prev => ({ ...prev, [key]: !(prev[key] ?? false) }))}
                  empWorkRecords={empWorkRecords}
                  empSlots={empSlots}
                  onEmpSlotChange={(i, y, m) => setEmpSlots(prev => {
                    const next = [...prev] as typeof prev;
                    next[i] = { y, m };
                    return next;
                  })}
                  empFields={empFields}
                  onEmpFieldChange={(key, val) => setEmpFields(prev => ({ ...prev, [key]: val }))}
                  numChildren={numChildren}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificatesPage;
